/**
 * Sprint QA - Professional QA Management Module
 * Hierarchy: Requirement → Acceptance Criteria → Test Scenario → Test Case → Test Steps
 * Features: Sprint Board, Requirements List, Sprints Management
 * Now with URL-based project routing for multi-project support
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from '../hooks/use-toast';
import {
  Calendar, FileText, CheckSquare, Plus, Edit, Trash2,
  ChevronRight, ChevronDown, ChevronUp, Loader2, X, 
  Clock, CheckCircle, XCircle, FolderOpen, MoreVertical, RefreshCw,
  List, User, Settings, AlertCircle, Flag, Copy, Hash,
  PlayCircle, FileCheck, Layers, TestTube, ListOrdered, ArrowLeft,
  LayoutGrid, Target, TrendingUp, GripVertical, Eye, ExternalLink,
  Send, Link, Play, Check, Search, Image, Upload, ImagePlus,
  Sparkles, FileUp, Wand2, Bot, Cloud, Database
} from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { RichTextEditor, RichTextViewer } from './ui/rich-text-editor';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from './ui/sheet';
import { SearchableSelect } from './ui/searchable-select';
import { Label } from './ui/label';
import AzureDevOpsImport from './AzureDevOpsImport';
import AllItemsView from './sprint-qa/AllItemsView';

// Helper to extract project slug from URL path
const getProjectSlugFromPath = () => {
  const match = window.location.pathname.match(/\/sprint-qa\/project\/([^/]+)/);
  return match ? match[1] : null;
};

// Helper to extract requirement key from URL path (e.g., REQ-1)
const getRequirementKeyFromPath = () => {
  const match = window.location.pathname.match(/\/sprint-qa\/project\/[^/]+\/requirement\/([^/]+)$/);
  return match ? decodeURIComponent(match[1]) : null;
};

// Helper to extract test scenario key from URL path (e.g., TS-1)
const getScenarioKeyFromPath = () => {
  const match = window.location.pathname.match(/\/sprint-qa\/project\/[^/]+\/scenario\/([^/]+)$/);
  return match ? decodeURIComponent(match[1]) : null;
};

// Helper to extract test case key from URL path (e.g., TC-1)
const getTestCaseKeyFromPath = () => {
  const match = window.location.pathname.match(/\/sprint-qa\/project\/[^/]+\/test-case\/([^/]+)$/);
  return match ? decodeURIComponent(match[1]) : null;
};

// Helper to determine current view type from URL
const getViewTypeFromPath = () => {
  const path = window.location.pathname;
  if (path.includes('/test-case/')) return 'test-case';
  if (path.includes('/scenario/')) return 'scenario';
  if (path.includes('/requirement/')) return 'requirement';
  return 'project';
};

const getBackendURL = () => process.env.REACT_APP_BACKEND_URL || '';

// Config objects
const requirementTypeConfig = {
  epic: { color: 'bg-purple-100 text-purple-700 border-purple-200', label: 'Epic', icon: '🎯' },
  story: { color: 'bg-blue-100 text-blue-700 border-blue-200', label: 'Story', icon: '📖' },
  bug: { color: 'bg-red-100 text-red-700 border-red-200', label: 'Bug', icon: '🐛' },
  technical: { color: 'bg-gray-100 text-gray-700 border-gray-200', label: 'Technical', icon: '⚙️' },
};

const requirementStatusConfig = {
  draft: { color: 'bg-gray-100 text-gray-600', label: 'Draft' },
  ready: { color: 'bg-blue-100 text-blue-700', label: 'Ready' },
  in_progress: { color: 'bg-amber-100 text-amber-700', label: 'In Progress' },
  done: { color: 'bg-green-100 text-green-700', label: 'Done' },
};

const priorityConfig = {
  low: { color: 'bg-slate-100 text-slate-600', icon: '▽', label: 'Low' },
  medium: { color: 'bg-blue-100 text-blue-700', icon: '▬', label: 'Medium' },
  high: { color: 'bg-orange-100 text-orange-700', icon: '△', label: 'High' },
  critical: { color: 'bg-red-100 text-red-700', icon: '▲', label: 'Critical' },
};

const testCaseStatusConfig = {
  draft: { color: 'bg-gray-100 text-gray-600', label: 'Draft' },
  ready: { color: 'bg-blue-100 text-blue-700', label: 'Ready' },
  in_progress: { color: 'bg-amber-100 text-amber-700', label: 'In Progress' },
  passed: { color: 'bg-green-100 text-green-700', label: 'Passed' },
  failed: { color: 'bg-red-100 text-red-700', label: 'Failed' },
  blocked: { color: 'bg-purple-100 text-purple-700', label: 'Blocked' },
  skipped: { color: 'bg-slate-100 text-slate-600', label: 'Skipped' },
};

const sprintStatusConfig = {
  planning: { color: 'bg-blue-100 text-blue-700', label: 'Planning' },
  active: { color: 'bg-green-100 text-green-700', label: 'Active' },
  completed: { color: 'bg-slate-100 text-slate-700', label: 'Completed' },
};

// Testing status columns for Sprint Board (default)
const DEFAULT_BOARD_COLUMNS = [
  { id: 'not_started', label: 'Not Started', order: 0 },
  { id: 'to_test', label: 'To Test', order: 1 },
  { id: 'testing', label: 'Testing', order: 2 },
  { id: 'done', label: 'Done', order: 3 },
  { id: 'blocked', label: 'Blocked', order: 4 },
];

const formatDate = (dateStr) => {
  if (!dateStr) return 'None';
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export default function SprintQA() {
  const BACKEND_URL = getBackendURL();
  const projectSlug = getProjectSlugFromPath(); // Get project slug from URL
  const requirementKeyFromUrl = getRequirementKeyFromPath(); // Get requirement key from URL (e.g., REQ-1)
  const scenarioKeyFromUrl = getScenarioKeyFromPath(); // Get scenario key from URL (e.g., TS-1)
  const testCaseKeyFromUrl = getTestCaseKeyFromPath(); // Get test case key from URL (e.g., TC-1)
  const currentViewType = getViewTypeFromPath(); // 'project', 'requirement', 'scenario', or 'test-case'
  
  // ============================================================================
  // State declarations - MUST be before callbacks that reference them
  // ============================================================================
  
  // Project state - loaded from URL slug
  const [currentProject, setCurrentProject] = useState(null);
  const [projectLoading, setProjectLoading] = useState(true);
  
  // Available projects for assignment
  const [availableProjects, setAvailableProjects] = useState([]);
  
  // Main view state
  const [activeTab, setActiveTab] = useState('backlog');
  const [selectedSprint, setSelectedSprint] = useState(null);
  const [selectedRequirement, setSelectedRequirement] = useState(null);
  const [selectedTestScenario, setSelectedTestScenario] = useState(null);
  const [selectedTestCase, setSelectedTestCase] = useState(null);
  
  // Full-page view state for scenarios and test cases
  const [fullPageScenario, setFullPageScenario] = useState(null);
  const [fullPageTestCase, setFullPageTestCase] = useState(null);
  
  // Data state
  const [requirements, setRequirements] = useState([]);
  const [sprints, setSprints] = useState([]);
  const [boardData, setBoardData] = useState(null);
  const [requirementDetail, setRequirementDetail] = useState(null);
  const [qaSettings, setQaSettings] = useState({});
  const [settingsForm, setSettingsForm] = useState({});
  const [boardColumns, setBoardColumns] = useState(DEFAULT_BOARD_COLUMNS);
  const [editingColumn, setEditingColumn] = useState(null);
  const [showColumnModal, setShowColumnModal] = useState(false);
  const [columnForm, setColumnForm] = useState({ id: '', label: '' });
  const [teamMembers, setTeamMembers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  
  // Ref to track if we've processed the URL (prevents loop)
  const urlRequirementProcessed = useRef(false);
  const urlScenarioProcessed = useRef(false);
  const urlTestCaseProcessed = useRef(false);
  
  // Editing state for AC and Scenarios
  const [editingAC, setEditingAC] = useState(null);
  const [showEditACModal, setShowEditACModal] = useState(false);
  const [editingScenario, setEditingScenario] = useState(null);
  const [showEditScenarioModal, setShowEditScenarioModal] = useState(false);
  
  // Backlog selection and drag state
  const [selectedRequirements, setSelectedRequirements] = useState([]);
  const [backlogDragItem, setBacklogDragItem] = useState(null);
  
  // Loading state
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Modal visibility state
  const [showCreateReqModal, setShowCreateReqModal] = useState(false);
  const [showEditReqModal, setShowEditReqModal] = useState(false);
  const [showAddACModal, setShowAddACModal] = useState(false);
  const [showAddScenarioModal, setShowAddScenarioModal] = useState(false);
  const [showSprintModal, setShowSprintModal] = useState(false);
  const [showEditSprintModal, setShowEditSprintModal] = useState(false);
  const [showTestCaseSheet, setShowTestCaseSheet] = useState(false);
  const [testCaseSheetMode, setTestCaseSheetMode] = useState('view');
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showExecutionModal, setShowExecutionModal] = useState(false);
  const [executingTestCase, setExecutingTestCase] = useState(null);
  const [stepResults, setStepResults] = useState({});
  const [executionNotes, setExecutionNotes] = useState('');
  
  // Comments and Activity state
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [activityTab, setActivityTab] = useState('comments');
  const [editingComment, setEditingComment] = useState(null);
  const [testExecutions, setTestExecutions] = useState([]);
  
  // Inline editing state for full-page views
  const [editingScenarioTitle, setEditingScenarioTitle] = useState(false);
  const [editingScenarioDescription, setEditingScenarioDescription] = useState(false);
  const [scenarioTitleDraft, setScenarioTitleDraft] = useState('');
  const [scenarioDescriptionDraft, setScenarioDescriptionDraft] = useState('');
  const [parentRequirementSearch, setParentRequirementSearch] = useState('');
  const [showParentRequirementDropdown, setShowParentRequirementDropdown] = useState(false);
  
  // Test Case inline editing state
  const [editingTestCaseTitle, setEditingTestCaseTitle] = useState(false);
  const [editingTestCaseDescription, setEditingTestCaseDescription] = useState(false);
  const [editingTestCasePreconditions, setEditingTestCasePreconditions] = useState(false);
  const [testCaseTitleDraft, setTestCaseTitleDraft] = useState('');
  const [testCaseDescriptionDraft, setTestCaseDescriptionDraft] = useState('');
  const [testCasePreconditionsDraft, setTestCasePreconditionsDraft] = useState('');
  const [showParentScenarioDropdown, setShowParentScenarioDropdown] = useState(false);
  const [parentScenarioSearch, setParentScenarioSearch] = useState('');
  const [editingStepIndex, setEditingStepIndex] = useState(null);
  const [testStepsDraft, setTestStepsDraft] = useState([]);
  
  // Evidence upload state
  const [uploadingEvidence, setUploadingEvidence] = useState(null); // { testCaseId, stepNumber }
  const [evidencePreview, setEvidencePreview] = useState(null); // { images: [], stepNumber }
  
  // Filter state
  const [sprintFilter, setSprintFilter] = useState('all');
  const [assigneeFilter, setAssigneeFilter] = useState(null);
  const [noPointsFilter, setNoPointsFilter] = useState(false);
  
  // Azure DevOps Import state
  const [showAzureImport, setShowAzureImport] = useState(false);
  const [azureAddonEnabled, setAzureAddonEnabled] = useState(false);
  
  // Form state
  const [requirementForm, setRequirementForm] = useState({
    title: '', description: '', type: 'story', priority: 'medium',
    business_rules: '', edge_cases: '', sprint_id: null, labels: [],
    story_points: null, assignee_id: null, reporter_id: null, project_id: null
  });
  const [acForm, setAcForm] = useState({ title: '', description: '' });
  const [scenarioForm, setScenarioForm] = useState({ title: '', description: '', story_points: null });
  const [testCaseForm, setTestCaseForm] = useState({
    title: '', description: '', preconditions: '', expected_result: '',
    priority: 'medium', test_type: 'manual', test_steps: [], assignee_id: null
  });
  const [newStep, setNewStep] = useState({ action: '', expected_result: '' });
  const [sprintForm, setSprintForm] = useState({ key: '', name: '', start_date: '', end_date: '', goal: '' });
  
  // ============================================================================
  // Helper functions - Global scope
  // ============================================================================
  
  // Status badge helper (used in multiple render functions)
  const getStatusBadge = (status) => {
    const statusMap = {
      'passed': { bg: 'bg-green-500', text: 'PASS' },
      'failed': { bg: 'bg-red-500', text: 'FAIL' },
      'blocked': { bg: 'bg-yellow-500', text: 'BLOCKED' },
      'in_progress': { bg: 'bg-blue-500', text: 'WIP' },
      'draft': { bg: 'bg-gray-400', text: 'DRAFT' },
      'ready': { bg: 'bg-indigo-500', text: 'READY' },
      'not_executed': { bg: 'bg-gray-400', text: 'NOT RUN' }
    };
    const config = statusMap[status] || statusMap['draft'];
    return (
      <span className={`${config.bg} text-white text-xs font-bold px-2 py-1 rounded`}>
        {config.text}
      </span>
    );
  };
  
  // ============================================================================
  // Navigation helpers - after state declarations
  // ============================================================================
  
  // Helper to navigate back to projects list
  const navigateToProjects = () => {
    window.history.pushState({}, '', '/sprint-qa');
    window.dispatchEvent(new CustomEvent('sprintQANavigate', { detail: '/sprint-qa' }));
  };
  
  // Helper to navigate to a requirement detail view
  const navigateToRequirement = useCallback((requirementKey) => {
    if (!projectSlug || !requirementKey) return;
    
    // First, update the URL
    const newPath = `/sprint-qa/project/${projectSlug}/requirement/${encodeURIComponent(requirementKey)}`;
    window.history.pushState({}, '', newPath);
    
    // Clear conflicting views
    setFullPageScenario(null);
    setFullPageTestCase(null);
    
    // Switch to requirements tab and fetch the requirement
    setActiveTab('requirements');
    
    // Fetch the requirement data
    const fetchRequirement = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const params = currentProject?.id ? { project_id: currentProject.id } : {};
        const response = await axios.get(`${BACKEND_URL}/api/sprint-qa/requirements/by-key/${encodeURIComponent(requirementKey)}`, {
          headers: { Authorization: `Bearer ${token}` },
          params
        });
        // Set selectedRequirement to the requirement object (used for .id references)
        setSelectedRequirement(response.data.requirement);
        // Set requirementDetail to the full response (includes acceptance_criteria, test_scenarios)
        setRequirementDetail(response.data);
      } catch (err) {
        console.error('Failed to fetch requirement:', err);
        // Navigate back to project view on error
        if (projectSlug) {
          window.history.pushState({}, '', `/sprint-qa/project/${projectSlug}`);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchRequirement();
  }, [projectSlug, currentProject, BACKEND_URL]);
  
  // Helper to navigate to a test scenario full page view
  const navigateToScenario = useCallback((scenarioKey) => {
    if (!projectSlug || !scenarioKey) return;
    
    // First, update the URL
    const newPath = `/sprint-qa/project/${projectSlug}/scenario/${encodeURIComponent(scenarioKey)}`;
    window.history.pushState({}, '', newPath);
    
    // Clear conflicting views - but do this AFTER URL update to prevent intermediate renders
    setFullPageTestCase(null);
    setSelectedRequirement(null);
    setRequirementDetail(null);
    
    // Reset the ref to allow useEffect to pick up future URL navigations
    urlTestCaseProcessed.current = false;
    
    // Immediately fetch the scenario data
    // Use a direct fetch instead of relying on currentProject in closure
    const fetchScenario = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        // Get project_id from currentProject if available, otherwise it will be resolved by backend
        const params = currentProject?.id ? { project_id: currentProject.id } : {};
        const response = await axios.get(`${BACKEND_URL}/api/sprint-qa/scenarios/by-key/${encodeURIComponent(scenarioKey)}`, {
          headers: { Authorization: `Bearer ${token}` },
          params
        });
        setFullPageScenario(response.data);
      } catch (err) {
        console.error('Failed to fetch scenario:', err);
        // Navigate back to project view on error
        if (projectSlug) {
          window.history.pushState({}, '', `/sprint-qa/project/${projectSlug}`);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchScenario();
  }, [projectSlug, currentProject, BACKEND_URL]);
  
  // Helper to navigate to a test case full page view
  const navigateToTestCase = useCallback((testCaseKey) => {
    if (!projectSlug || !testCaseKey) return;
    
    // First, update the URL
    const newPath = `/sprint-qa/project/${projectSlug}/test-case/${encodeURIComponent(testCaseKey)}`;
    window.history.pushState({}, '', newPath);
    
    // Clear conflicting views
    setFullPageScenario(null);
    setSelectedRequirement(null);
    setRequirementDetail(null);
    
    // Reset the ref to allow useEffect to pick up future URL navigations
    urlScenarioProcessed.current = false;
    
    // Immediately fetch the test case data
    const fetchTestCase = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const params = currentProject?.id ? { project_id: currentProject.id } : {};
        const response = await axios.get(`${BACKEND_URL}/api/sprint-qa/test-cases/by-key/${encodeURIComponent(testCaseKey)}`, {
          headers: { Authorization: `Bearer ${token}` },
          params
        });
        setFullPageTestCase(response.data);
      } catch (err) {
        console.error('Failed to fetch test case:', err);
        // Navigate back to project view on error
        if (projectSlug) {
          window.history.pushState({}, '', `/sprint-qa/project/${projectSlug}`);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchTestCase();
  }, [projectSlug, currentProject, BACKEND_URL]);
  
  // Helper to clear requirement from URL (go back to project view)
  const navigateToProjectView = useCallback(() => {
    if (projectSlug) {
      const newPath = `/sprint-qa/project/${projectSlug}`;
      window.history.pushState({}, '', newPath);
      // Clear all full-page views
      setFullPageScenario(null);
      setFullPageTestCase(null);
      // Reset refs to allow URL-based navigation to work again
      urlScenarioProcessed.current = false;
      urlTestCaseProcessed.current = false;
    }
  }, [projectSlug]);

  // Additional state that wasn't moved above
  const [showCreateRequirementModal, setShowCreateRequirementModal] = useState(false);
  const [showEditRequirementModal, setShowEditRequirementModal] = useState(false);
  const [showCreateSprintModal, setShowCreateSprintModal] = useState(false);
  const [showAssignSprintModal, setShowAssignSprintModal] = useState(false);
  const [editingTestCase, setEditingTestCase] = useState(null);
  const [showExecutionSheet, setShowExecutionSheet] = useState(false);
  const [currentExecution, setCurrentExecution] = useState(null);
  const [executionLoading, setExecutionLoading] = useState(false);
  const [viewingExecution, setViewingExecution] = useState(null); // For viewing past execution details
  const [editingSprint, setEditingSprint] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);
  const [editingRequirement, setEditingRequirement] = useState(null);
  
  // AI Generation State
  const [aiGenerationMode, setAiGenerationMode] = useState('manual'); // 'manual' or 'ai'
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiFile, setAiFile] = useState(null);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiPreview, setAiPreview] = useState(null);
  const [aiSourceInfo, setAiSourceInfo] = useState(null);

  // Helper to select a requirement and update URL (uses requirement key for readable URLs)
  const selectRequirement = useCallback((req, options = {}) => {
    const { updateUrl = true, switchTab = true } = options;
    setSelectedRequirement(req);
    if (req && updateUrl && req.key) {
      navigateToRequirement(req.key);
    }
    if (switchTab && req) {
      setActiveTab('requirements');
    }
  }, [navigateToRequirement]);

  // Helper to clear requirement selection and update URL
  const clearRequirementSelection = useCallback(() => {
    setSelectedRequirement(null);
    setRequirementDetail(null);
    navigateToProjectView();
  }, [navigateToProjectView]);

  // Fetch functions
  const fetchRequirements = useCallback(async () => {
    // Don't fetch if no project is selected (prevents loading all requirements)
    if (!currentProject) {
      return;
    }
    
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (sprintFilter !== 'all') {
        params.append('sprint_id', sprintFilter);
      }
      // Use actual project ID for filtering (skip for legacy/unassigned)
      if (currentProject?.id && currentProject.id !== 'unassigned') {
        params.append('project_id', currentProject.id);
      }
      const queryString = params.toString() ? `?${params.toString()}` : '';
      const response = await axios.get(`${BACKEND_URL}/api/sprint-qa/requirements${queryString}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequirements(response.data.requirements || []);
    } catch (err) {
      console.error('Failed to fetch requirements:', err);
    } finally {
      setLoading(false);
    }
  }, [BACKEND_URL, sprintFilter, currentProject]);

  const fetchTeamMembers = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/sprint-qa/team-members`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const members = response.data.members || [];
      setTeamMembers(members);
      
      // Also fetch current user info
      try {
        const userResponse = await axios.get(`${BACKEND_URL}/api/users/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCurrentUser(userResponse.data);
      } catch (userErr) {
        // If /users/me fails, try to find current user from members list
        console.log('Could not fetch current user, using first member');
        if (members.length > 0) {
          setCurrentUser(members[0]);
        }
      }
    } catch (err) {
      console.error('Failed to fetch team members:', err);
    }
  }, [BACKEND_URL]);

  // Fetch available projects for requirement assignment
  const fetchAvailableProjects = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/sprint-qa/projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter out legacy/unassigned from the list
      const projects = (response.data.projects || []).filter(p => !p.is_legacy);
      setAvailableProjects(projects);
    } catch (err) {
      console.error('Failed to fetch available projects:', err);
    }
  }, [BACKEND_URL]);

  // Fetch project by slug from URL
  const fetchProjectBySlug = useCallback(async () => {
    if (!projectSlug) {
      navigateToProjects();
      return;
    }
    
    try {
      setProjectLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/sprint-qa/projects/by-slug/${projectSlug}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentProject(response.data);
    } catch (err) {
      console.error('Failed to fetch project:', err);
      if (err.response?.status === 404 || err.response?.status === 403) {
        // Project not found or access denied, redirect to projects list
        navigateToProjects();
      }
    } finally {
      setProjectLoading(false);
    }
  }, [BACKEND_URL, projectSlug]);

  const fetchSprints = useCallback(async () => {
    // Don't fetch if no project is selected
    if (!currentProject) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      // Use "unassigned" for legacy items, actual project ID for regular projects
      if (currentProject?.id && currentProject.id !== 'unassigned') {
        params.append('project_id', currentProject.id);
      } else if (currentProject?.is_legacy) {
        // For legacy items, don't filter by project_id (will show items with null project_id)
      }
      const response = await axios.get(`${BACKEND_URL}/api/sprint-qa/sprints?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const sprintsList = response.data.sprints || [];
      setSprints(sprintsList);
      
      // Auto-select first active sprint for board view
      if (!selectedSprint && sprintsList.length > 0) {
        const activeSprint = sprintsList.find(s => s.status === 'active') || sprintsList[0];
        setSelectedSprint(activeSprint);
      } else if (selectedSprint && !sprintsList.find(s => s.id === selectedSprint.id)) {
        // Selected sprint not in current project, reset
        setSelectedSprint(sprintsList[0] || null);
      }
    } catch (err) {
      console.error('Failed to fetch sprints:', err);
    }
  }, [BACKEND_URL, selectedSprint, currentProject]);

  const fetchSprintBoard = useCallback(async (sprintId) => {
    if (!sprintId) return;
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/sprint-qa/sprints/${sprintId}/board`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBoardData(response.data);
    } catch (err) {
      console.error('Failed to fetch sprint board:', err);
      setBoardData(null);
    } finally {
      setLoading(false);
    }
  }, [BACKEND_URL]);

  const fetchRequirementDetail = useCallback(async (reqId) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/sprint-qa/requirements/${reqId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequirementDetail(response.data);
    } catch (err) {
      console.error('Failed to fetch requirement detail:', err);
    } finally {
      setLoading(false);
    }
  }, [BACKEND_URL]);

  // Fetch scenario by key for full-page view
  const fetchScenarioByKey = useCallback(async (scenarioKey) => {
    if (!currentProject) return null;
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/sprint-qa/scenarios/by-key/${encodeURIComponent(scenarioKey)}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { project_id: currentProject.id }
      });
      setFullPageScenario(response.data);
      return response.data;
    } catch (err) {
      console.error('Failed to fetch scenario by key:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [BACKEND_URL, currentProject]);

  // Fetch test case by key for full-page view
  const fetchTestCaseByKey = useCallback(async (testCaseKey) => {
    if (!currentProject) return null;
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/sprint-qa/test-cases/by-key/${encodeURIComponent(testCaseKey)}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { project_id: currentProject.id }
      });
      setFullPageTestCase(response.data);
      return response.data;
    } catch (err) {
      console.error('Failed to fetch test case by key:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [BACKEND_URL, currentProject]);

  // Fetch comments for an entity
  const fetchComments = useCallback(async (entityType, entityId) => {
    try {
      setCommentsLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/sprint-qa/comments`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { entity_type: entityType, entity_id: entityId }
      });
      setComments(response.data.comments || []);
    } catch (err) {
      console.error('Failed to fetch comments:', err);
      setComments([]);
    } finally {
      setCommentsLoading(false);
    }
  }, [BACKEND_URL]);

  // Add a new comment
  const addComment = useCallback(async (entityType, entityId, content) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${BACKEND_URL}/api/sprint-qa/comments`, {
        entity_type: entityType,
        entity_id: entityId,
        content: content
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Refresh comments
      fetchComments(entityType, entityId);
      setNewComment('');
    } catch (err) {
      console.error('Failed to add comment:', err);
      alert('Failed to add comment');
    }
  }, [BACKEND_URL, fetchComments]);

  // Update a comment
  const updateComment = useCallback(async (commentId, content, entityType, entityId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${BACKEND_URL}/api/sprint-qa/comments/${commentId}`, {
        content: content
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Refresh comments
      fetchComments(entityType, entityId);
      setEditingComment(null);
    } catch (err) {
      console.error('Failed to update comment:', err);
      alert('Failed to update comment');
    }
  }, [BACKEND_URL, fetchComments]);

  // Delete a comment
  const deleteComment = useCallback(async (commentId, entityType, entityId) => {
    if (!confirm('Are you sure you want to delete this comment?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/sprint-qa/comments/${commentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Refresh comments
      fetchComments(entityType, entityId);
    } catch (err) {
      console.error('Failed to delete comment:', err);
      alert('Failed to delete comment');
    }
  }, [BACKEND_URL, fetchComments]);

  // Fetch test executions for a test case
  const fetchTestExecutions = useCallback(async (testCaseId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/sprint-qa/test-cases/${testCaseId}/executions`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { limit: 20 }
      });
      setTestExecutions(response.data.executions || []);
    } catch (err) {
      console.error('Failed to fetch test executions:', err);
      setTestExecutions([]);
    }
  }, [BACKEND_URL]);

  // Inline update scenario field (auto-save)
  const updateScenarioField = useCallback(async (scenarioId, field, value) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${BACKEND_URL}/api/sprint-qa/test-scenarios/${scenarioId}`, 
        { [field]: value },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      // Refresh the scenario data
      if (fullPageScenario?.test_scenario?.key) {
        fetchScenarioByKey(fullPageScenario.test_scenario.key);
      }
    } catch (err) {
      console.error(`Failed to update scenario ${field}:`, err);
      alert(`Failed to update ${field}`);
    }
  }, [BACKEND_URL, fullPageScenario, fetchScenarioByKey]);

  // Update scenario parent requirement
  const updateScenarioRequirement = useCallback(async (scenarioId, newRequirementId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${BACKEND_URL}/api/sprint-qa/test-scenarios/${scenarioId}`, 
        { requirement_id: newRequirementId },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      // Refresh the scenario data
      if (fullPageScenario?.test_scenario?.key) {
        fetchScenarioByKey(fullPageScenario.test_scenario.key);
      }
      setShowParentRequirementDropdown(false);
      setParentRequirementSearch('');
    } catch (err) {
      console.error('Failed to update parent requirement:', err);
      alert('Failed to update parent requirement');
    }
  }, [BACKEND_URL, fullPageScenario, fetchScenarioByKey]);

  // Inline update test case field (auto-save)
  const updateTestCaseField = useCallback(async (testCaseId, field, value) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${BACKEND_URL}/api/sprint-qa/test-cases/${testCaseId}`, 
        { [field]: value },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      // Refresh the test case data
      if (fullPageTestCase?.test_case?.key) {
        fetchTestCaseByKey(fullPageTestCase.test_case.key);
      }
    } catch (err) {
      console.error(`Failed to update test case ${field}:`, err);
      alert(`Failed to update ${field}`);
    }
  }, [BACKEND_URL, fullPageTestCase, fetchTestCaseByKey]);

  // Update test case parent scenario
  const updateTestCaseScenario = useCallback(async (testCaseId, newScenarioId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${BACKEND_URL}/api/sprint-qa/test-cases/${testCaseId}`, 
        { test_scenario_id: newScenarioId },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      // Refresh the test case data
      if (fullPageTestCase?.test_case?.key) {
        fetchTestCaseByKey(fullPageTestCase.test_case.key);
      }
      setShowParentScenarioDropdown(false);
      setParentScenarioSearch('');
    } catch (err) {
      console.error('Failed to update parent scenario:', err);
      alert('Failed to update parent scenario');
    }
  }, [BACKEND_URL, fullPageTestCase, fetchTestCaseByKey]);

  // Update test steps for a test case
  const updateTestSteps = useCallback(async (testCaseId, newSteps) => {
    try {
      const token = localStorage.getItem('token');
      // Renumber steps to ensure correct order
      const renumberedSteps = newSteps.map((step, idx) => ({
        ...step,
        step_number: idx + 1
      }));
      await axios.put(`${BACKEND_URL}/api/sprint-qa/test-cases/${testCaseId}`, 
        { test_steps: renumberedSteps },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      // Refresh the test case data
      if (fullPageTestCase?.test_case?.key) {
        fetchTestCaseByKey(fullPageTestCase.test_case.key);
      }
    } catch (err) {
      console.error('Failed to update test steps:', err);
      alert('Failed to update test steps');
    }
  }, [BACKEND_URL, fullPageTestCase, fetchTestCaseByKey]);

  // Add a new test step (inline editing in full-page view)
  const addInlineTestStep = useCallback((testCaseId, currentSteps) => {
    const newStep = {
      step_number: (currentSteps?.length || 0) + 1,
      action: '',
      test_data: '',
      expected_result: ''
    };
    const updatedSteps = [...(currentSteps || []), newStep];
    setTestStepsDraft(updatedSteps);
    setEditingStepIndex(updatedSteps.length - 1);
  }, []);

  // Delete a test step
  const deleteTestStep = useCallback(async (testCaseId, currentSteps, stepIndex) => {
    if (!confirm('Are you sure you want to delete this step?')) return;
    const updatedSteps = currentSteps.filter((_, idx) => idx !== stepIndex);
    await updateTestSteps(testCaseId, updatedSteps);
  }, [updateTestSteps]);

  // Move test step up or down
  const moveTestStep = useCallback(async (testCaseId, currentSteps, fromIndex, direction) => {
    const toIndex = direction === 'up' ? fromIndex - 1 : fromIndex + 1;
    if (toIndex < 0 || toIndex >= currentSteps.length) return;
    
    const updatedSteps = [...currentSteps];
    const [movedStep] = updatedSteps.splice(fromIndex, 1);
    updatedSteps.splice(toIndex, 0, movedStep);
    
    await updateTestSteps(testCaseId, updatedSteps);
  }, [updateTestSteps]);

  // Upload evidence for a test step
  const uploadEvidence = useCallback(async (testCaseId, stepNumber, file) => {
    try {
      setUploadingEvidence({ testCaseId, stepNumber });
      const token = localStorage.getItem('token');
      
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${BACKEND_URL}/api/sprint-qa/test-cases/${testCaseId}/steps/${stepNumber}/evidence`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      // Refresh the test case data
      if (fullPageTestCase?.test_case?.key) {
        fetchTestCaseByKey(fullPageTestCase.test_case.key);
      }
      
      return response.data;
    } catch (err) {
      console.error('Failed to upload evidence:', err);
      alert('Failed to upload evidence: ' + (err.response?.data?.detail || err.message));
    } finally {
      setUploadingEvidence(null);
    }
  }, [BACKEND_URL, fullPageTestCase, fetchTestCaseByKey]);

  // Delete evidence from a test step
  const deleteEvidence = useCallback(async (testCaseId, stepNumber, filename) => {
    if (!confirm('Are you sure you want to delete this evidence?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${BACKEND_URL}/api/sprint-qa/test-cases/${testCaseId}/steps/${stepNumber}/evidence/${filename}`,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      // Refresh the test case data
      if (fullPageTestCase?.test_case?.key) {
        fetchTestCaseByKey(fullPageTestCase.test_case.key);
      }
    } catch (err) {
      console.error('Failed to delete evidence:', err);
      alert('Failed to delete evidence: ' + (err.response?.data?.detail || err.message));
    }
  }, [BACKEND_URL, fullPageTestCase, fetchTestCaseByKey]);

  // Handle file input change
  const handleEvidenceFileChange = useCallback((e, testCaseId, stepNumber) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadEvidence(testCaseId, stepNumber, file);
    }
    // Reset input so same file can be selected again
    e.target.value = '';
  }, [uploadEvidence]);

  const fetchQaSettings = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      // Use actual project ID for settings (skip for legacy/unassigned)
      const params = (currentProject?.id && currentProject.id !== 'unassigned') 
        ? `?project_id=${currentProject.id}` 
        : '';
      const response = await axios.get(`${BACKEND_URL}/api/sprint-qa/settings${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQaSettings(response.data);
      setSettingsForm({
        requirement_prefix: response.data.requirement_prefix || 'REQ',
        test_scenario_prefix: response.data.test_scenario_prefix || 'TS',
        test_case_prefix: response.data.test_case_prefix || 'TC',
        sprint_prefix: response.data.sprint_prefix || 'SPR'
      });
    } catch (err) {
      console.error('Failed to fetch settings:', err);
    }
  }, [BACKEND_URL, currentProject]);

  // Initial load - fetch project from URL slug
  useEffect(() => {
    fetchProjectBySlug();
    fetchTeamMembers();
    fetchAvailableProjects();
    checkAzureAddonStatus();
  }, [fetchProjectBySlug, fetchTeamMembers, fetchAvailableProjects]);

  // Check if Azure DevOps addon is enabled
  const checkAzureAddonStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const response = await axios.get(
        `${getBackendURL()}/api/addons/azure-devops/status`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAzureAddonEnabled(response.data.enabled || false);
    } catch (error) {
      // 403 means plan not supported - that's fine
      if (error.response?.status !== 403) {
        console.error('Failed to check Azure DevOps addon status:', error);
      }
      setAzureAddonEnabled(false);
    }
  };

  // When project is loaded, fetch all data
  useEffect(() => {
    if (!projectLoading && currentProject) {
      fetchSprints();
      fetchQaSettings();
      fetchRequirements();
    }
  }, [currentProject, projectLoading, fetchSprints, fetchQaSettings, fetchRequirements]);

  // Load requirement from URL if present (uses requirement key like REQ-1)
  // Only runs once when requirements are first loaded
  useEffect(() => {
    if (!projectLoading && currentProject && requirementKeyFromUrl && requirements.length > 0 && !urlRequirementProcessed.current) {
      // Find the requirement by key in our loaded list
      const reqFromUrl = requirements.find(r => r.key === requirementKeyFromUrl);
      if (reqFromUrl) {
        urlRequirementProcessed.current = true; // Mark as processed to prevent re-runs
        setSelectedRequirement(reqFromUrl);
        setActiveTab('requirements');
      } else {
        // Requirement key not found in current project - might be invalid URL
        console.warn(`Requirement ${requirementKeyFromUrl} not found in current project`);
        urlRequirementProcessed.current = true; // Still mark as processed
      }
    }
  }, [projectLoading, currentProject, requirementKeyFromUrl, requirements]);

  // Load scenario from URL for full-page view
  useEffect(() => {
    if (!projectLoading && currentProject && scenarioKeyFromUrl && !urlScenarioProcessed.current) {
      urlScenarioProcessed.current = true;
      fetchScenarioByKey(scenarioKeyFromUrl);
    }
  }, [projectLoading, currentProject, scenarioKeyFromUrl, fetchScenarioByKey]);

  // Load test case from URL for full-page view
  useEffect(() => {
    if (!projectLoading && currentProject && testCaseKeyFromUrl && !urlTestCaseProcessed.current) {
      urlTestCaseProcessed.current = true;
      fetchTestCaseByKey(testCaseKeyFromUrl);
    }
  }, [projectLoading, currentProject, testCaseKeyFromUrl, fetchTestCaseByKey]);

  // Load comments when viewing full-page scenario
  useEffect(() => {
    if (fullPageScenario?.test_scenario?.id) {
      fetchComments('test_scenario', fullPageScenario.test_scenario.id);
    }
  }, [fullPageScenario?.test_scenario?.id, fetchComments]);

  // Load comments and executions when viewing full-page test case
  useEffect(() => {
    if (fullPageTestCase?.test_case?.id) {
      fetchComments('test_case', fullPageTestCase.test_case.id);
      fetchTestExecutions(fullPageTestCase.test_case.id);
    }
  }, [fullPageTestCase?.test_case?.id, fetchComments, fetchTestExecutions]);

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      const viewType = getViewTypeFromPath();
      
      if (viewType === 'test-case') {
        const tcKey = getTestCaseKeyFromPath();
        if (tcKey) {
          // Reset refs and fetch
          urlTestCaseProcessed.current = false;
          fetchTestCaseByKey(tcKey);
        }
        setFullPageScenario(null);
        setSelectedRequirement(null);
        urlScenarioProcessed.current = false;
      } else if (viewType === 'scenario') {
        const scKey = getScenarioKeyFromPath();
        if (scKey) {
          // Reset refs and fetch
          urlScenarioProcessed.current = false;
          fetchScenarioByKey(scKey);
        }
        setFullPageTestCase(null);
        setSelectedRequirement(null);
        urlTestCaseProcessed.current = false;
      } else if (viewType === 'requirement') {
        const newReqKey = getRequirementKeyFromPath();
        if (newReqKey) {
          const reqFromUrl = requirements.find(r => r.key === newReqKey);
          if (reqFromUrl) {
            setSelectedRequirement(reqFromUrl);
            setActiveTab('requirements');
          }
        }
        // Clear full-page views and reset refs
        setFullPageScenario(null);
        setFullPageTestCase(null);
        urlScenarioProcessed.current = false;
        urlTestCaseProcessed.current = false;
      } else {
        // Project view - clear all selections and reset refs
        setSelectedRequirement(null);
        setRequirementDetail(null);
        setFullPageScenario(null);
        setFullPageTestCase(null);
        urlScenarioProcessed.current = false;
        urlTestCaseProcessed.current = false;
        urlRequirementProcessed.current = false;
      }
    };
    
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [requirements, fetchScenarioByKey, fetchTestCaseByKey]);

  useEffect(() => {
    // Only fetch if we have a valid project loaded
    if (!currentProject || projectLoading) return;
    
    if (activeTab === 'board' && selectedSprint) {
      fetchSprintBoard(selectedSprint.id);
    } else if ((activeTab === 'requirements' || activeTab === 'backlog') && requirements.length === 0) {
      // Only fetch if we don't already have requirements loaded
      fetchRequirements();
    }
  }, [activeTab, selectedSprint, fetchSprintBoard, fetchRequirements, currentProject, projectLoading, requirements.length]);

  useEffect(() => {
    if (selectedRequirement) {
      fetchRequirementDetail(selectedRequirement.id);
    }
  }, [selectedRequirement, fetchRequirementDetail]);

  // Reset forms
  const resetRequirementForm = () => setRequirementForm({
    title: '', description: '', type: 'story', priority: 'medium',
    business_rules: '', edge_cases: '', sprint_id: selectedSprint?.id || null, labels: [],
    story_points: null, assignee_id: null, reporter_id: null,
    project_id: currentProject?.id !== 'unassigned' ? currentProject?.id : null
  });
  const resetSprintForm = () => setSprintForm({ name: '', goal: '', start_date: '', end_date: '', status: 'planning', assignee_id: null });
  const resetAcForm = () => setAcForm({ title: '', given: '', when: '', then: '' });
  const resetScenarioForm = () => setScenarioForm({ title: '', description: '', story_points: null });
  const resetTestCaseForm = () => {
    setTestCaseForm({
      title: '', description: '', preconditions: '', expected_result: '',
      priority: 'medium', test_type: 'manual', test_steps: [], assignee_id: null
    });
    setNewStep({ action: '', expected_result: '' });
  };

  // Open edit requirement modal
  const openEditRequirement = (req) => {
    setEditingRequirement(req);
    setRequirementForm({
      title: req.title || '',
      description: req.description || '',
      type: req.type || 'story',
      priority: req.priority || 'medium',
      business_rules: req.business_rules || '',
      edge_cases: req.edge_cases || '',
      sprint_id: req.sprint_id || null,
      status: req.status || 'draft',
      testing_status: req.testing_status || 'not_started',
      labels: req.labels || [],
      story_points: req.story_points || null,
      assignee_id: req.assignee_id || null,
      reporter_id: req.reporter_id || null,
      project_id: req.project_id || null
    });
    setShowEditRequirementModal(true);
  };

  // CRUD Operations
  const createRequirement = async () => {
    try {
      setActionLoading(true);
      const token = localStorage.getItem('token');
      // Use project_id from form (which user can change), or default to current project
      const projectId = requirementForm.project_id || 
        (currentProject?.id && currentProject.id !== 'unassigned' ? currentProject.id : null);
      await axios.post(`${BACKEND_URL}/api/sprint-qa/requirements`, {
        ...requirementForm,
        sprint_id: requirementForm.sprint_id || selectedSprint?.id,
        project_id: projectId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowCreateRequirementModal(false);
      resetRequirementForm();
      if (activeTab === 'board' && selectedSprint) {
        fetchSprintBoard(selectedSprint.id);
      } else {
        fetchRequirements();
      }
    } catch (err) {
      console.error('Failed to create requirement:', err);
    } finally {
      setActionLoading(false);
    }
  };

  // AI Generation Functions
  const generateAIPreview = async () => {
    const projectId = requirementForm.project_id || 
      (currentProject?.id && currentProject.id !== 'unassigned' ? currentProject.id : null);
    
    if (!projectId) {
      alert('Please select a project first');
      return;
    }
    
    if (!aiPrompt && !aiFile) {
      alert('Please enter a prompt or upload a document');
      return;
    }
    
    try {
      setAiGenerating(true);
      const token = localStorage.getItem('token');
      
      const formData = new FormData();
      formData.append('project_id', projectId);
      
      if (aiPrompt) {
        formData.append('prompt', aiPrompt);
      }
      
      if (aiFile) {
        formData.append('file', aiFile);
      }
      
      const response = await axios.post(
        `${BACKEND_URL}/api/sprint-qa/requirements/generate-preview`,
        formData,
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      if (response.data.success) {
        setAiPreview(response.data.preview);
        setAiSourceInfo({
          type: response.data.source_type,
          content: response.data.source_content,
          filename: response.data.source_filename
        });
        toast({
          title: "Preview Generated",
          description: "Review the generated content below and click 'Save' to create.",
        });
      }
    } catch (err) {
      console.error('AI generation failed:', err);
      const errorMessage = err.response?.data?.detail || 'Failed to generate content. Please try again.';
      toast({
        variant: "destructive",
        title: "AI Generation Failed",
        description: errorMessage,
      });
    } finally {
      setAiGenerating(false);
    }
  };

  const saveAIGeneratedContent = async () => {
    if (!aiPreview) return;
    
    const projectId = requirementForm.project_id || 
      (currentProject?.id && currentProject.id !== 'unassigned' ? currentProject.id : null);
    
    try {
      setActionLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.post(
        `${BACKEND_URL}/api/sprint-qa/requirements/save-ai-generated`,
        {
          project_id: projectId,
          generated_content: aiPreview,
          source_type: aiSourceInfo?.type || 'prompt',
          source_content: aiSourceInfo?.content,
          source_filename: aiSourceInfo?.filename
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.data.success) {
        toast({
          title: "Content Saved Successfully",
          description: `Created: 1 Requirement, ${response.data.acceptance_criteria_count} Acceptance Criteria, ${response.data.test_scenarios_count} Test Scenarios, ${response.data.test_cases_count} Test Cases`,
        });
        setShowCreateRequirementModal(false);
        resetAIGeneration();
        resetRequirementForm();
        fetchRequirements();
      }
    } catch (err) {
      console.error('Failed to save AI-generated content:', err);
      toast({
        variant: "destructive",
        title: "Save Failed",
        description: err.response?.data?.detail || 'Failed to save. Please try again.',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const resetAIGeneration = () => {
    setAiGenerationMode('manual');
    setAiPrompt('');
    setAiFile(null);
    setAiPreview(null);
    setAiSourceInfo(null);
  };

  const updateRequirement = async () => {
    if (!editingRequirement) return;
    try {
      setActionLoading(true);
      const token = localStorage.getItem('token');
      await axios.put(`${BACKEND_URL}/api/sprint-qa/requirements/${editingRequirement.id}`, requirementForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowEditRequirementModal(false);
      setEditingRequirement(null);
      resetRequirementForm();
      // Refresh the appropriate view
      if (selectedRequirement?.id === editingRequirement.id) {
        fetchRequirementDetail(editingRequirement.id);
      }
      if (activeTab === 'board' && selectedSprint) {
        fetchSprintBoard(selectedSprint.id);
      } else {
        fetchRequirements();
      }
    } catch (err) {
      console.error('Failed to update requirement:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const assignRequirementToSprint = async (reqId, sprintId) => {
    try {
      setActionLoading(true);
      const token = localStorage.getItem('token');
      await axios.put(`${BACKEND_URL}/api/sprint-qa/requirements/${reqId}`, {
        sprint_id: sprintId || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowAssignSprintModal(false);
      // Refresh views
      if (activeTab === 'board' && selectedSprint) {
        fetchSprintBoard(selectedSprint.id);
      }
      fetchRequirements();
    } catch (err) {
      console.error('Failed to assign sprint:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const updateRequirementTestingStatus = async (reqId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${BACKEND_URL}/api/sprint-qa/requirements/${reqId}/testing-status?testing_status=${newStatus}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (selectedSprint) {
        fetchSprintBoard(selectedSprint.id);
      }
    } catch (err) {
      console.error('Failed to update testing status:', err);
    }
  };

  const deleteRequirement = async (id) => {
    if (!window.confirm('Delete this requirement and all its test scenarios/cases?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/sprint-qa/requirements/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      clearRequirementSelection();
      if (activeTab === 'board' && selectedSprint) {
        fetchSprintBoard(selectedSprint.id);
      } else {
        fetchRequirements();
      }
    } catch (err) {
      console.error('Failed to delete requirement:', err);
    }
  };

  const createSprint = async () => {
    try {
      setActionLoading(true);
      const token = localStorage.getItem('token');
      // Use actual project ID (not 'unassigned')
      const projectId = (currentProject?.id && currentProject.id !== 'unassigned') ? currentProject.id : null;
      const response = await axios.post(`${BACKEND_URL}/api/sprint-qa/sprints`, {
        ...sprintForm,
        project_id: projectId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowCreateSprintModal(false);
      resetSprintForm();
      fetchSprints();
      // Select newly created sprint
      setSelectedSprint(response.data);
    } catch (err) {
      console.error('Failed to create sprint:', err);
    } finally {
      setActionLoading(false);
    }
  };

  // Open edit sprint modal
  const openEditSprint = (sprint) => {
    setEditingSprint(sprint);
    setSprintForm({
      name: sprint.name || '',
      goal: sprint.goal || '',
      start_date: sprint.start_date ? sprint.start_date.split('T')[0] : '',
      end_date: sprint.end_date ? sprint.end_date.split('T')[0] : '',
      status: sprint.status || 'planning'
    });
    setShowEditSprintModal(true);
  };

  const updateSprint = async () => {
    if (!editingSprint) return;
    try {
      setActionLoading(true);
      const token = localStorage.getItem('token');
      await axios.put(`${BACKEND_URL}/api/sprint-qa/sprints/${editingSprint.id}`, sprintForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowEditSprintModal(false);
      setEditingSprint(null);
      resetSprintForm();
      fetchSprints();
      // Update selected sprint if it was the one being edited
      if (selectedSprint?.id === editingSprint.id) {
        setSelectedSprint({ ...selectedSprint, ...sprintForm });
      }
    } catch (err) {
      console.error('Failed to update sprint:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const deleteSprint = async (sprintId) => {
    if (!window.confirm('Are you sure you want to delete this sprint? Requirements in this sprint will be moved to the backlog.')) {
      return;
    }
    try {
      setActionLoading(true);
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/sprint-qa/sprints/${sprintId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Close edit modal if open
      setShowEditSprintModal(false);
      setEditingSprint(null);
      // Clear selected sprint if it was deleted
      if (selectedSprint?.id === sprintId) {
        setSelectedSprint(null);
      }
      fetchSprints();
      fetchRequirements();
    } catch (err) {
      console.error('Failed to delete sprint:', err);
    } finally {
      setActionLoading(false);
    }
  };

  // Move selected requirements to a sprint (or backlog if sprintId is null)
  const moveRequirementsToSprint = async (reqIds, sprintId) => {
    try {
      setActionLoading(true);
      const token = localStorage.getItem('token');
      
      // Update each requirement
      await Promise.all(reqIds.map(reqId => 
        axios.put(`${BACKEND_URL}/api/sprint-qa/requirements/${reqId}`, {
          sprint_id: sprintId
        }, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ));
      
      // Clear selection
      setSelectedRequirements([]);
      // Refresh data
      fetchRequirements();
      if (activeTab === 'board' && selectedSprint) {
        fetchSprintBoard(selectedSprint.id);
      }
    } catch (err) {
      console.error('Failed to move requirements:', err);
    } finally {
      setActionLoading(false);
    }
  };

  // Toggle requirement selection
  const toggleRequirementSelection = (reqId) => {
    setSelectedRequirements(prev => 
      prev.includes(reqId) 
        ? prev.filter(id => id !== reqId)
        : [...prev, reqId]
    );
  };

  // Select/deselect all in a section
  const toggleSelectAllInSection = (reqIds) => {
    const allSelected = reqIds.every(id => selectedRequirements.includes(id));
    if (allSelected) {
      setSelectedRequirements(prev => prev.filter(id => !reqIds.includes(id)));
    } else {
      setSelectedRequirements(prev => [...new Set([...prev, ...reqIds])]);
    }
  };

  // Backlog drag and drop handlers
  const handleBacklogDragStart = (e, req) => {
    setBacklogDragItem(req);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleBacklogDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleBacklogDrop = async (e, targetSprintId) => {
    e.preventDefault();
    if (!backlogDragItem) return;
    
    // Move the dragged item to the target sprint
    await moveRequirementsToSprint([backlogDragItem.id], targetSprintId);
    setBacklogDragItem(null);
  };

  const createAcceptanceCriteria = async () => {
    if (!selectedRequirement) return;
    try {
      setActionLoading(true);
      const token = localStorage.getItem('token');
      await axios.post(`${BACKEND_URL}/api/sprint-qa/acceptance-criteria`, {
        ...acForm,
        requirement_id: selectedRequirement.id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowAddACModal(false);
      resetAcForm();
      fetchRequirementDetail(selectedRequirement.id);
    } catch (err) {
      console.error('Failed to create AC:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const updateAcceptanceCriteria = async () => {
    if (!editingAC) return;
    try {
      setActionLoading(true);
      const token = localStorage.getItem('token');
      await axios.put(`${BACKEND_URL}/api/sprint-qa/acceptance-criteria/${editingAC.id}`, acForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowEditACModal(false);
      setEditingAC(null);
      resetAcForm();
      if (selectedRequirement) {
        fetchRequirementDetail(selectedRequirement.id);
      }
    } catch (err) {
      console.error('Failed to update AC:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const deleteAcceptanceCriteria = async (id) => {
    if (!window.confirm('Delete this acceptance criteria?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/sprint-qa/acceptance-criteria/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (selectedRequirement) {
        fetchRequirementDetail(selectedRequirement.id);
      }
    } catch (err) {
      console.error('Failed to delete AC:', err);
    }
  };

  const openEditAC = (ac) => {
    setEditingAC(ac);
    setAcForm({
      title: ac.title || '',
      given: ac.given || '',
      when: ac.when || '',
      then: ac.then || '',
      is_verified: ac.is_verified || false
    });
    setShowEditACModal(true);
  };

  const createTestScenario = async () => {
    if (!selectedRequirement) return;
    try {
      setActionLoading(true);
      const token = localStorage.getItem('token');
      await axios.post(`${BACKEND_URL}/api/sprint-qa/test-scenarios`, {
        ...scenarioForm,
        requirement_id: selectedRequirement.id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowAddScenarioModal(false);
      resetScenarioForm();
      fetchRequirementDetail(selectedRequirement.id);
    } catch (err) {
      console.error('Failed to create test scenario:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const updateTestScenario = async () => {
    if (!editingScenario) return;
    try {
      setActionLoading(true);
      const token = localStorage.getItem('token');
      await axios.put(`${BACKEND_URL}/api/sprint-qa/test-scenarios/${editingScenario.id}`, scenarioForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowEditScenarioModal(false);
      setEditingScenario(null);
      resetScenarioForm();
      if (selectedRequirement) {
        fetchRequirementDetail(selectedRequirement.id);
      }
    } catch (err) {
      console.error('Failed to update test scenario:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const openEditScenario = (scenario) => {
    setEditingScenario(scenario);
    setScenarioForm({
      title: scenario.title || '',
      description: scenario.description || ''
    });
    setShowEditScenarioModal(true);
  };

  const createTestCase = async () => {
    if (!selectedTestScenario) {
      alert('Error: No test scenario selected. Please try again.');
      return;
    }
    if (!testCaseForm.title?.trim()) {
      alert('Please enter a title for the test case.');
      return;
    }
    try {
      setActionLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(`${BACKEND_URL}/api/sprint-qa/test-cases`, {
        ...testCaseForm,
        test_scenario_id: selectedTestScenario.id,
        project_id: currentProject?.id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Test case created successfully:', response.data);
      setShowTestCaseSheet(false);
      resetTestCaseForm();
      
      // Refresh the appropriate view
      if (fullPageScenario?.test_scenario?.key) {
        // Refresh the full-page scenario view
        fetchScenarioByKey(fullPageScenario.test_scenario.key);
      } else if (selectedRequirement) {
        // Refresh the requirement detail view
        fetchRequirementDetail(selectedRequirement.id);
      }
    } catch (err) {
      console.error('Failed to create test case:', err);
      const errorMsg = err.response?.data?.detail || err.message || 'Unknown error';
      alert(`Failed to create test case: ${errorMsg}`);
    } finally {
      setActionLoading(false);
    }
  };

  const updateTestCase = async () => {
    if (!editingTestCase) {
      alert('Error: No test case selected for editing.');
      return;
    }
    if (!testCaseForm.title?.trim()) {
      alert('Please enter a title for the test case.');
      return;
    }
    try {
      setActionLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.put(`${BACKEND_URL}/api/sprint-qa/test-cases/${editingTestCase.id}`, testCaseForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Test case updated successfully:', response.data);
      setShowTestCaseSheet(false);
      setEditingTestCase(null);
      resetTestCaseForm();
      
      // Refresh the appropriate view
      if (fullPageTestCase?.test_case?.key) {
        // Refresh the full-page test case view
        fetchTestCaseByKey(fullPageTestCase.test_case.key);
      } else if (fullPageScenario?.test_scenario?.key) {
        // Refresh the full-page scenario view
        fetchScenarioByKey(fullPageScenario.test_scenario.key);
      } else if (selectedRequirement) {
        // Refresh the requirement detail view
        fetchRequirementDetail(selectedRequirement.id);
      }
    } catch (err) {
      console.error('Failed to update test case:', err);
      const errorMsg = err.response?.data?.detail || err.message || 'Unknown error';
      alert(`Failed to update test case: ${errorMsg}`);
    } finally {
      setActionLoading(false);
    }
  };

  // Open test case sheet in different modes
  const openAddTestCase = (scenario) => {
    setSelectedTestScenario(scenario);
    resetTestCaseForm();
    setTestCaseSheetMode('add');
    setEditingTestCase(null);
    setShowTestCaseSheet(true);
  };

  const openEditTestCase = (tc, scenario = null) => {
    setEditingTestCase(tc);
    // Set the scenario context for proper saving
    if (scenario) {
      setSelectedTestScenario(scenario);
    }
    setTestCaseForm({
      title: tc.title || '',
      description: tc.description || '',
      preconditions: tc.preconditions || '',
      priority: tc.priority || 'medium',
      status: tc.status || 'draft',
      test_type: tc.test_type || 'manual',
      expected_result: tc.expected_result || '',
      test_steps: tc.test_steps || [],
      assignee_id: tc.assignee_id || null
    });
    setTestCaseSheetMode('edit');
    setShowTestCaseSheet(true);
  };

  const openViewTestCase = (tc) => {
    setSelectedTestCase(tc);
    setTestCaseSheetMode('view');
    setShowTestCaseSheet(true);
  };

  // Test Execution Functions
  const startTestExecution = async (testCase) => {
    try {
      setExecutionLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(`${BACKEND_URL}/api/sprint-qa/test-executions/start`, {
        test_case_id: testCase.id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentExecution(response.data);
      setShowExecutionSheet(true);
      setShowTestCaseSheet(false);
    } catch (err) {
      console.error('Failed to start test execution:', err);
      alert('Failed to start test execution: ' + (err.response?.data?.detail || err.message));
    } finally {
      setExecutionLoading(false);
    }
  };

  const updateStepResult = async (stepNumber, status, actualResult = null, notes = null) => {
    if (!currentExecution) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`${BACKEND_URL}/api/sprint-qa/test-executions/${currentExecution.id}/step`, {
        step_number: stepNumber,
        status: status,
        actual_result: actualResult,
        notes: notes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentExecution(response.data);
    } catch (err) {
      console.error('Failed to update step result:', err);
      alert('Failed to update step: ' + (err.response?.data?.detail || err.message));
    }
  };

  const completeTestExecution = async (overallStatus, notes = null) => {
    if (!currentExecution) return;
    try {
      setExecutionLoading(true);
      const token = localStorage.getItem('token');
      await axios.put(`${BACKEND_URL}/api/sprint-qa/test-executions/${currentExecution.id}/complete`, {
        overall_status: overallStatus,
        notes: notes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowExecutionSheet(false);
      setCurrentExecution(null);
      // Refresh the requirement detail to show updated test case status
      if (selectedRequirement) {
        fetchRequirementDetail(selectedRequirement.id);
      }
      // Refresh the test case view if open
      if (fullPageTestCase?.test_case?.key) {
        fetchTestCaseByKey(fullPageTestCase.test_case.key);
      }
      alert(`Test execution completed: ${overallStatus.toUpperCase()}`);
    } catch (err) {
      console.error('Failed to complete test execution:', err);
      alert('Failed to complete execution: ' + (err.response?.data?.detail || err.message));
    } finally {
      setExecutionLoading(false);
    }
  };

  // Upload evidence during test execution (to execution record, not test case template)
  const uploadExecutionEvidence = async (executionId, stepNumber, file) => {
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      
      // Upload to execution-specific endpoint
      await axios.post(
        `${BACKEND_URL}/api/sprint-qa/test-executions/${executionId}/steps/${stepNumber}/evidence`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      // Refresh current execution to get updated evidence
      if (currentExecution) {
        const response = await axios.get(
          `${BACKEND_URL}/api/sprint-qa/test-executions/${currentExecution.id}`,
          { headers: { Authorization: `Bearer ${token}` }}
        );
        setCurrentExecution(response.data);
      }
      
      return true;
    } catch (err) {
      console.error('Failed to upload execution evidence:', err);
      alert('Failed to upload evidence: ' + (err.response?.data?.detail || err.message));
      return false;
    }
  };

  // Fetch single execution details for viewing
  const fetchExecutionDetails = async (executionId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BACKEND_URL}/api/sprint-qa/test-executions/${executionId}`,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setViewingExecution(response.data);
    } catch (err) {
      console.error('Failed to fetch execution details:', err);
      alert('Failed to load execution details');
    }
  };

  // Resume a WIP (in_progress) execution
  const resumeExecution = async (executionId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BACKEND_URL}/api/sprint-qa/test-executions/${executionId}`,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setCurrentExecution(response.data);
      setShowExecutionSheet(true);
    } catch (err) {
      console.error('Failed to resume execution:', err);
      alert('Failed to resume execution: ' + (err.response?.data?.detail || err.message));
    }
  };

  const deleteTestScenario = async (id) => {
    if (!window.confirm('Delete this test scenario and all its test cases?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/sprint-qa/test-scenarios/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (selectedRequirement) {
        fetchRequirementDetail(selectedRequirement.id);
      }
    } catch (err) {
      console.error('Failed to delete test scenario:', err);
    }
  };

  const deleteTestCase = async (id) => {
    if (!window.confirm('Delete this test case?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/sprint-qa/test-cases/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedTestCase(null);
      setEditingTestCase(null);
      setShowTestCaseSheet(false);
      if (selectedRequirement) {
        fetchRequirementDetail(selectedRequirement.id);
      }
    } catch (err) {
      console.error('Failed to delete test case:', err);
    }
  };

  const updateQaSettings = async () => {
    try {
      setActionLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.put(`${BACKEND_URL}/api/sprint-qa/settings`, settingsForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQaSettings(response.data);
      alert('Settings saved!');
    } catch (err) {
      console.error('Failed to update settings:', err);
    } finally {
      setActionLoading(false);
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e, req) => {
    setDraggedItem(req);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, columnId) => {
    e.preventDefault();
    if (draggedItem && draggedItem.testing_status !== columnId) {
      await updateRequirementTestingStatus(draggedItem.id, columnId);
    }
    setDraggedItem(null);
  };

  // Add test step to form
  const addTestStep = () => {
    if (!newStep.action) return;
    const step = {
      step_number: testCaseForm.test_steps.length + 1,
      action: newStep.action,
      expected_result: newStep.expected_result,
      status: 'not_executed'
    };
    setTestCaseForm({
      ...testCaseForm,
      test_steps: [...testCaseForm.test_steps, step]
    });
    setNewStep({ action: '', expected_result: '' });
  };

  const removeTestStep = (index) => {
    const steps = testCaseForm.test_steps.filter((_, i) => i !== index)
      .map((s, i) => ({ ...s, step_number: i + 1 }));
    setTestCaseForm({ ...testCaseForm, test_steps: steps });
  };

  // ============================================================================
  // Sprint Board View
  // ============================================================================
  const renderSprintBoard = () => {
    if (!selectedSprint) {
      return (
        <div className="h-full flex flex-col items-center justify-center">
          <LayoutGrid className="w-16 h-16 text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Sprint Selected</h3>
          <p className="text-sm text-gray-500 mb-4">Create or select a sprint to view the board</p>
          <Button onClick={() => { resetSprintForm(); setShowCreateSprintModal(true); }}>
            <Plus className="w-4 h-4 mr-2" /> Create Sprint
          </Button>
        </div>
      );
    }

    return (
      <div className="h-full flex flex-col">
        {/* Board Header */}
        <div className="px-4 py-3 bg-white border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Select value={selectedSprint?.id || ''} onValueChange={(id) => {
                const sprint = sprints.find(s => s.id === id);
                setSelectedSprint(sprint);
              }}>
                <SelectTrigger className="w-64">
                  <SelectValue placeholder="Select Sprint" />
                </SelectTrigger>
                <SelectContent>
                  {sprints.map(s => (
                    <SelectItem key={s.id} value={s.id}>
                      <div className="flex items-center gap-2">
                        {s.key && <span className="font-mono text-xs">{s.key}</span>}
                        <span>{s.name}</span>
                        <Badge className={`${sprintStatusConfig[s.status]?.color} text-xs ml-2`}>
                          {sprintStatusConfig[s.status]?.label}
                        </Badge>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {boardData?.stats && (
                <div className="flex items-center gap-4 text-sm">
                  <span className="flex items-center gap-1 text-gray-600">
                    <FileText className="w-4 h-4" />
                    {boardData.stats.total_requirements} Requirements
                  </span>
                  <span className="flex items-center gap-1 text-gray-600">
                    <TestTube className="w-4 h-4" />
                    {boardData.stats.total_test_cases} Test Cases
                  </span>
                  <div className="flex items-center gap-2">
                    <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-green-500 transition-all"
                        style={{ width: `${boardData.stats.progress_percentage}%` }}
                      />
                    </div>
                    <span className="text-xs text-gray-500">
                      {Math.round(boardData.stats.progress_percentage)}%
                    </span>
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => fetchSprintBoard(selectedSprint.id)}>
                <RefreshCw className="w-4 h-4" />
              </Button>
              <Button size="sm" onClick={() => { resetRequirementForm(); setShowCreateRequirementModal(true); }}>
                <Plus className="w-4 h-4 mr-1" /> Add Requirement
              </Button>
            </div>
          </div>
        </div>

        {/* Assignee Filter Bar */}
        {teamMembers.length > 0 && (
          <div className="px-4 py-2 bg-gray-50 border-b flex items-center gap-2 flex-wrap">
            <span className="text-sm text-gray-500 font-medium">Filter:</span>
            {teamMembers.map(member => {
              const isActive = assigneeFilter === member.id;
              const firstName = (member.name || member.email || '').split(' ')[0];
              return (
                <button
                  key={member.id}
                  onClick={() => setAssigneeFilter(isActive ? null : member.id)}
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                    isActive 
                      ? 'bg-indigo-600 text-white' 
                      : 'bg-white border border-gray-300 text-gray-700 hover:bg-indigo-50 hover:border-indigo-300'
                  }`}
                >
                  {firstName}
                </button>
              );
            })}
            {assigneeFilter && (
              <button
                onClick={() => setAssigneeFilter(null)}
                className="px-3 py-1 rounded-full text-sm font-medium bg-gray-200 text-gray-600 hover:bg-gray-300 transition-all flex items-center gap-1"
              >
                <X className="w-3 h-3" /> Clear
              </button>
            )}
          </div>
        )}

        {/* Board Columns */}
        <div className="flex-1 overflow-x-auto p-4">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
            </div>
          ) : (
            <div className="flex gap-4 h-full min-w-max">
              {boardColumns.sort((a, b) => a.order - b.order).map(column => {
                // Filter items by assignee if filter is active
                const allItems = boardData?.columns?.[column.id] || [];
                const items = assigneeFilter 
                  ? allItems.filter(req => req.assignee_id === assigneeFilter)
                  : allItems;
                
                return (
                  <div
                    key={column.id}
                    className="w-72 flex flex-col bg-gray-100 rounded-lg"
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, column.id)}
                  >
                    {/* Column Header - Gray Theme */}
                    <div className="px-3 py-2.5 rounded-t-lg border-b bg-gray-200 border-gray-300">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-gray-500"></span>
                          <span className="text-sm font-medium text-gray-700">{column.label}</span>
                        </div>
                        <Badge variant="secondary" className="text-xs bg-gray-300 text-gray-700">{items.length}</Badge>
                      </div>
                    </div>
                    
                    {/* Column Items */}
                    <div className="flex-1 p-2 space-y-2 overflow-y-auto min-h-[200px]">
                      {items.map(req => (
                        <div
                          key={req.id}
                          draggable
                          onDragStart={(e) => handleDragStart(e, req)}
                          className="p-3 bg-white rounded-lg border shadow-sm hover:shadow-md hover:border-indigo-300 cursor-pointer transition-all"
                        >
                          {/* Card Header - Clickable to open requirement */}
                          <div 
                            className="flex items-center gap-2 mb-2 cursor-pointer"
                            onClick={(e) => { e.stopPropagation(); selectRequirement(req); fetchRequirementDetail(req.id); }}
                          >
                            {req.key && (
                              <span className="text-xs font-mono font-semibold text-indigo-600 hover:underline">
                                {req.key}
                              </span>
                            )}
                            <Badge className={`${requirementTypeConfig[req.type]?.color} text-xs px-1.5 py-0`}>
                              {requirementTypeConfig[req.type]?.icon}
                            </Badge>
                            <Badge className={`${priorityConfig[req.priority]?.color} text-xs px-1.5 py-0`}>
                              {priorityConfig[req.priority]?.icon}
                            </Badge>
                          </div>
                          
                          {/* Title - Clickable to open requirement */}
                          <h4 
                            className="text-sm font-medium text-gray-900 mb-2 line-clamp-2 hover:text-indigo-600 cursor-pointer"
                            onClick={(e) => { e.stopPropagation(); selectRequirement(req); fetchRequirementDetail(req.id); }}
                          >
                            {req.title}
                          </h4>
                          
                          {/* Progress Bar */}
                          <div className="mb-2">
                            <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                              <span>{req.test_cases_passed}/{req.test_case_count} TC</span>
                              <span>{Math.round(req.progress || 0)}%</span>
                            </div>
                            <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
                              <div 
                                className={`h-full rounded-full ${req.test_cases_failed > 0 ? 'bg-red-500' : 'bg-green-500'}`}
                                style={{ width: `${req.progress || 0}%` }}
                              />
                            </div>
                          </div>
                          
                          {/* Stats */}
                          <div className="flex items-center justify-between text-xs text-gray-400">
                            <div className="flex items-center gap-2">
                              <span className="flex items-center gap-1" title="Acceptance Criteria">
                                <CheckSquare className="w-3 h-3" />
                                {req.acceptance_criteria_count}
                              </span>
                              <span className="flex items-center gap-1" title="Test Scenarios">
                                <Layers className="w-3 h-3" />
                                {req.test_scenario_count}
                              </span>
                              <span className="flex items-center gap-1" title="Test Cases">
                                <TestTube className="w-3 h-3" />
                                {req.test_case_count || 0}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              {req.story_points !== null && req.story_points !== undefined ? (
                                <span className="bg-indigo-100 text-indigo-700 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold" title="Story Points">
                                  {req.story_points}
                                </span>
                              ) : (
                                <span className="bg-gray-100 text-gray-400 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold" title="Story Points">
                                  0
                                </span>
                              )}
                              {req.assignee_name ? (
                                <span 
                                  className="bg-blue-500 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold uppercase" 
                                  title={req.assignee_name}
                                >
                                  {req.assignee_name.substring(0, 2)}
                                </span>
                              ) : (
                                <span className="bg-gray-200 text-gray-400 w-6 h-6 rounded-full flex items-center justify-center text-xs" title="Unassigned">
                                  ?
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                      
                      {items.length === 0 && (
                        <div className="text-center py-8 text-gray-400 text-sm">
                          {assigneeFilter ? 'No items for this assignee' : 'Drop requirements here'}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  // ============================================================================
  // Requirements List View
  // ============================================================================
  const renderRequirementsList = () => {
    // Apply filters
    let filteredReqs = requirements;
    if (assigneeFilter) {
      filteredReqs = filteredReqs.filter(req => req.assignee_id === assigneeFilter);
    }
    if (noPointsFilter) {
      filteredReqs = filteredReqs.filter(req => req.story_points === null || req.story_points === undefined || req.story_points === 0);
    }
    
    return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-white border-b">
        <div className="flex items-center gap-4">
          <h2 className="text-lg font-semibold">Requirements</h2>
          <Select value={sprintFilter} onValueChange={setSprintFilter}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="All Sprints" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sprints</SelectItem>
              {sprints.map(s => (
                <SelectItem key={s.id} value={s.id}>{s.key} {s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={fetchRequirements}>
            <RefreshCw className="w-4 h-4" />
          </Button>
          {azureAddonEnabled && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setShowAzureImport(true)}
              className="text-blue-600 border-blue-200 hover:bg-blue-50"
            >
              <Cloud className="w-4 h-4 mr-1" /> Import from Azure DevOps
            </Button>
          )}
          <Button size="sm" onClick={() => { resetRequirementForm(); setShowCreateRequirementModal(true); }}>
            <Plus className="w-4 h-4 mr-1" /> New Requirement
          </Button>
        </div>
      </div>

      {/* Filter Bar */}
      {teamMembers.length > 0 && (
        <div className="px-4 py-2 bg-gray-50 border-b flex items-center gap-2 flex-wrap">
          <span className="text-sm text-gray-500 font-medium">Filter:</span>
          {teamMembers.map(member => {
            const isActive = assigneeFilter === member.id;
            const firstName = (member.name || member.email || '').split(' ')[0];
            return (
              <button
                key={member.id}
                onClick={() => setAssigneeFilter(isActive ? null : member.id)}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                  isActive 
                    ? 'bg-indigo-600 text-white' 
                    : 'bg-white border border-gray-300 text-gray-700 hover:bg-indigo-50 hover:border-indigo-300'
                }`}
              >
                {firstName}
              </button>
            );
          })}
          <div className="h-4 w-px bg-gray-300 mx-1" />
          <button
            onClick={() => setNoPointsFilter(!noPointsFilter)}
            className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
              noPointsFilter 
                ? 'bg-amber-500 text-white' 
                : 'bg-white border border-gray-300 text-gray-700 hover:bg-amber-50 hover:border-amber-300'
            }`}
          >
            No Points
          </button>
          {(assigneeFilter || noPointsFilter) && (
            <button
              onClick={() => { setAssigneeFilter(null); setNoPointsFilter(false); }}
              className="px-3 py-1 rounded-full text-sm font-medium bg-gray-200 text-gray-600 hover:bg-gray-300 transition-all flex items-center gap-1"
            >
              <X className="w-3 h-3" /> Clear All
            </button>
          )}
        </div>
      )}

      {/* List */}
      <div className="flex-1 overflow-auto p-4">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
          </div>
        ) : filteredReqs.length === 0 ? (
          <Card className="p-12 text-center">
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">{(assigneeFilter || noPointsFilter) ? 'No Matching Requirements' : 'No Requirements Yet'}</h3>
            <p className="text-sm text-gray-500 mb-4">{(assigneeFilter || noPointsFilter) ? 'Try adjusting your filters' : 'Create your first requirement to get started'}</p>
            {!(assigneeFilter || noPointsFilter) && (
              <Button onClick={() => setShowCreateRequirementModal(true)}>
                <Plus className="w-4 h-4 mr-2" /> Create Requirement
              </Button>
            )}
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredReqs.map(req => (
              <Card 
                key={req.id} 
                className={`p-4 cursor-pointer transition-all hover:shadow-md hover:border-indigo-200 ${selectedRequirement?.id === req.id ? 'border-indigo-500 ring-1 ring-indigo-500' : ''}`}
                onClick={() => selectRequirement(req, { switchTab: false })}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      {req.key && (
                        <span className="text-xs font-mono font-semibold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded">
                          {req.key}
                        </span>
                      )}
                      <Badge className={requirementTypeConfig[req.type]?.color}>
                        {requirementTypeConfig[req.type]?.icon} {requirementTypeConfig[req.type]?.label}
                      </Badge>
                      <Badge className={requirementStatusConfig[req.status]?.color}>
                        {requirementStatusConfig[req.status]?.label}
                      </Badge>
                      <Badge className={priorityConfig[req.priority]?.color}>
                        {priorityConfig[req.priority]?.icon} {priorityConfig[req.priority]?.label}
                      </Badge>
                    </div>
                    <h3 className="font-medium text-gray-900 mb-1">{req.title}</h3>
                    {req.description && (
                      <p className="text-sm text-gray-500 line-clamp-2">{req.description}</p>
                    )}
                    <div className="flex items-center gap-4 mt-3 text-xs text-gray-400">
                      <span className="flex items-center gap-1">
                        <CheckSquare className="w-3 h-3" />
                        {req.acceptance_criteria_count || 0} AC
                      </span>
                      <span className="flex items-center gap-1">
                        <Layers className="w-3 h-3" />
                        {req.test_scenario_count || 0} Scenarios
                      </span>
                      <span className="flex items-center gap-1">
                        <TestTube className="w-3 h-3" />
                        {req.test_case_count || 0} Test Cases
                      </span>
                      {req.story_points !== null && req.story_points !== undefined && (
                        <span className="bg-indigo-100 text-indigo-700 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold" title="Story Points">
                          {req.story_points}
                        </span>
                      )}
                      {req.assignee_name && (
                        <span className="flex items-center gap-1 bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded" title={`Assigned to: ${req.assignee_name}`}>
                          <User className="w-3 h-3" />
                          {req.assignee_name.split(' ')[0]}
                        </span>
                      )}
                      {req.reporter_name && (
                        <span className="text-gray-400" title={`Reporter: ${req.reporter_name}`}>
                          by {req.reporter_name.split(' ')[0]}
                        </span>
                      )}
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="sm">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); selectRequirement(req, { switchTab: false }); }}>
                        <FileCheck className="w-4 h-4 mr-2" /> View Details
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem 
                        onClick={(e) => { e.stopPropagation(); deleteRequirement(req.id); }} 
                        className="text-red-600"
                      >
                        <Trash2 className="w-4 h-4 mr-2" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
  };

  // ============================================================================
  // Requirement Detail View (Hierarchical)
  // ============================================================================
  const renderRequirementDetail = () => {
    if (!requirementDetail) {
      return (
        <div className="flex items-center justify-center h-full">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      );
    }

    const req = requirementDetail.requirement;
    const acs = requirementDetail.acceptance_criteria || [];
    const scenarios = requirementDetail.test_scenarios || [];
    const assignedSprint = sprints.find(s => s.id === req.sprint_id);

    return (
      <div className="h-full flex flex-col">
        {/* Header */}
        <div className="px-4 py-3 bg-white border-b">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => clearRequirementSelection()}>
                <ArrowLeft className="w-4 h-4" />
              </Button>
              {req.key && (
                <span className="text-sm font-mono font-semibold text-indigo-600 bg-indigo-50 px-2 py-1 rounded">
                  {req.key}
                </span>
              )}
              <Badge className={requirementTypeConfig[req.type]?.color}>
                {requirementTypeConfig[req.type]?.icon} {requirementTypeConfig[req.type]?.label}
              </Badge>
              <Badge className={requirementStatusConfig[req.status]?.color}>
                {requirementStatusConfig[req.status]?.label}
              </Badge>
              <Badge className={priorityConfig[req.priority]?.color}>
                {priorityConfig[req.priority]?.icon} {priorityConfig[req.priority]?.label}
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              <Button data-testid="edit-requirement-btn" variant="outline" size="sm" onClick={() => openEditRequirement(req)}>
                <Edit className="w-4 h-4 mr-1" /> Edit
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button data-testid="requirement-actions-menu" variant="ghost" size="sm">
                    <MoreVertical className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem data-testid="assign-to-sprint-menuitem" onClick={() => { setEditingRequirement(req); setShowAssignSprintModal(true); }}>
                    <Calendar className="w-4 h-4 mr-2" /> Assign to Sprint
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="delete-requirement-menuitem" onClick={() => deleteRequirement(req.id)} className="text-red-600">
                    <Trash2 className="w-4 h-4 mr-2" /> Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
          <h1 className="text-xl font-semibold text-gray-900">{req.title}</h1>
          {req.description && (
            <div className="mt-2 text-sm text-gray-600">
              <RichTextViewer content={req.description} />
            </div>
          )}
          
          {/* Sprint Assignment Badge */}
          <div className="flex items-center flex-wrap gap-x-4 gap-y-2 mt-2 text-xs">
            <div className="flex items-center gap-2">
              <span className="text-gray-500">Sprint:</span>
              {assignedSprint ? (
                <Badge variant="outline" className="text-xs">
                  <Calendar className="w-3 h-3 mr-1" />
                  {assignedSprint.key} {assignedSprint.name}
                </Badge>
              ) : (
                <Badge variant="secondary" className="text-xs text-gray-400">
                  Not assigned
                </Badge>
              )}
            </div>
            {req.story_points !== null && req.story_points !== undefined && (
              <div className="flex items-center gap-2">
                <span className="text-gray-500">Points:</span>
                <span className="bg-indigo-100 text-indigo-700 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold">
                  {req.story_points}
                </span>
              </div>
            )}
            <div className="flex items-center gap-2">
              <span className="text-gray-500">Reporter:</span>
              <span className="text-gray-700">{req.reporter_name || 'Unknown'}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-gray-500">Assignee:</span>
              {req.assignee_name ? (
                <Badge variant="outline" className="text-xs">
                  <User className="w-3 h-3 mr-1" />
                  {req.assignee_name}
                </Badge>
              ) : (
                <Badge variant="secondary" className="text-xs text-gray-400">
                  Unassigned
                </Badge>
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4 space-y-6">
          {/* AI Source Section - Show if requirement was AI generated */}
          {req.ai_generated && (
            <Card className="p-4 bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-medium text-indigo-900 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-indigo-600" />
                  AI Generated Source
                </h3>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs bg-white">
                    {req.ai_source_type === 'document' ? (
                      <><FileText className="w-3 h-3 mr-1" /> Document</>
                    ) : (
                      <><Edit className="w-3 h-3 mr-1" /> Prompt</>
                    )}
                  </Badge>
                  <Button 
                    size="sm" 
                    variant="outline"
                    className="text-xs"
                    onClick={() => {
                      setAiPrompt(req.ai_source_content || '');
                      setAiGenerationMode('ai');
                      setAiPreview(null);
                      setShowCreateRequirementModal(true);
                    }}
                  >
                    <RefreshCw className="w-3 h-3 mr-1" /> Re-generate
                  </Button>
                </div>
              </div>
              {req.ai_source_filename && (
                <p className="text-xs text-indigo-700 mb-2">
                  <strong>File:</strong> {req.ai_source_filename}
                </p>
              )}
              {req.ai_source_content && (
                <div className="bg-white rounded-lg p-3 border border-indigo-100 max-h-40 overflow-y-auto">
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{req.ai_source_content}</p>
                </div>
              )}
            </Card>
          )}

          {/* Business Rules & Edge Cases */}
          {(req.business_rules || req.edge_cases) && (
            <div className="grid grid-cols-2 gap-4">
              {req.business_rules && (
                <Card className="p-4">
                  <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" /> Business Rules
                  </h3>
                  <p className="text-sm text-gray-600 whitespace-pre-wrap">{req.business_rules}</p>
                </Card>
              )}
              {req.edge_cases && (
                <Card className="p-4">
                  <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                    <Flag className="w-4 h-4" /> Edge Cases
                  </h3>
                  <p className="text-sm text-gray-600 whitespace-pre-wrap">{req.edge_cases}</p>
                </Card>
              )}
            </div>
          )}

          {/* Acceptance Criteria Section */}
          <Card className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium text-gray-900 flex items-center gap-2">
                <CheckSquare className="w-5 h-5 text-indigo-600" />
                Acceptance Criteria ({acs.length})
              </h3>
              <Button size="sm" variant="outline" onClick={() => { resetAcForm(); setShowAddACModal(true); }}>
                <Plus className="w-4 h-4 mr-1" /> Add AC
              </Button>
            </div>
            
            {acs.length === 0 ? (
              <p className="text-sm text-gray-500 italic">No acceptance criteria defined yet.</p>
            ) : (
              <div className="space-y-3">
                {acs.map((ac, idx) => (
                  <div key={ac.id} className="p-3 bg-gray-50 rounded-lg border hover:border-indigo-200 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-semibold text-gray-500">AC-{idx + 1}</span>
                          {ac.is_verified && (
                            <Badge className="bg-green-100 text-green-700 text-xs">Verified</Badge>
                          )}
                        </div>
                        <h4 className="font-medium text-gray-900">{ac.title}</h4>
                        {ac.given && (
                          <div className="mt-2 space-y-1 text-sm">
                            <p><span className="font-semibold text-green-700">Given:</span> {ac.given}</p>
                            <p><span className="font-semibold text-blue-700">When:</span> {ac.when}</p>
                            <p><span className="font-semibold text-purple-700">Then:</span> {ac.then}</p>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1 ml-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => openEditAC(ac)}
                          className="h-8 w-8 p-0"
                        >
                          <Edit className="w-4 h-4 text-gray-500" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => deleteAcceptanceCriteria(ac.id)}
                          className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Test Scenarios Section */}
          <Card className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium text-gray-900 flex items-center gap-2">
                <Layers className="w-5 h-5 text-indigo-600" />
                Test Scenarios ({scenarios.length})
              </h3>
              <Button size="sm" variant="outline" onClick={() => { resetScenarioForm(); setShowAddScenarioModal(true); }}>
                <Plus className="w-4 h-4 mr-1" /> Add Scenario
              </Button>
            </div>
            
            {scenarios.length === 0 ? (
              <p className="text-sm text-gray-500 italic">No test scenarios defined yet.</p>
            ) : (
              <div className="space-y-4">
                {scenarios.map(ts => (
                  <div key={ts.id} className="border rounded-lg">
                    {/* Scenario Header */}
                    <div className="p-3 bg-slate-50 border-b flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {ts.key && (
                          <span className="text-xs font-mono font-semibold text-amber-600 bg-amber-50 px-2 py-0.5 rounded">
                            {ts.key}
                          </span>
                        )}
                        <h4 
                          className="font-medium text-gray-900 hover:text-indigo-600 cursor-pointer"
                          onClick={() => navigateToScenario(ts.key)}
                          title="View full page"
                        >
                          {ts.title}
                        </h4>
                        <Badge variant="outline" className="text-xs">
                          {ts.test_cases?.length || 0} Test Cases
                        </Badge>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => openAddTestCase(ts)}
                          data-testid={`add-testcase-btn-${ts.id}`}
                          title="Add Test Case"
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigateToScenario(ts.key)}>
                              <Eye className="w-4 h-4 mr-2" /> View Full Page
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openEditScenario(ts)}>
                              <Edit className="w-4 h-4 mr-2" /> Edit Scenario
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => deleteTestScenario(ts.id)} className="text-red-600">
                              <Trash2 className="w-4 h-4 mr-2" /> Delete Scenario
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                    
                    {/* Scenario Description */}
                    {ts.description && (
                      <div className="px-3 py-2 bg-slate-50/50 border-b text-sm text-gray-600">
                        <RichTextViewer content={ts.description} className="[&_p]:my-1" />
                      </div>
                    )}
                    
                    {/* Test Cases */}
                    <div className="p-3">
                      {ts.test_cases?.length === 0 ? (
                        <p className="text-sm text-gray-400 italic text-center py-2">
                          No test cases yet. Click + to add one.
                        </p>
                      ) : (
                        <div className="space-y-2">
                          {ts.test_cases?.map(tc => (
                            <div 
                              key={tc.id}
                              className="p-3 bg-white border rounded-lg hover:border-indigo-300 cursor-pointer transition-all group"
                              onClick={() => openViewTestCase(tc)}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  {tc.key && (
                                    <span 
                                      className="text-xs font-mono font-semibold text-teal-600 bg-teal-50 px-1.5 py-0.5 rounded hover:bg-teal-100"
                                      onClick={(e) => { e.stopPropagation(); navigateToTestCase(tc.key); }}
                                      title="View full page"
                                    >
                                      {tc.key}
                                    </span>
                                  )}
                                  <span 
                                    className="text-sm font-medium text-gray-900 hover:text-indigo-600"
                                    onClick={(e) => { e.stopPropagation(); navigateToTestCase(tc.key); }}
                                    title="View full page"
                                  >
                                    {tc.title}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                                    onClick={(e) => { e.stopPropagation(); navigateToTestCase(tc.key); }}
                                    title="View Full Page"
                                  >
                                    <Eye className="w-3.5 h-3.5 text-gray-500" />
                                  </Button>
                                  <Badge className={testCaseStatusConfig[tc.status]?.color + ' text-xs'}>
                                    {testCaseStatusConfig[tc.status]?.label}
                                  </Badge>
                                  <Badge className={priorityConfig[tc.priority]?.color + ' text-xs'}>
                                    {priorityConfig[tc.priority]?.icon}
                                  </Badge>
                                  <Badge variant="outline" className="text-xs">
                                    {tc.test_type === 'automation' ? '🤖 Auto' : '👤 Manual'}
                                  </Badge>
                                  <Badge variant="secondary" className="text-xs">
                                    {tc.test_steps?.length || 0} Steps
                                  </Badge>
                                  {tc.last_execution_status && (
                                    <Badge className={`text-xs ${
                                      tc.last_execution_status === 'passed' ? 'bg-green-100 text-green-700' :
                                      tc.last_execution_status === 'failed' ? 'bg-red-100 text-red-700' :
                                      'bg-gray-100 text-gray-700'
                                    }`}>
                                      Last: {tc.last_execution_status}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>
    );
  };

  // ============================================================================
  // Sprints View
  // ============================================================================
  const renderSprintsView = () => (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between px-4 py-3 bg-white border-b">
        <h2 className="text-lg font-semibold">Sprints</h2>
        <Button size="sm" onClick={() => { resetSprintForm(); setShowCreateSprintModal(true); }}>
          <Plus className="w-4 h-4 mr-1" /> Create Sprint
        </Button>
      </div>
      
      <div className="flex-1 overflow-auto p-4">
        {sprints.length === 0 ? (
          <Card className="p-12 text-center">
            <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Sprints Yet</h3>
            <p className="text-sm text-gray-500 mb-4">Create your first sprint to organize work</p>
            <Button onClick={() => setShowCreateSprintModal(true)}>
              <Plus className="w-4 h-4 mr-2" /> Create Sprint
            </Button>
          </Card>
        ) : (
          <div className="grid gap-4">
            {sprints.map(sprint => (
              <Card key={sprint.id} className={`p-5 hover:border-indigo-300 transition-all ${selectedSprint?.id === sprint.id ? 'border-indigo-500 ring-1 ring-indigo-500' : ''}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1 cursor-pointer" onClick={() => { setSelectedSprint(sprint); setActiveTab('board'); }}>
                    <div className="flex items-center gap-3 mb-2">
                      {sprint.key && (
                        <span className="text-sm font-mono font-semibold text-indigo-600 bg-indigo-50 px-2 py-1 rounded">
                          {sprint.key}
                        </span>
                      )}
                      <h3 className="font-semibold text-lg">{sprint.name}</h3>
                      <Badge className={sprintStatusConfig[sprint.status]?.color}>
                        {sprintStatusConfig[sprint.status]?.label}
                      </Badge>
                    </div>
                    {sprint.goal && <p className="text-sm text-gray-600 mb-2">{sprint.goal}</p>}
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {formatDate(sprint.start_date)} - {formatDate(sprint.end_date)}
                      </span>
                      <span className="flex items-center gap-1">
                        <FileText className="w-4 h-4" />
                        {sprint.requirement_count || 0} Requirements
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      data-testid={`edit-sprint-${sprint.id}`}
                      onClick={(e) => { e.stopPropagation(); openEditSprint(sprint); }}
                    >
                      <Edit className="w-4 h-4 mr-1" /> Edit
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={(e) => { e.stopPropagation(); setSelectedSprint(sprint); setActiveTab('board'); }}
                    >
                      <LayoutGrid className="w-4 h-4 mr-1" /> View Board
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      data-testid={`delete-sprint-${sprint.id}`}
                      onClick={(e) => { e.stopPropagation(); deleteSprint(sprint.id); }}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50 hover:border-red-300"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // ============================================================================
  // Backlog View (Jira-style)
  // ============================================================================
  const renderBacklogView = () => {
    // Sort sprints: active first, then planning, then completed
    const sortedSprints = [...sprints].sort((a, b) => {
      const order = { active: 0, planning: 1, completed: 2 };
      return (order[a.status] || 1) - (order[b.status] || 1);
    });

    // Apply assignee filter to requirements
    let filteredRequirements = assigneeFilter
      ? requirements.filter(req => req.assignee_id === assigneeFilter)
      : requirements;
    
    // Apply no points filter
    if (noPointsFilter) {
      filteredRequirements = filteredRequirements.filter(req => 
        req.story_points === null || req.story_points === undefined || req.story_points === 0
      );
    }

    // Get backlog items (not assigned to any sprint AND not done)
    const backlogItems = filteredRequirements.filter(req => 
      !req.sprint_id && req.status !== 'done' && req.testing_status !== 'done'
    );

    // Get requirements for a sprint (excluding completed)
    const getSprintRequirements = (sprintId) => {
      return filteredRequirements.filter(req => 
        req.sprint_id === sprintId && req.status !== 'done' && req.testing_status !== 'done'
      );
    };

    // Render a requirement row with checkbox and drag support
    const renderRequirementRow = (req) => {
      const isSelected = selectedRequirements.includes(req.id);
      
      return (
        <div 
          key={req.id}
          draggable
          onDragStart={(e) => handleBacklogDragStart(e, req)}
          onDragEnd={() => setBacklogDragItem(null)}
          className={`flex items-center justify-between px-4 py-3 bg-white border-b border-gray-100 hover:bg-gray-50 cursor-grab transition-colors ${isSelected ? 'bg-indigo-50 hover:bg-indigo-100' : ''} ${backlogDragItem?.id === req.id ? 'opacity-50' : ''}`}
        >
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <input 
              type="checkbox" 
              checked={isSelected}
              onChange={() => toggleRequirementSelection(req.id)}
              onClick={(e) => e.stopPropagation()} 
              className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <GripVertical className="w-4 h-4 text-gray-300 cursor-grab" />
            {req.key && (
              <span 
                className="text-sm font-mono font-medium text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded whitespace-nowrap cursor-pointer hover:bg-indigo-100"
                onClick={() => { selectRequirement(req); fetchRequirementDetail(req.id); }}
              >
                {req.key}
              </span>
            )}
            <Badge className={`${requirementTypeConfig[req.type]?.color} text-xs whitespace-nowrap`}>
              {requirementTypeConfig[req.type]?.icon}
            </Badge>
            <span 
              className="font-medium text-gray-900 truncate cursor-pointer hover:text-indigo-600"
              onClick={() => { selectRequirement(req); fetchRequirementDetail(req.id); }}
            >
              {req.title}
            </span>
          </div>
          <div className="flex items-center gap-3 ml-4">
            <Badge className={`text-xs whitespace-nowrap ${
              req.testing_status === 'done' ? 'bg-green-100 text-green-700' :
              req.testing_status === 'testing' ? 'bg-amber-100 text-amber-700' :
              req.testing_status === 'to_test' ? 'bg-blue-100 text-blue-700' :
              req.testing_status === 'blocked' ? 'bg-red-100 text-red-700' :
              'bg-gray-100 text-gray-700'
            }`}>
              {req.testing_status?.replace('_', ' ').toUpperCase() || 'NOT STARTED'}
            </Badge>
            <Badge className={`text-xs ${priorityConfig[req.priority]?.color}`}>
              {priorityConfig[req.priority]?.icon}
            </Badge>
            {req.story_points !== null && req.story_points !== undefined ? (
              <span className="bg-indigo-100 text-indigo-700 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold" title="Story Points">
                {req.story_points}
              </span>
            ) : (
              <span className="bg-gray-100 text-gray-400 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold" title="Story Points">
                0
              </span>
            )}
            {req.assignee_name ? (
              <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-xs font-medium truncate max-w-[100px]" title={req.assignee_name}>
                {req.assignee_name.split(' ')[0]}
              </span>
            ) : (
              <span className="text-gray-300 text-xs">Unassigned</span>
            )}
          </div>
        </div>
      );
    };

    return (
      <div className="h-full flex flex-col overflow-auto bg-gray-100">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 bg-white border-b">
          <div className="flex items-center gap-4">
            <h2 className="text-xl font-bold text-gray-900">Backlog</h2>
            <Input 
              placeholder="Search backlog..." 
              className="w-64"
            />
          </div>
          <Button size="sm" onClick={() => { resetRequirementForm(); setShowCreateRequirementModal(true); }}>
            <Plus className="w-4 h-4 mr-1" /> Create
          </Button>
        </div>

        {/* Selection Toolbar - shows when items are selected */}
        {selectedRequirements.length > 0 && (
          <div className="flex items-center justify-between px-6 py-3 bg-indigo-50 border-b border-indigo-100">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-indigo-700">
                {selectedRequirements.length} item{selectedRequirements.length !== 1 ? 's' : ''} selected
              </span>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setSelectedRequirements([])}
                className="text-indigo-600 hover:text-indigo-800"
              >
                Clear selection
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <Select onValueChange={(sprintId) => moveRequirementsToSprint(selectedRequirements, sprintId === 'backlog' ? null : sprintId)}>
                <SelectTrigger className="w-56 bg-white">
                  <SelectValue placeholder="Move to sprint..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="backlog">
                    <span className="text-gray-500">Move to Backlog</span>
                  </SelectItem>
                  {sprints.filter(s => s.status !== 'completed').map(s => (
                    <SelectItem key={s.id} value={s.id}>
                      <div className="flex items-center gap-2">
                        {s.key && <span className="font-mono text-xs text-indigo-600">{s.key}</span>}
                        <span>{s.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {/* Assignee Filter Bar */}
        {teamMembers.length > 0 && (
          <div className="px-6 py-2 bg-gray-50 border-b flex items-center gap-2 flex-wrap">
            <span className="text-sm text-gray-500 font-medium">Filter:</span>
            {teamMembers.map(member => {
              const isActive = assigneeFilter === member.id;
              const firstName = (member.name || member.email || '').split(' ')[0];
              return (
                <button
                  key={member.id}
                  onClick={() => setAssigneeFilter(isActive ? null : member.id)}
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                    isActive 
                      ? 'bg-indigo-600 text-white' 
                      : 'bg-white border border-gray-300 text-gray-700 hover:bg-indigo-50 hover:border-indigo-300'
                  }`}
                >
                  {firstName}
                </button>
              );
            })}
            <div className="h-4 w-px bg-gray-300 mx-1" />
            <button
              onClick={() => setNoPointsFilter(!noPointsFilter)}
              className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                noPointsFilter 
                  ? 'bg-amber-500 text-white' 
                  : 'bg-white border border-gray-300 text-gray-700 hover:bg-amber-50 hover:border-amber-300'
              }`}
            >
              No Points
            </button>
            {(assigneeFilter || noPointsFilter) && (
              <button
                onClick={() => { setAssigneeFilter(null); setNoPointsFilter(false); }}
                className="px-3 py-1 rounded-full text-sm font-medium bg-gray-200 text-gray-600 hover:bg-gray-300 transition-all flex items-center gap-1"
              >
                <X className="w-3 h-3" /> Clear All
              </button>
            )}
          </div>
        )}

        <div className="flex-1 overflow-auto p-4 space-y-4">
          {/* Sprint Sections */}
          {sortedSprints.filter(s => s.status !== 'completed').map(sprint => {
            const sprintReqs = getSprintRequirements(sprint.id);
            const isActive = sprint.status === 'active';
            const allSelected = sprintReqs.length > 0 && sprintReqs.every(r => selectedRequirements.includes(r.id));
            const someSelected = sprintReqs.some(r => selectedRequirements.includes(r.id));
            
            return (
              <div 
                key={sprint.id} 
                className={`bg-white rounded-lg border border-gray-200 overflow-hidden transition-colors ${backlogDragItem && backlogDragItem.sprint_id !== sprint.id ? 'border-indigo-300 border-2' : ''}`}
                onDragOver={handleBacklogDragOver}
                onDrop={(e) => handleBacklogDrop(e, sprint.id)}
              >
                {/* Sprint Header */}
                <div className={`px-4 py-3 flex items-center justify-between ${isActive ? 'bg-indigo-50 border-b border-indigo-100' : 'bg-gray-50 border-b border-gray-200'}`}>
                  <div className="flex items-center gap-3">
                    <input 
                      type="checkbox"
                      checked={allSelected}
                      ref={el => el && (el.indeterminate = someSelected && !allSelected)}
                      onChange={() => toggleSelectAllInSection(sprintReqs.map(r => r.id))}
                      className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      disabled={sprintReqs.length === 0}
                    />
                    <ChevronDown className="w-4 h-4 text-gray-500" />
                    <div className="flex items-center gap-2">
                      {sprint.key && (
                        <span className={`text-sm font-mono font-bold ${isActive ? 'text-indigo-700' : 'text-gray-700'}`}>
                          {sprint.key}
                        </span>
                      )}
                      <span className={`font-semibold ${isActive ? 'text-indigo-900' : 'text-gray-900'}`}>
                        {sprint.name}
                      </span>
                      {isActive && (
                        <Badge className="bg-green-100 text-green-700 text-xs">ACTIVE</Badge>
                      )}
                      {sprint.status === 'planning' && (
                        <Badge className="bg-blue-100 text-blue-700 text-xs">PLANNING</Badge>
                      )}
                    </div>
                    <span className="text-sm text-gray-500">
                      {formatDate(sprint.start_date)} - {formatDate(sprint.end_date)}
                    </span>
                    <span className="text-sm text-gray-400">
                      ({sprintReqs.length} {sprintReqs.length === 1 ? 'item' : 'items'})
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {isActive && (
                      <Button variant="outline" size="sm" onClick={() => openEditSprint(sprint)}>
                        Complete Sprint
                      </Button>
                    )}
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => { setSelectedSprint(sprint); setActiveTab('board'); }}
                    >
                      <LayoutGrid className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Sprint Requirements */}
                <div className="divide-y divide-gray-100">
                  {sprintReqs.length > 0 ? (
                    sprintReqs.map(renderRequirementRow)
                  ) : (
                    <div className={`px-4 py-6 text-center text-gray-500 text-sm ${backlogDragItem ? 'bg-indigo-50 border-2 border-dashed border-indigo-300' : ''}`}>
                      {backlogDragItem ? 'Drop here to add to this sprint' : 'No requirements in this sprint'}
                    </div>
                  )}
                </div>

                {/* Add to Sprint */}
                <div className="px-4 py-2 border-t border-gray-100">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="text-gray-500 hover:text-gray-700"
                    onClick={() => { 
                      resetRequirementForm(); 
                      setRequirementForm(prev => ({ ...prev, sprint_id: sprint.id }));
                      setShowCreateRequirementModal(true); 
                    }}
                  >
                    <Plus className="w-4 h-4 mr-1" /> Create
                  </Button>
                </div>
              </div>
            );
          })}

          {/* Backlog Section (Unassigned) */}
          <div 
            className={`bg-white rounded-lg border border-gray-200 border-dashed overflow-hidden transition-colors ${backlogDragItem && backlogDragItem.sprint_id ? 'border-indigo-300 border-2' : ''}`}
            onDragOver={handleBacklogDragOver}
            onDrop={(e) => handleBacklogDrop(e, null)}
          >
            {/* Backlog Header */}
            <div className="px-4 py-3 flex items-center justify-between bg-gray-50 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <input 
                  type="checkbox"
                  checked={backlogItems.length > 0 && backlogItems.every(r => selectedRequirements.includes(r.id))}
                  ref={el => el && (el.indeterminate = backlogItems.some(r => selectedRequirements.includes(r.id)) && !backlogItems.every(r => selectedRequirements.includes(r.id)))}
                  onChange={() => toggleSelectAllInSection(backlogItems.map(r => r.id))}
                  className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  disabled={backlogItems.length === 0}
                />
                <ChevronDown className="w-4 h-4 text-gray-500" />
                <span className="font-semibold text-gray-900">Backlog</span>
                <span className="text-sm text-gray-400">
                  ({backlogItems.length} {backlogItems.length === 1 ? 'item' : 'items'})
                </span>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => { resetSprintForm(); setShowCreateSprintModal(true); }}
              >
                <Plus className="w-4 h-4 mr-1" /> Create Sprint
              </Button>
            </div>

            {/* Backlog Items */}
            <div className="divide-y divide-gray-100">
              {backlogItems.length > 0 ? (
                backlogItems.map(renderRequirementRow)
              ) : (
                <div className={`px-4 py-12 text-center ${backlogDragItem ? 'bg-indigo-50 border-2 border-dashed border-indigo-300' : ''}`}>
                  {backlogDragItem ? (
                    <p className="text-indigo-600 text-sm font-medium">Drop here to move to backlog</p>
                  ) : (
                    <>
                      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-100 mb-3">
                        <CheckCircle className="w-6 h-6 text-gray-400" />
                      </div>
                      <p className="text-gray-500 text-sm">Your backlog is empty.</p>
                      <p className="text-gray-400 text-xs mt-1">Create a requirement to get started</p>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Add to Backlog */}
            <div className="px-4 py-2 border-t border-gray-100">
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-gray-500 hover:text-gray-700"
                onClick={() => { resetRequirementForm(); setShowCreateRequirementModal(true); }}
              >
                <Plus className="w-4 h-4 mr-1" /> Create
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ============================================================================
  // Settings View
  // ============================================================================
  // Board column management functions
  const openAddColumn = () => {
    setEditingColumn(null);
    setColumnForm({ id: '', label: '' });
    setShowColumnModal(true);
  };

  const openEditColumn = (column) => {
    setEditingColumn(column);
    setColumnForm({ id: column.id, label: column.label });
    setShowColumnModal(true);
  };

  const saveColumn = () => {
    if (!columnForm.label.trim()) return;
    
    if (editingColumn) {
      // Edit existing column
      setBoardColumns(prev => prev.map(col => 
        col.id === editingColumn.id 
          ? { ...col, label: columnForm.label.trim() }
          : col
      ));
    } else {
      // Add new column
      const newId = columnForm.id.trim() || columnForm.label.toLowerCase().replace(/\s+/g, '_');
      const maxOrder = Math.max(...boardColumns.map(c => c.order), -1);
      setBoardColumns(prev => [...prev, { 
        id: newId, 
        label: columnForm.label.trim(), 
        order: maxOrder + 1 
      }]);
    }
    setShowColumnModal(false);
    setEditingColumn(null);
    setColumnForm({ id: '', label: '' });
  };

  const deleteColumn = (columnId) => {
    if (boardColumns.length <= 1) return; // Keep at least one column
    setBoardColumns(prev => prev.filter(col => col.id !== columnId));
  };

  const moveColumnUp = (index) => {
    if (index === 0) return;
    const sorted = [...boardColumns].sort((a, b) => a.order - b.order);
    const temp = sorted[index].order;
    sorted[index].order = sorted[index - 1].order;
    sorted[index - 1].order = temp;
    setBoardColumns([...sorted]);
  };

  const moveColumnDown = (index) => {
    const sorted = [...boardColumns].sort((a, b) => a.order - b.order);
    if (index === sorted.length - 1) return;
    const temp = sorted[index].order;
    sorted[index].order = sorted[index + 1].order;
    sorted[index + 1].order = temp;
    setBoardColumns([...sorted]);
  };

  const renderSettingsView = () => (
    <div className="h-full overflow-auto p-6">
      <div className="max-w-3xl">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">QA Settings</h2>
        <p className="text-gray-600 mb-6">Configure numbering prefixes and board columns for your QA module.</p>
        
        {/* Numbering Prefixes Card */}
        <Card className="p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Hash className="w-5 h-5 text-gray-500" />
            Numbering Prefixes
          </h3>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Requirement Prefix</label>
                <Input
                  value={settingsForm.requirement_prefix || ''}
                  onChange={(e) => setSettingsForm({ ...settingsForm, requirement_prefix: e.target.value.toUpperCase() })}
                  placeholder="REQ"
                  className="font-mono uppercase"
                />
                <p className="text-xs text-gray-400 mt-1">e.g., {settingsForm.requirement_prefix || 'REQ'}-1</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Test Scenario Prefix</label>
                <Input
                  value={settingsForm.test_scenario_prefix || ''}
                  onChange={(e) => setSettingsForm({ ...settingsForm, test_scenario_prefix: e.target.value.toUpperCase() })}
                  placeholder="TS"
                  className="font-mono uppercase"
                />
                <p className="text-xs text-gray-400 mt-1">e.g., {settingsForm.test_scenario_prefix || 'TS'}-1</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Test Case Prefix</label>
                <Input
                  value={settingsForm.test_case_prefix || ''}
                  onChange={(e) => setSettingsForm({ ...settingsForm, test_case_prefix: e.target.value.toUpperCase() })}
                  placeholder="TC"
                  className="font-mono uppercase"
                />
                <p className="text-xs text-gray-400 mt-1">e.g., {settingsForm.test_case_prefix || 'TC'}-1</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sprint Prefix</label>
                <Input
                  value={settingsForm.sprint_prefix || ''}
                  onChange={(e) => setSettingsForm({ ...settingsForm, sprint_prefix: e.target.value.toUpperCase() })}
                  placeholder="SPR"
                  className="font-mono uppercase"
                />
                <p className="text-xs text-gray-400 mt-1">e.g., {settingsForm.sprint_prefix || 'SPR'}-1</p>
              </div>
            </div>
          </div>
          
          <div className="mt-6 pt-4 border-t">
            <Button onClick={updateQaSettings} disabled={actionLoading}>
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Save Prefixes
            </Button>
          </div>
        </Card>

        {/* Board Columns Card */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <LayoutGrid className="w-5 h-5 text-gray-500" />
                Board Columns
              </h3>
              <p className="text-sm text-gray-500 mt-1">Configure the columns displayed on your Sprint Board</p>
            </div>
            <Button size="sm" onClick={openAddColumn}>
              <Plus className="w-4 h-4 mr-1" /> Add Column
            </Button>
          </div>
          
          <div className="space-y-2">
            {boardColumns.sort((a, b) => a.order - b.order).map((column, index) => (
              <div 
                key={column.id} 
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="flex flex-col gap-0.5">
                    <button 
                      onClick={() => moveColumnUp(index)}
                      disabled={index === 0}
                      className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <ChevronUp className="w-4 h-4" />
                    </button>
                    <button 
                      onClick={() => moveColumnDown(index)}
                      disabled={index === boardColumns.length - 1}
                      className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <ChevronDown className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-gray-400"></span>
                    <span className="font-medium text-gray-900">{column.label}</span>
                    <span className="text-xs text-gray-400 font-mono">({column.id})</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={() => openEditColumn(column)}>
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => deleteColumn(column.id)}
                    disabled={boardColumns.length <= 1}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50 disabled:opacity-30"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-700 flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              Columns will be displayed on the Sprint Board in the order shown above.
            </p>
          </div>
        </Card>
      </div>
    </div>
  );

  // ============================================================================
  // Full-Page Test Scenario View
  // ============================================================================
  const renderFullPageScenarioView = () => {
    if (!fullPageScenario) return null;
    
    const scenario = fullPageScenario.test_scenario;
    const testCases = fullPageScenario.test_cases || [];
    const parentReq = fullPageScenario.parent_requirement;
    
    const statusOptions = [
      { value: 'draft', label: 'Draft', color: 'bg-gray-100 text-gray-700' },
      { value: 'ready', label: 'Ready', color: 'bg-blue-100 text-blue-700' },
      { value: 'in_progress', label: 'In Progress', color: 'bg-yellow-100 text-yellow-700' },
      { value: 'passed', label: 'Passed', color: 'bg-green-100 text-green-700' },
      { value: 'failed', label: 'Failed', color: 'bg-red-100 text-red-700' }
    ];
    
    const priorityOptions = [
      { value: 'low', label: 'Low' },
      { value: 'medium', label: 'Medium' },
      { value: 'high', label: 'High' },
      { value: 'critical', label: 'Critical' }
    ];
    
    const currentStatus = statusOptions.find(s => s.value === scenario.status) || statusOptions[0];
    
    // Filter requirements for parent dropdown
    const filteredRequirements = requirements.filter(r => 
      parentRequirementSearch === '' || 
      r.title.toLowerCase().includes(parentRequirementSearch.toLowerCase()) ||
      r.key?.toLowerCase().includes(parentRequirementSearch.toLowerCase())
    );
    
    // Get reporter name from team members
    const reporterMember = teamMembers.find(m => m.id === scenario.created_by || m.user_id === scenario.created_by);
    const reporterName = reporterMember?.name || reporterMember?.email || scenario.created_by_name || 'Unknown';
    
    // Get assignee info
    const assigneeMember = teamMembers.find(m => m.id === scenario.assignee_id || m.user_id === scenario.assignee_id);
    const assigneeName = scenario.assignee_name || assigneeMember?.name || 'Unassigned';
    
    const formatDate = (dateStr) => {
      if (!dateStr) return '-';
      return new Date(dateStr).toLocaleDateString('en-US', { 
        month: 'short', day: 'numeric', year: 'numeric' 
      });
    };
    
    const formatTimeAgo = (dateStr) => {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      const now = new Date();
      const diff = Math.floor((now - date) / 1000);
      if (diff < 60) return 'just now';
      if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
      if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
      if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
      return formatDate(dateStr);
    };
    
    return (
      <div className="h-[calc(100vh-180px)] flex flex-col bg-white">
        {/* Breadcrumb Header */}
        <div className="border-b px-6 py-3 flex-shrink-0 bg-gray-50">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Button 
              variant="ghost" 
              size="sm"
              className="h-7 px-2"
              onClick={() => { setFullPageScenario(null); navigateToProjectView(); }}
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Spaces
            </Button>
            <ChevronRight className="w-4 h-4" />
            <span className="text-gray-700 font-medium">{currentProject?.name || 'Project'}</span>
            {parentReq && (
              <>
                <ChevronRight className="w-4 h-4" />
                <Button
                  variant="link"
                  size="sm"
                  className="text-indigo-600 p-0 h-auto font-medium"
                  onClick={() => { setFullPageScenario(null); navigateToRequirement(parentReq.key); }}
                >
                  {parentReq.key}
                </Button>
              </>
            )}
            <ChevronRight className="w-4 h-4" />
            <span className="text-amber-600 font-semibold">{scenario.key}</span>
          </div>
        </div>
        
        {/* Main Content - Two Column Layout */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left Panel - Main Content */}
          <div className="flex-1 overflow-auto p-6">
            <div className="max-w-4xl">
              {/* Title Section - Inline Editable */}
              <div className="mb-6">
                {editingScenarioTitle ? (
                  <div className="flex items-center gap-2">
                    <Input
                      value={scenarioTitleDraft}
                      onChange={(e) => setScenarioTitleDraft(e.target.value)}
                      className="text-2xl font-semibold h-12"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          updateScenarioField(scenario.id, 'title', scenarioTitleDraft);
                          setEditingScenarioTitle(false);
                        }
                        if (e.key === 'Escape') {
                          setEditingScenarioTitle(false);
                        }
                      }}
                    />
                    <Button 
                      size="sm" 
                      onClick={() => {
                        updateScenarioField(scenario.id, 'title', scenarioTitleDraft);
                        setEditingScenarioTitle(false);
                      }}
                    >
                      <Check className="w-4 h-4" />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => setEditingScenarioTitle(false)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <h1 
                    className="text-2xl font-semibold text-gray-900 mb-3 cursor-pointer hover:bg-gray-100 px-2 py-1 -mx-2 rounded transition-colors"
                    onClick={() => {
                      setScenarioTitleDraft(scenario.title);
                      setEditingScenarioTitle(true);
                    }}
                  >
                    {scenario.title}
                  </h1>
                )}
                
                <div className="flex items-center gap-3">
                  {/* Status Dropdown - Auto-save */}
                  <Select value={scenario.status || 'draft'} onValueChange={(value) => {
                    updateScenarioField(scenario.id, 'status', value);
                  }}>
                    <SelectTrigger className={`w-36 h-8 ${currentStatus.color}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {statusOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <Button size="sm" variant="outline" onClick={() => {
                    navigator.clipboard.writeText(window.location.href);
                    alert('Link copied!');
                  }}>
                    <Link className="w-4 h-4 mr-1" /> Copy Link
                  </Button>
                </div>
              </div>
              
              {/* Description Section - Inline Editable */}
              <div className="mb-8">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Description</h3>
                {editingScenarioDescription ? (
                  <div className="border rounded-lg">
                    <RichTextEditor
                      content={scenarioDescriptionDraft}
                      onChange={(content) => setScenarioDescriptionDraft(content)}
                    />
                    <div className="flex justify-end gap-2 p-2 border-t bg-gray-50">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => setEditingScenarioDescription(false)}
                      >
                        Cancel
                      </Button>
                      <Button 
                        size="sm"
                        onClick={() => {
                          updateScenarioField(scenario.id, 'description', scenarioDescriptionDraft);
                          setEditingScenarioDescription(false);
                        }}
                      >
                        <Check className="w-4 h-4 mr-1" /> Save
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div 
                    className="cursor-pointer hover:bg-gray-50 p-3 -m-3 rounded-lg transition-colors border border-transparent hover:border-gray-200 min-h-[60px]"
                    onClick={() => {
                      setScenarioDescriptionDraft(scenario.description || '');
                      setEditingScenarioDescription(true);
                    }}
                  >
                    {scenario.description ? (
                      <div className="prose prose-sm max-w-none">
                        <RichTextViewer content={scenario.description} />
                      </div>
                    ) : (
                      <p className="text-gray-400 italic">Click to add a description...</p>
                    )}
                  </div>
                )}
              </div>
              
              {/* Test Cases (Subtasks) Section */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
                    Test Cases ({testCases.length})
                  </h3>
                  <Button size="sm" variant="outline" onClick={() => openAddTestCase(scenario)}>
                    <Plus className="w-4 h-4 mr-1" /> Add Test Case
                  </Button>
                </div>
                
                {testCases.length > 0 ? (
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Key</th>
                          <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Title</th>
                          <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Priority</th>
                          <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Assignee</th>
                          <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {testCases.map(tc => (
                          <tr 
                            key={tc.id} 
                            className="hover:bg-gray-50 cursor-pointer"
                            onClick={() => navigateToTestCase(tc.key)}
                          >
                            <td className="px-4 py-3">
                              <span className="text-sm font-mono text-teal-600 font-medium">{tc.key}</span>
                            </td>
                            <td className="px-4 py-3">
                              <span className="text-sm text-indigo-600 hover:underline">{tc.title}</span>
                            </td>
                            <td className="px-4 py-3">
                              <Badge className={priorityConfig[tc.priority]?.color} variant="outline">
                                {priorityConfig[tc.priority]?.label || tc.priority}
                              </Badge>
                            </td>
                            <td className="px-4 py-3">
                              <span className="text-sm text-gray-600">
                                {tc.assignee_name || 'Unassigned'}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <Badge variant="outline" className={
                                tc.status === 'passed' ? 'bg-green-50 text-green-700 border-green-200' :
                                tc.status === 'failed' ? 'bg-red-50 text-red-700 border-red-200' :
                                tc.status === 'in_progress' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' :
                                'bg-gray-50 text-gray-600'
                              }>
                                {tc.status || 'Draft'}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="border rounded-lg p-8 text-center bg-gray-50">
                    <TestTube className="w-10 h-10 mx-auto text-gray-300 mb-2" />
                    <p className="text-gray-500 text-sm">No test cases yet</p>
                    <Button size="sm" variant="link" onClick={() => openAddTestCase(scenario)}>
                      Create first test case
                    </Button>
                  </div>
                )}
              </div>
              
              {/* Activity Section */}
              <div className="border-t pt-6">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Activity</h3>
                
                {/* Activity Tabs */}
                <div className="flex gap-4 mb-4 border-b">
                  {['comments', 'history'].map(tab => (
                    <button
                      key={tab}
                      onClick={() => setActivityTab(tab)}
                      className={`pb-2 px-1 text-sm font-medium border-b-2 transition-colors ${
                        activityTab === tab
                          ? 'border-indigo-600 text-indigo-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      {tab === 'comments' ? 'Comments' : 'History'}
                    </button>
                  ))}
                </div>
                
                {/* Comment Input */}
                {activityTab === 'comments' && (
                  <div className="space-y-4">
                    <div className="flex gap-3">
                      <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-semibold text-sm flex-shrink-0">
                        {currentUser?.name?.[0]?.toUpperCase() || 'U'}
                      </div>
                      <div className="flex-1">
                        <Textarea
                          placeholder="Add a comment..."
                          value={newComment}
                          onChange={(e) => setNewComment(e.target.value)}
                          className="min-h-[80px] resize-none"
                        />
                        <div className="flex justify-end mt-2">
                          <Button 
                            size="sm" 
                            onClick={() => addComment('test_scenario', scenario.id, newComment)}
                            disabled={!newComment.trim()}
                          >
                            <Send className="w-4 h-4 mr-1" /> Send
                          </Button>
                        </div>
                      </div>
                    </div>
                    
                    {/* Quick Reactions */}
                    <div className="flex flex-wrap gap-2 ml-11">
                      {['Looks good!', 'Need help?', 'This is blocked...', 'Can you clarify?'].map(reaction => (
                        <Button
                          key={reaction}
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs"
                          onClick={() => addComment('test_scenario', scenario.id, reaction)}
                        >
                          {reaction}
                        </Button>
                      ))}
                    </div>
                    
                    {/* Comments List */}
                    <div className="space-y-4 mt-6">
                      {commentsLoading ? (
                        <p className="text-gray-500 text-center py-4">Loading comments...</p>
                      ) : comments.length === 0 ? (
                        <p className="text-gray-400 text-center py-4 italic">No comments yet. Be the first to comment!</p>
                      ) : (
                        comments.map(comment => (
                          <div key={comment.id} className="flex gap-3">
                            <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 font-semibold text-sm flex-shrink-0">
                              {comment.author_name?.[0]?.toUpperCase() || 'U'}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-medium text-gray-900 text-sm">{comment.author_name}</span>
                                <span className="text-xs text-gray-400">{formatTimeAgo(comment.created_at)}</span>
                                {comment.is_edited && <span className="text-xs text-gray-400">(edited)</span>}
                              </div>
                              {editingComment === comment.id ? (
                                <div>
                                  <Textarea
                                    defaultValue={comment.content}
                                    id={`edit-comment-${comment.id}`}
                                    className="min-h-[60px] mb-2"
                                  />
                                  <div className="flex gap-2">
                                    <Button size="sm" onClick={() => {
                                      const content = document.getElementById(`edit-comment-${comment.id}`).value;
                                      updateComment(comment.id, content, 'test_scenario', scenario.id);
                                    }}>Save</Button>
                                    <Button size="sm" variant="outline" onClick={() => setEditingComment(null)}>Cancel</Button>
                                  </div>
                                </div>
                              ) : (
                                <div>
                                  <p className="text-gray-700 text-sm">{comment.content}</p>
                                  {comment.author_id === currentUser?.id && (
                                    <div className="flex gap-2 mt-1">
                                      <button 
                                        className="text-xs text-gray-400 hover:text-gray-600"
                                        onClick={() => setEditingComment(comment.id)}
                                      >
                                        Edit
                                      </button>
                                      <button 
                                        className="text-xs text-gray-400 hover:text-red-600"
                                        onClick={() => deleteComment(comment.id, 'test_scenario', scenario.id)}
                                      >
                                        Delete
                                      </button>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
                
                {activityTab === 'history' && (
                  <div className="text-gray-400 text-sm italic py-4">
                    History tracking coming soon...
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Right Sidebar - Details Panel with Inline Editing */}
          <div className="w-80 border-l bg-gray-50 overflow-auto flex-shrink-0">
            <div className="p-4">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Details</h3>
              
              <div className="space-y-4">
                {/* Assignee - Inline Dropdown */}
                <div>
                  <label className="text-xs font-medium text-gray-500 block mb-1">Assignee</label>
                  <Select 
                    value={scenario.assignee_id || 'unassigned'} 
                    onValueChange={(value) => {
                      const selectedMember = teamMembers.find(m => m.id === value || m.user_id === value);
                      updateScenarioField(scenario.id, 'assignee_id', value === 'unassigned' ? null : value);
                      if (selectedMember) {
                        // Also update assignee_name for display
                        const token = localStorage.getItem('token');
                        axios.put(`${BACKEND_URL}/api/sprint-qa/test-scenarios/${scenario.id}`, 
                          { assignee_id: value, assignee_name: selectedMember.name || selectedMember.email },
                          { headers: { Authorization: `Bearer ${token}` }}
                        ).then(() => {
                          if (fullPageScenario?.test_scenario?.key) {
                            fetchScenarioByKey(fullPageScenario.test_scenario.key);
                          }
                        });
                      }
                    }}
                  >
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue placeholder="Select assignee">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-gray-400" />
                          {assigneeName}
                        </div>
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                      {teamMembers.map(member => (
                        <SelectItem key={member.id || member.user_id} value={member.id || member.user_id}>
                          {member.name || member.email}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {scenario.assignee_id !== currentUser?.id && (
                    <button 
                      className="text-xs text-indigo-600 hover:underline mt-1"
                      onClick={() => {
                        const token = localStorage.getItem('token');
                        axios.put(`${BACKEND_URL}/api/sprint-qa/test-scenarios/${scenario.id}`, 
                          { assignee_id: currentUser?.id, assignee_name: currentUser?.name || currentUser?.email },
                          { headers: { Authorization: `Bearer ${token}` }}
                        ).then(() => {
                          if (fullPageScenario?.test_scenario?.key) {
                            fetchScenarioByKey(fullPageScenario.test_scenario.key);
                          }
                        });
                      }}
                    >
                      Assign to me
                    </button>
                  )}
                </div>
                
                {/* Priority - Inline Dropdown */}
                <div>
                  <label className="text-xs font-medium text-gray-500 block mb-1">Priority</label>
                  <Select 
                    value={scenario.priority || 'medium'} 
                    onValueChange={(value) => updateScenarioField(scenario.id, 'priority', value)}
                  >
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {priorityOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Parent Requirement - Searchable Dropdown */}
                <div className="relative">
                  <label className="text-xs font-medium text-gray-500 block mb-1">Parent</label>
                  {showParentRequirementDropdown ? (
                    <div className="border rounded-lg bg-white shadow-lg">
                      <div className="p-2 border-b">
                        <div className="relative">
                          <Search className="absolute left-2 top-2.5 w-4 h-4 text-gray-400" />
                          <Input
                            placeholder="Search requirements..."
                            value={parentRequirementSearch}
                            onChange={(e) => setParentRequirementSearch(e.target.value)}
                            className="pl-8 h-8 text-sm"
                            autoFocus
                          />
                        </div>
                      </div>
                      <div className="max-h-48 overflow-auto">
                        {filteredRequirements.length > 0 ? (
                          filteredRequirements.map(req => (
                            <button
                              key={req.id}
                              className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm flex items-center gap-2"
                              onClick={() => updateScenarioRequirement(scenario.id, req.id)}
                            >
                              <span className="font-mono text-indigo-600 text-xs">{req.key}</span>
                              <span className="truncate">{req.title}</span>
                            </button>
                          ))
                        ) : (
                          <p className="px-3 py-2 text-gray-400 text-sm">No requirements found</p>
                        )}
                      </div>
                      <div className="p-2 border-t">
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="w-full"
                          onClick={() => {
                            setShowParentRequirementDropdown(false);
                            setParentRequirementSearch('');
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <button
                      className="w-full text-left p-2 border rounded-lg hover:bg-gray-100 transition-colors text-sm"
                      onClick={() => setShowParentRequirementDropdown(true)}
                    >
                      {parentReq ? (
                        <span className="text-indigo-600 hover:underline">
                          {parentReq.key} - {parentReq.title}
                        </span>
                      ) : (
                        <span className="text-gray-400 italic">Select parent requirement...</span>
                      )}
                    </button>
                  )}
                </div>
                
                {/* Story Points - Inline Input */}
                <div>
                  <label className="text-xs font-medium text-gray-500 block mb-1">Story Point Estimate</label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={scenario.story_points || ''}
                    placeholder="Enter points..."
                    className="h-8 text-sm"
                    onChange={(e) => {
                      const value = e.target.value ? parseInt(e.target.value) : null;
                      updateScenarioField(scenario.id, 'story_points', value);
                    }}
                  />
                </div>
                
                {/* Reporter - Display Name with Dropdown */}
                <div>
                  <label className="text-xs font-medium text-gray-500 block mb-1">Reporter</label>
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-xs font-medium">
                      {reporterName[0]?.toUpperCase() || 'U'}
                    </div>
                    <span>{reporterName}</span>
                  </div>
                </div>
                
                {/* Created */}
                <div>
                  <label className="text-xs font-medium text-gray-500 block mb-1">Created</label>
                  <span className="text-sm text-gray-700">{formatDate(scenario.created_at)}</span>
                </div>
                
                {/* Updated */}
                <div>
                  <label className="text-xs font-medium text-gray-500 block mb-1">Updated</label>
                  <span className="text-sm text-gray-700">{formatDate(scenario.updated_at)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ============================================================================
  // Full-Page Test Case View (Zephyr Style)
  // ============================================================================
  const renderFullPageTestCaseView = () => {
    if (!fullPageTestCase) return null;
    
    const tc = fullPageTestCase.test_case;
    const parentScenario = fullPageTestCase.parent_scenario;
    const parentReq = fullPageTestCase.parent_requirement;
    
    // Get all test scenarios for the parent dropdown
    const allScenarios = requirementDetail?.test_scenarios || [];
    const filteredScenarios = allScenarios.filter(s =>
      parentScenarioSearch === '' ||
      s.title.toLowerCase().includes(parentScenarioSearch.toLowerCase()) ||
      s.key?.toLowerCase().includes(parentScenarioSearch.toLowerCase())
    );
    
    // Get assignee info
    const assigneeMember = teamMembers.find(m => m.id === tc.assignee_id || m.user_id === tc.assignee_id);
    const assigneeName = tc.assignee_name || assigneeMember?.name || 'Unassigned';
    
    // Get reporter name
    const reporterMember = teamMembers.find(m => m.id === tc.created_by || m.user_id === tc.created_by);
    const reporterName = reporterMember?.name || reporterMember?.email || tc.created_by_name || 'Unknown';
    
    const statusOptions = [
      { value: 'draft', label: 'Draft', color: 'bg-gray-100 text-gray-700' },
      { value: 'ready', label: 'Ready', color: 'bg-blue-100 text-blue-700' },
      { value: 'in_progress', label: 'In Progress', color: 'bg-yellow-100 text-yellow-700' },
      { value: 'passed', label: 'Passed', color: 'bg-green-100 text-green-700' },
      { value: 'failed', label: 'Failed', color: 'bg-red-100 text-red-700' },
      { value: 'blocked', label: 'Blocked', color: 'bg-orange-100 text-orange-700' }
    ];
    
    const priorityOptions = [
      { value: 'low', label: 'Low' },
      { value: 'medium', label: 'Medium' },
      { value: 'high', label: 'High' },
      { value: 'critical', label: 'Critical' }
    ];
    
    const testTypeOptions = [
      { value: 'manual', label: 'Manual' },
      { value: 'automated', label: 'Automated' },
      { value: 'api', label: 'API' },
      { value: 'ui', label: 'UI' }
    ];
    
    const currentStatus = statusOptions.find(s => s.value === tc.status) || statusOptions[0];
    
    const formatDate = (dateStr) => {
      if (!dateStr) return '-';
      return new Date(dateStr).toLocaleDateString('en-US', { 
        month: 'short', day: 'numeric', year: 'numeric' 
      });
    };
    
    const formatTimeAgo = (dateStr) => {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      const now = new Date();
      const diff = Math.floor((now - date) / 1000);
      if (diff < 60) return 'just now';
      if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
      if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
      if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
      return formatDate(dateStr);
    };
    
    return (
      <div className="h-[calc(100vh-180px)] flex flex-col bg-white">
        {/* Breadcrumb Header */}
        <div className="border-b px-6 py-3 flex-shrink-0 bg-gray-50">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Button 
              variant="ghost" 
              size="sm"
              className="h-7 px-2"
              onClick={() => { setFullPageTestCase(null); navigateToProjectView(); }}
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Spaces
            </Button>
            <ChevronRight className="w-4 h-4" />
            <span className="text-gray-700 font-medium">{currentProject?.name || 'Project'}</span>
            {parentReq && (
              <>
                <ChevronRight className="w-4 h-4" />
                <Button
                  variant="link"
                  size="sm"
                  className="text-indigo-600 p-0 h-auto font-medium"
                  onClick={() => { setFullPageTestCase(null); navigateToRequirement(parentReq.key); }}
                >
                  {parentReq.key}
                </Button>
              </>
            )}
            {parentScenario && (
              <>
                <ChevronRight className="w-4 h-4" />
                <Button
                  variant="link"
                  size="sm"
                  className="text-amber-600 p-0 h-auto font-medium"
                  onClick={() => { setFullPageTestCase(null); navigateToScenario(parentScenario.key); }}
                >
                  {parentScenario.key}
                </Button>
              </>
            )}
            <ChevronRight className="w-4 h-4" />
            <span className="text-teal-600 font-semibold">{tc.key}</span>
          </div>
        </div>
        
        {/* Main Content - Two Column Layout */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left Panel - Main Content */}
          <div className="flex-1 overflow-auto p-6">
            <div className="max-w-4xl">
              {/* Title Section - Inline Editable */}
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-lg font-mono font-bold text-teal-600 bg-teal-50 px-3 py-1 rounded">
                    {tc.key}
                  </span>
                  {editingTestCaseTitle ? (
                    <div className="flex items-center gap-2 flex-1">
                      <Input
                        value={testCaseTitleDraft}
                        onChange={(e) => setTestCaseTitleDraft(e.target.value)}
                        className="text-2xl font-semibold h-12 flex-1"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            updateTestCaseField(tc.id, 'title', testCaseTitleDraft);
                            setEditingTestCaseTitle(false);
                          }
                          if (e.key === 'Escape') {
                            setEditingTestCaseTitle(false);
                          }
                        }}
                      />
                      <Button 
                        size="sm" 
                        onClick={() => {
                          updateTestCaseField(tc.id, 'title', testCaseTitleDraft);
                          setEditingTestCaseTitle(false);
                        }}
                      >
                        <Check className="w-4 h-4" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => setEditingTestCaseTitle(false)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <h1 
                      className="text-2xl font-semibold text-gray-900 cursor-pointer hover:bg-gray-100 px-2 py-1 rounded transition-colors"
                      onClick={() => {
                        setTestCaseTitleDraft(tc.title);
                        setEditingTestCaseTitle(true);
                      }}
                    >
                      {tc.title}
                    </h1>
                  )}
                </div>
                
                <div className="flex items-center gap-3">
                  {/* Status Dropdown - Auto-save */}
                  <Select value={tc.status || 'draft'} onValueChange={(value) => {
                    updateTestCaseField(tc.id, 'status', value);
                  }}>
                    <SelectTrigger className={`w-36 h-8 ${currentStatus.color}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {statusOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <Button size="sm" variant="outline" onClick={() => {
                    navigator.clipboard.writeText(window.location.href);
                    alert('Link copied!');
                  }}>
                    <Link className="w-4 h-4 mr-1" /> Copy Link
                  </Button>
                  
                  <Button 
                    size="sm" 
                    className="bg-green-600 hover:bg-green-700"
                    onClick={() => startTestExecution(tc)}
                    disabled={executionLoading}
                    data-testid="run-test-btn"
                  >
                    {executionLoading ? (
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <Play className="w-4 h-4 mr-1" />
                    )}
                    Run Test
                  </Button>
                </div>
              </div>
              
              {/* Description Section - Inline Editable */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Description</h3>
                {editingTestCaseDescription ? (
                  <div className="border rounded-lg">
                    <RichTextEditor
                      content={testCaseDescriptionDraft}
                      onChange={(content) => setTestCaseDescriptionDraft(content)}
                    />
                    <div className="flex justify-end gap-2 p-2 border-t bg-gray-50">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => setEditingTestCaseDescription(false)}
                      >
                        Cancel
                      </Button>
                      <Button 
                        size="sm"
                        onClick={() => {
                          updateTestCaseField(tc.id, 'description', testCaseDescriptionDraft);
                          setEditingTestCaseDescription(false);
                        }}
                      >
                        <Check className="w-4 h-4 mr-1" /> Save
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div 
                    className="cursor-pointer hover:bg-gray-50 p-3 -m-3 rounded-lg transition-colors border border-transparent hover:border-gray-200 min-h-[60px]"
                    onClick={() => {
                      setTestCaseDescriptionDraft(tc.description || '');
                      setEditingTestCaseDescription(true);
                    }}
                  >
                    {tc.description ? (
                      <div className="prose prose-sm max-w-none">
                        <RichTextViewer content={tc.description} />
                      </div>
                    ) : (
                      <p className="text-gray-400 italic">Click to add a description...</p>
                    )}
                  </div>
                )}
              </div>
              
              {/* Preconditions - Inline Editable */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" /> Preconditions
                </h3>
                {editingTestCasePreconditions ? (
                  <div className="border border-amber-200 rounded-lg bg-amber-50">
                    <Textarea
                      value={testCasePreconditionsDraft}
                      onChange={(e) => setTestCasePreconditionsDraft(e.target.value)}
                      placeholder="What must be true before running this test?"
                      className="min-h-[80px] bg-transparent border-0 focus:ring-0"
                      autoFocus
                    />
                    <div className="flex justify-end gap-2 p-2 border-t border-amber-200">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => setEditingTestCasePreconditions(false)}
                      >
                        Cancel
                      </Button>
                      <Button 
                        size="sm"
                        onClick={() => {
                          updateTestCaseField(tc.id, 'preconditions', testCasePreconditionsDraft);
                          setEditingTestCasePreconditions(false);
                        }}
                      >
                        <Check className="w-4 h-4 mr-1" /> Save
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div 
                    className="cursor-pointer p-4 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors min-h-[50px]"
                    onClick={() => {
                      setTestCasePreconditionsDraft(tc.preconditions || '');
                      setEditingTestCasePreconditions(true);
                    }}
                  >
                    {tc.preconditions ? (
                      <p className="text-amber-700 text-sm">{tc.preconditions}</p>
                    ) : (
                      <p className="text-amber-400 italic text-sm">Click to add preconditions...</p>
                    )}
                  </div>
                )}
              </div>
              
              {/* Test Details Table (Zephyr Style) - Inline Editable */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
                    Test Details ({tc.test_steps?.length || 0} steps)
                  </h3>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => addInlineTestStep(tc.id, tc.test_steps || [])}
                    data-testid="add-inline-test-step-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add Step
                  </Button>
                </div>
                
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="text-left text-xs font-semibold text-gray-600 uppercase px-2 py-3 w-16">Order</th>
                        <th className="text-left text-xs font-semibold text-gray-600 uppercase px-4 py-3 w-12">#</th>
                        <th className="text-left text-xs font-semibold text-gray-600 uppercase px-4 py-3">Test Step</th>
                        <th className="text-left text-xs font-semibold text-gray-600 uppercase px-4 py-3 w-40">Test Data</th>
                        <th className="text-left text-xs font-semibold text-gray-600 uppercase px-4 py-3">Expected Result</th>
                        <th className="text-left text-xs font-semibold text-gray-600 uppercase px-4 py-3 w-28">Evidence</th>
                        <th className="text-left text-xs font-semibold text-gray-600 uppercase px-2 py-3 w-20">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {(editingStepIndex !== null ? testStepsDraft : tc.test_steps)?.length > 0 ? (
                        (editingStepIndex !== null ? testStepsDraft : tc.test_steps).map((step, idx) => (
                          <tr key={idx} className="hover:bg-gray-50 group">
                            {/* Move Up/Down Buttons */}
                            <td className="px-2 py-2">
                              <div className="flex flex-col gap-0.5">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-6 w-6 p-0"
                                  disabled={idx === 0}
                                  onClick={() => moveTestStep(tc.id, tc.test_steps, idx, 'up')}
                                >
                                  <ChevronUp className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-6 w-6 p-0"
                                  disabled={idx === (tc.test_steps?.length || 0) - 1}
                                  onClick={() => moveTestStep(tc.id, tc.test_steps, idx, 'down')}
                                >
                                  <ChevronDown className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                            {/* Step Number */}
                            <td className="px-4 py-3 text-sm text-gray-500 font-medium">{idx + 1}</td>
                            {/* Test Step (Action) - Editable */}
                            <td className="px-4 py-2">
                              {editingStepIndex === idx ? (
                                <Input
                                  value={testStepsDraft[idx]?.action || ''}
                                  onChange={(e) => {
                                    const updated = [...testStepsDraft];
                                    updated[idx] = { ...updated[idx], action: e.target.value };
                                    setTestStepsDraft(updated);
                                  }}
                                  placeholder="Action to perform..."
                                  className="text-sm"
                                  autoFocus
                                />
                              ) : (
                                <div 
                                  className="cursor-pointer hover:bg-gray-100 px-2 py-1 -mx-2 rounded min-h-[32px] flex items-center"
                                  onClick={() => {
                                    setTestStepsDraft([...(tc.test_steps || [])]);
                                    setEditingStepIndex(idx);
                                  }}
                                >
                                  <span className="text-sm text-gray-900">{step.action || <span className="text-gray-400 italic">Click to edit...</span>}</span>
                                </div>
                              )}
                            </td>
                            {/* Test Data - Editable */}
                            <td className="px-4 py-2">
                              {editingStepIndex === idx ? (
                                <Input
                                  value={testStepsDraft[idx]?.test_data || ''}
                                  onChange={(e) => {
                                    const updated = [...testStepsDraft];
                                    updated[idx] = { ...updated[idx], test_data: e.target.value };
                                    setTestStepsDraft(updated);
                                  }}
                                  placeholder="Test data..."
                                  className="text-sm"
                                />
                              ) : (
                                <div 
                                  className="cursor-pointer hover:bg-gray-100 px-2 py-1 -mx-2 rounded min-h-[32px] flex items-center"
                                  onClick={() => {
                                    setTestStepsDraft([...(tc.test_steps || [])]);
                                    setEditingStepIndex(idx);
                                  }}
                                >
                                  <span className="text-sm text-gray-600">{step.test_data || <span className="text-gray-400">-</span>}</span>
                                </div>
                              )}
                            </td>
                            {/* Expected Result - Editable */}
                            <td className="px-4 py-2">
                              {editingStepIndex === idx ? (
                                <Input
                                  value={testStepsDraft[idx]?.expected_result || ''}
                                  onChange={(e) => {
                                    const updated = [...testStepsDraft];
                                    updated[idx] = { ...updated[idx], expected_result: e.target.value };
                                    setTestStepsDraft(updated);
                                  }}
                                  placeholder="Expected result..."
                                  className="text-sm"
                                />
                              ) : (
                                <div 
                                  className="cursor-pointer hover:bg-gray-100 px-2 py-1 -mx-2 rounded min-h-[32px] flex items-center"
                                  onClick={() => {
                                    setTestStepsDraft([...(tc.test_steps || [])]);
                                    setEditingStepIndex(idx);
                                  }}
                                >
                                  <span className="text-sm text-gray-700">{step.expected_result || <span className="text-gray-400">-</span>}</span>
                                </div>
                              )}
                            </td>
                            {/* Evidence */}
                            <td className="px-4 py-2">
                              <div className="flex items-center gap-2">
                                {/* Evidence thumbnails */}
                                {step.evidence?.length > 0 && (
                                  <div className="flex gap-1">
                                    {step.evidence.slice(0, 3).map((filename, evidenceIdx) => (
                                      <div key={evidenceIdx} className="relative group/evidence">
                                        <img
                                          src={`${BACKEND_URL}/api/sprint-qa/evidence/${filename}`}
                                          alt={`Evidence ${evidenceIdx + 1}`}
                                          className="w-8 h-8 object-cover rounded border cursor-pointer hover:ring-2 hover:ring-indigo-400"
                                          onClick={() => setEvidencePreview({ images: step.evidence, currentIndex: evidenceIdx, testCaseId: tc.id, stepNumber: step.step_number })}
                                        />
                                        <button
                                          className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full text-xs opacity-0 group-hover/evidence:opacity-100 transition-opacity flex items-center justify-center"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            deleteEvidence(tc.id, step.step_number, filename);
                                          }}
                                        >
                                          ×
                                        </button>
                                      </div>
                                    ))}
                                    {step.evidence.length > 3 && (
                                      <span className="text-xs text-gray-500 self-center">+{step.evidence.length - 3}</span>
                                    )}
                                  </div>
                                )}
                                {/* Upload button */}
                                <label className="cursor-pointer">
                                  <input
                                    type="file"
                                    accept="image/jpeg,image/png,image/webp,image/gif"
                                    className="hidden"
                                    onChange={(e) => handleEvidenceFileChange(e, tc.id, step.step_number)}
                                    disabled={uploadingEvidence?.testCaseId === tc.id && uploadingEvidence?.stepNumber === step.step_number}
                                  />
                                  <div className={`p-1.5 rounded hover:bg-gray-100 transition-colors ${
                                    uploadingEvidence?.testCaseId === tc.id && uploadingEvidence?.stepNumber === step.step_number
                                      ? 'animate-pulse'
                                      : ''
                                  }`}>
                                    {uploadingEvidence?.testCaseId === tc.id && uploadingEvidence?.stepNumber === step.step_number ? (
                                      <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
                                    ) : (
                                      <ImagePlus className="w-4 h-4 text-gray-400 hover:text-indigo-600" />
                                    )}
                                  </div>
                                </label>
                              </div>
                            </td>
                            {/* Actions */}
                            <td className="px-2 py-2">
                              {editingStepIndex === idx ? (
                                <div className="flex gap-1">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-7 w-7 p-0 text-green-600"
                                    onClick={() => {
                                      updateTestSteps(tc.id, testStepsDraft);
                                      setEditingStepIndex(null);
                                    }}
                                  >
                                    <Check className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-7 w-7 p-0 text-gray-400"
                                    onClick={() => {
                                      setEditingStepIndex(null);
                                      setTestStepsDraft([]);
                                    }}
                                  >
                                    <X className="w-4 h-4" />
                                  </Button>
                                </div>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 w-7 p-0 text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                                  onClick={() => deleteTestStep(tc.id, tc.test_steps, idx)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="7" className="px-4 py-8 text-center text-gray-400">
                            No test steps defined. Click &quot;Add Step&quot; to create your first step.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              
              {/* Test Executions History (Zephyr Style) */}
              <div className="mb-8">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">
                  Test Executions
                </h3>
                
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="text-left text-xs font-semibold text-gray-600 uppercase px-4 py-3">Version</th>
                        <th className="text-left text-xs font-semibold text-gray-600 uppercase px-4 py-3">Test Cycle</th>
                        <th className="text-left text-xs font-semibold text-gray-600 uppercase px-4 py-3">Status</th>
                        <th className="text-left text-xs font-semibold text-gray-600 uppercase px-4 py-3">Executed By</th>
                        <th className="text-left text-xs font-semibold text-gray-600 uppercase px-4 py-3">Executed On</th>
                        <th className="text-left text-xs font-semibold text-gray-600 uppercase px-4 py-3 w-20">Details</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {testExecutions.length > 0 ? (
                        testExecutions.map((exec, idx) => {
                          // Find the user name from team members or use the stored name
                          const executorMember = teamMembers.find(m => m.id === exec.executed_by || m.user_id === exec.executed_by);
                          const executorName = exec.executed_by_name || executorMember?.name || executorMember?.email || 'Unknown';
                          
                          return (
                            <tr key={exec.id || idx} className="hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm text-gray-600">v1.0</td>
                              <td className="px-4 py-3 text-sm text-gray-600">{exec.test_cycle || 'Manual Run'}</td>
                              <td className="px-4 py-3">{getStatusBadge(exec.overall_result || exec.status)}</td>
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2">
                                  <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-xs font-medium">
                                    {executorName[0]?.toUpperCase() || 'U'}
                                  </div>
                                  <span className="text-sm text-gray-700">{executorName}</span>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">{formatDate(exec.completed_at || exec.created_at)}</td>
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-1">
                                  {/* Resume button for WIP executions */}
                                  {(exec.status === 'in_progress' || exec.overall_result === 'in_progress') && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-8 px-2 hover:bg-green-100 text-green-600"
                                      onClick={() => resumeExecution(exec.id)}
                                      title="Resume execution"
                                    >
                                      <Play className="w-4 h-4 mr-1" />
                                      Resume
                                    </Button>
                                  )}
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-8 w-8 p-0 hover:bg-indigo-100"
                                    onClick={() => fetchExecutionDetails(exec.id)}
                                    title="View execution details"
                                  >
                                    <Eye className="w-4 h-4 text-indigo-600" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="6" className="px-4 py-8 text-center text-gray-400">
                            No executions yet. Run this test case to see results.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              
              {/* Activity Section */}
              <div className="border-t pt-6">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Activity</h3>
                
                {/* Activity Tabs */}
                <div className="flex gap-4 mb-4 border-b">
                  {['comments', 'history'].map(tab => (
                    <button
                      key={tab}
                      onClick={() => setActivityTab(tab)}
                      className={`pb-2 px-1 text-sm font-medium border-b-2 transition-colors ${
                        activityTab === tab
                          ? 'border-indigo-600 text-indigo-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      {tab === 'comments' ? 'Comments' : 'History'}
                    </button>
                  ))}
                </div>
                
                {/* Comment Input */}
                {activityTab === 'comments' && (
                  <div className="space-y-4">
                    <div className="flex gap-3">
                      <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-semibold text-sm flex-shrink-0">
                        {currentUser?.name?.[0]?.toUpperCase() || 'U'}
                      </div>
                      <div className="flex-1">
                        <Textarea
                          placeholder="Add a comment..."
                          value={newComment}
                          onChange={(e) => setNewComment(e.target.value)}
                          className="min-h-[80px] resize-none"
                        />
                        <div className="flex justify-end mt-2">
                          <Button 
                            size="sm" 
                            onClick={() => addComment('test_case', tc.id, newComment)}
                            disabled={!newComment.trim()}
                          >
                            <Send className="w-4 h-4 mr-1" /> Send
                          </Button>
                        </div>
                      </div>
                    </div>
                    
                    {/* Comments List */}
                    <div className="space-y-4 mt-6">
                      {commentsLoading ? (
                        <p className="text-gray-500 text-center py-4">Loading comments...</p>
                      ) : comments.length === 0 ? (
                        <p className="text-gray-400 text-center py-4 italic">No comments yet.</p>
                      ) : (
                        comments.map(comment => (
                          <div key={comment.id} className="flex gap-3">
                            <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 font-semibold text-sm flex-shrink-0">
                              {comment.author_name?.[0]?.toUpperCase() || 'U'}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-medium text-gray-900 text-sm">{comment.author_name}</span>
                                <span className="text-xs text-gray-400">{formatTimeAgo(comment.created_at)}</span>
                                {comment.is_edited && <span className="text-xs text-gray-400">(edited)</span>}
                              </div>
                              {editingComment === comment.id ? (
                                <div>
                                  <Textarea
                                    defaultValue={comment.content}
                                    id={`edit-tc-comment-${comment.id}`}
                                    className="min-h-[60px] mb-2"
                                  />
                                  <div className="flex gap-2">
                                    <Button size="sm" onClick={() => {
                                      const content = document.getElementById(`edit-tc-comment-${comment.id}`).value;
                                      updateComment(comment.id, content, 'test_case', tc.id);
                                    }}>Save</Button>
                                    <Button size="sm" variant="outline" onClick={() => setEditingComment(null)}>Cancel</Button>
                                  </div>
                                </div>
                              ) : (
                                <div>
                                  <p className="text-gray-700 text-sm">{comment.content}</p>
                                  {comment.author_id === currentUser?.id && (
                                    <div className="flex gap-2 mt-1">
                                      <button 
                                        className="text-xs text-gray-400 hover:text-gray-600"
                                        onClick={() => setEditingComment(comment.id)}
                                      >
                                        Edit
                                      </button>
                                      <button 
                                        className="text-xs text-gray-400 hover:text-red-600"
                                        onClick={() => deleteComment(comment.id, 'test_case', tc.id)}
                                      >
                                        Delete
                                      </button>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
                
                {activityTab === 'history' && (
                  <div className="text-gray-400 text-sm italic py-4">
                    History tracking coming soon...
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Right Sidebar - Details Panel with Inline Editing */}
          <div className="w-80 border-l bg-gray-50 overflow-auto flex-shrink-0">
            <div className="p-4">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Details</h3>
              
              <div className="space-y-4">
                {/* Assignee - Inline Dropdown */}
                <div>
                  <label className="text-xs font-medium text-gray-500 block mb-1">Assignee</label>
                  <Select 
                    value={tc.assignee_id || 'unassigned'} 
                    onValueChange={(value) => {
                      const selectedMember = teamMembers.find(m => m.id === value || m.user_id === value);
                      const token = localStorage.getItem('token');
                      axios.put(`${BACKEND_URL}/api/sprint-qa/test-cases/${tc.id}`, 
                        { 
                          assignee_id: value === 'unassigned' ? null : value, 
                          assignee_name: selectedMember ? (selectedMember.name || selectedMember.email) : null 
                        },
                        { headers: { Authorization: `Bearer ${token}` }}
                      ).then(() => {
                        if (fullPageTestCase?.test_case?.key) {
                          fetchTestCaseByKey(fullPageTestCase.test_case.key);
                        }
                      });
                    }}
                  >
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue placeholder="Select assignee">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-gray-400" />
                          {assigneeName}
                        </div>
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                      {teamMembers.map(member => (
                        <SelectItem key={member.id || member.user_id} value={member.id || member.user_id}>
                          {member.name || member.email}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {tc.assignee_id !== currentUser?.id && (
                    <button 
                      className="text-xs text-indigo-600 hover:underline mt-1"
                      onClick={() => {
                        const token = localStorage.getItem('token');
                        axios.put(`${BACKEND_URL}/api/sprint-qa/test-cases/${tc.id}`, 
                          { assignee_id: currentUser?.id, assignee_name: currentUser?.name || currentUser?.email },
                          { headers: { Authorization: `Bearer ${token}` }}
                        ).then(() => {
                          if (fullPageTestCase?.test_case?.key) {
                            fetchTestCaseByKey(fullPageTestCase.test_case.key);
                          }
                        });
                      }}
                    >
                      Assign to me
                    </button>
                  )}
                </div>
                
                {/* Priority - Inline Dropdown */}
                <div>
                  <label className="text-xs font-medium text-gray-500 block mb-1">Priority</label>
                  <Select 
                    value={tc.priority || 'medium'} 
                    onValueChange={(value) => updateTestCaseField(tc.id, 'priority', value)}
                  >
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {priorityOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Test Type - Inline Dropdown */}
                <div>
                  <label className="text-xs font-medium text-gray-500 block mb-1">Test Type</label>
                  <Select 
                    value={tc.test_type || 'manual'} 
                    onValueChange={(value) => updateTestCaseField(tc.id, 'test_type', value)}
                  >
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {testTypeOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Parent Scenario - Display with link */}
                {parentScenario && (
                  <div>
                    <label className="text-xs font-medium text-gray-500 block mb-1">Parent Scenario</label>
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 h-auto text-amber-600 text-left"
                      onClick={() => { setFullPageTestCase(null); navigateToScenario(parentScenario.key); }}
                    >
                      {parentScenario.key} - {parentScenario.title}
                    </Button>
                  </div>
                )}
                
                {/* Parent Requirement */}
                {parentReq && (
                  <div>
                    <label className="text-xs font-medium text-gray-500 block mb-1">Requirement</label>
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 h-auto text-indigo-600"
                      onClick={() => { setFullPageTestCase(null); navigateToRequirement(parentReq.key); }}
                    >
                      {parentReq.key} - {parentReq.title}
                    </Button>
                  </div>
                )}
                
                {/* Reporter */}
                <div>
                  <label className="text-xs font-medium text-gray-500 block mb-1">Reporter</label>
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-xs font-medium">
                      {reporterName[0]?.toUpperCase() || 'U'}
                    </div>
                    <span>{reporterName}</span>
                  </div>
                </div>
                
                {/* Last Executed */}
                {tc.last_executed_at && (
                  <div>
                    <label className="text-xs font-medium text-gray-500 block mb-1">Last Executed</label>
                    <span className="text-sm text-gray-700">{formatDate(tc.last_executed_at)}</span>
                    {tc.last_executed_by && (
                      <span className="text-xs text-gray-500 block">by {tc.last_executed_by}</span>
                    )}
                  </div>
                )}
                
                {/* Execution Count */}
                <div>
                  <label className="text-xs font-medium text-gray-500 block mb-1">Execution Count</label>
                  <span className="text-sm text-gray-700">{tc.execution_count || 0}</span>
                </div>
                
                {/* Created */}
                <div>
                  <label className="text-xs font-medium text-gray-500 block mb-1">Created</label>
                  <span className="text-sm text-gray-700">{formatDate(tc.created_at)}</span>
                </div>
                
                {/* Updated */}
                <div>
                  <label className="text-xs font-medium text-gray-500 block mb-1">Updated</label>
                  <span className="text-sm text-gray-700">{formatDate(tc.updated_at)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ============================================================================
  // Main Render
  // ============================================================================
  
  // Shared modals/sheets that need to be available in all views
  const renderSharedModals = () => (
    <>
      {/* Test Case Sheet (Side Panel) - Add/Edit/View */}
      <Sheet open={showTestCaseSheet} onOpenChange={setShowTestCaseSheet}>
        <SheetContent className="w-[600px] sm:max-w-[600px] overflow-y-auto">
          <SheetHeader className="pb-4 border-b">
            <SheetTitle className="flex items-center gap-2">
              {testCaseSheetMode === 'add' && (
                <>
                  <Plus className="w-5 h-5 text-indigo-600" />
                  Add Test Case
                </>
              )}
              {testCaseSheetMode === 'edit' && (
                <>
                  <Edit className="w-5 h-5 text-indigo-600" />
                  Edit Test Case
                  {editingTestCase?.key && (
                    <Badge className="text-sm font-mono bg-teal-50 text-teal-700">{editingTestCase.key}</Badge>
                  )}
                </>
              )}
              {testCaseSheetMode === 'view' && (
                <>
                  <FileCheck className="w-5 h-5 text-indigo-600" />
                  View Test Case
                  {selectedTestCase?.key && (
                    <Badge className="text-sm font-mono bg-teal-50 text-teal-700">{selectedTestCase.key}</Badge>
                  )}
                </>
              )}
            </SheetTitle>
          </SheetHeader>
          
          {/* Add/Edit Mode Form */}
          {(testCaseSheetMode === 'add' || testCaseSheetMode === 'edit') && (
            <div className="py-4 space-y-4">
              <div>
                <Label htmlFor="tc-title">Title <span className="text-red-500">*</span></Label>
                <Input
                  id="tc-title"
                  value={testCaseForm.title}
                  onChange={(e) => setTestCaseForm({ ...testCaseForm, title: e.target.value })}
                  placeholder="Enter test case title"
                  data-testid="testcase-title-input"
                />
              </div>
              
              <div>
                <Label htmlFor="tc-description">Description</Label>
                <div className="border rounded-md overflow-hidden">
                  <RichTextEditor
                    content={testCaseForm.description}
                    onChange={(html) => setTestCaseForm({ ...testCaseForm, description: html })}
                    placeholder="Describe what this test case validates..."
                    minHeight={80}
                    data-testid="testcase-description-editor"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Priority</Label>
                  <Select
                    value={testCaseForm.priority}
                    onValueChange={(v) => setTestCaseForm({ ...testCaseForm, priority: v })}
                  >
                    <SelectTrigger data-testid="testcase-priority-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Test Type</Label>
                  <Select
                    value={testCaseForm.test_type}
                    onValueChange={(v) => setTestCaseForm({ ...testCaseForm, test_type: v })}
                  >
                    <SelectTrigger data-testid="testcase-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manual">Manual</SelectItem>
                      <SelectItem value="automated">Automated</SelectItem>
                      <SelectItem value="api">API</SelectItem>
                      <SelectItem value="ui">UI</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label htmlFor="tc-preconditions">Preconditions</Label>
                <Textarea
                  id="tc-preconditions"
                  value={testCaseForm.preconditions}
                  onChange={(e) => setTestCaseForm({ ...testCaseForm, preconditions: e.target.value })}
                  placeholder="What must be true before running this test?"
                  rows={2}
                  data-testid="testcase-preconditions-input"
                />
              </div>
              
              {/* Test Steps Section */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Test Steps</Label>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const newStep = {
                        step_number: testCaseForm.test_steps.length + 1,
                        action: '',
                        test_data: '',
                        expected_result: ''
                      };
                      setTestCaseForm({
                        ...testCaseForm,
                        test_steps: [...testCaseForm.test_steps, newStep]
                      });
                    }}
                    data-testid="add-test-step-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add Step
                  </Button>
                </div>
                
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {testCaseForm.test_steps.map((step, idx) => (
                    <div key={idx} className="border rounded-lg p-3 bg-gray-50">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-500">Step {idx + 1}</span>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="h-6 w-6 p-0 text-gray-400 hover:text-red-500"
                          onClick={() => {
                            const newSteps = testCaseForm.test_steps.filter((_, i) => i !== idx)
                              .map((s, i) => ({ ...s, step_number: i + 1 }));
                            setTestCaseForm({ ...testCaseForm, test_steps: newSteps });
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="space-y-2">
                        <Input
                          placeholder="Action to perform..."
                          value={step.action}
                          onChange={(e) => {
                            const newSteps = [...testCaseForm.test_steps];
                            newSteps[idx].action = e.target.value;
                            setTestCaseForm({ ...testCaseForm, test_steps: newSteps });
                          }}
                        />
                        <Input
                          placeholder="Test data (optional)..."
                          value={step.test_data || ''}
                          onChange={(e) => {
                            const newSteps = [...testCaseForm.test_steps];
                            newSteps[idx].test_data = e.target.value;
                            setTestCaseForm({ ...testCaseForm, test_steps: newSteps });
                          }}
                        />
                        <Input
                          placeholder="Expected result..."
                          value={step.expected_result}
                          onChange={(e) => {
                            const newSteps = [...testCaseForm.test_steps];
                            newSteps[idx].expected_result = e.target.value;
                            setTestCaseForm({ ...testCaseForm, test_steps: newSteps });
                          }}
                        />
                      </div>
                    </div>
                  ))}
                  {testCaseForm.test_steps.length === 0 && (
                    <p className="text-center text-gray-400 text-sm py-4">No test steps added yet. Click &quot;Add Step&quot; to begin.</p>
                  )}
                </div>
              </div>
              
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowTestCaseSheet(false)}>Cancel</Button>
                <Button
                  onClick={testCaseSheetMode === 'add' ? createTestCase : updateTestCase}
                  disabled={!testCaseForm.title || actionLoading}
                  className="bg-indigo-600 hover:bg-indigo-700"
                  data-testid="save-testcase-btn"
                >
                  {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                  {testCaseSheetMode === 'add' ? 'Create Test Case' : 'Save Changes'}
                </Button>
              </div>
            </div>
          )}
          
          {/* View Mode - Display Only */}
          {testCaseSheetMode === 'view' && selectedTestCase && (
            <div className="py-4 space-y-4">
              <div>
                <h3 className="text-xl font-semibold">{selectedTestCase.title}</h3>
                <div className="flex items-center gap-2 mt-2">
                  <Badge className={priorityConfig[selectedTestCase.priority]?.color}>
                    {priorityConfig[selectedTestCase.priority]?.label}
                  </Badge>
                  <Badge variant="outline">{selectedTestCase.test_type}</Badge>
                </div>
              </div>
              
              {selectedTestCase.description && (
                <div>
                  <Label className="text-gray-500">Description</Label>
                  <div className="mt-1">
                    <RichTextViewer content={selectedTestCase.description} />
                  </div>
                </div>
              )}
              
              {selectedTestCase.preconditions && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <Label className="text-amber-800">Preconditions</Label>
                  <p className="text-amber-700 mt-1">{selectedTestCase.preconditions}</p>
                </div>
              )}
              
              {selectedTestCase.test_steps?.length > 0 && (
                <div>
                  <Label className="text-gray-500">Test Steps</Label>
                  <div className="mt-2 space-y-2">
                    {selectedTestCase.test_steps.map((step, idx) => (
                      <div key={idx} className="border rounded-lg p-3">
                        <div className="flex items-start gap-3">
                          <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-medium flex-shrink-0">
                            {idx + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900">{step.action}</p>
                            {step.test_data && (
                              <p className="text-sm text-gray-500 mt-1">Data: {step.test_data}</p>
                            )}
                            <p className="text-sm text-gray-600 mt-1">
                              <span className="font-medium">Expected:</span> {step.expected_result}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowTestCaseSheet(false)}>Close</Button>
                <Button
                  onClick={() => {
                    openEditTestCase(selectedTestCase, selectedTestScenario);
                  }}
                  className="bg-indigo-600 hover:bg-indigo-700"
                >
                  <Edit className="w-4 h-4 mr-2" /> Edit Test Case
                </Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Test Execution Sheet */}
      <Sheet open={showExecutionSheet} onOpenChange={setShowExecutionSheet}>
        <SheetContent className="w-[500px] sm:max-w-[500px] overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <PlayCircle className="w-5 h-5 text-green-600" />
              Execute Test Case
            </SheetTitle>
            {currentExecution && (
              <div className="text-sm text-gray-500">
                <span className="font-mono text-indigo-600">{currentExecution.test_case_key}</span>
                {' - '}{currentExecution.test_case_title}
              </div>
            )}
          </SheetHeader>
          
          {currentExecution && (
            <div className="mt-6 space-y-6">
              {/* Execution Status */}
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-medium text-blue-900">In Progress</span>
                </div>
                <span className="text-xs text-gray-500">
                  Started: {formatDate(currentExecution.started_at)}
                </span>
              </div>
              
              {/* Step Results */}
              <div>
                <h3 className="text-sm font-semibold mb-3">Test Steps ({currentExecution.step_results?.length || 0})</h3>
                <div className="space-y-3">
                  {currentExecution.step_results?.map((step, idx) => (
                    <div key={idx} className={`p-3 rounded-lg border-2 ${
                      step.status === 'passed' ? 'bg-green-50 border-green-200' :
                      step.status === 'failed' ? 'bg-red-50 border-red-200' :
                      step.status === 'blocked' ? 'bg-yellow-50 border-yellow-200' :
                      'bg-gray-50 border-gray-200'
                    }`}>
                      <div className="flex items-start gap-3">
                        <span className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                          step.status === 'passed' ? 'bg-green-200 text-green-800' :
                          step.status === 'failed' ? 'bg-red-200 text-red-800' :
                          step.status === 'blocked' ? 'bg-yellow-200 text-yellow-800' :
                          'bg-gray-200 text-gray-800'
                        }`}>
                          {step.step_number}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900">{step.action}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            <span className="font-medium">Expected:</span> {step.expected_result}
                          </p>
                          {step.actual_result && (
                            <p className="text-xs text-gray-600 mt-1">
                              <span className="font-medium">Actual:</span> {step.actual_result}
                            </p>
                          )}
                          {step.notes && (
                            <p className="text-xs text-gray-500 mt-1 italic">Note: {step.notes}</p>
                          )}
                          
                          {/* Evidence Section */}
                          {step.evidence?.length > 0 && (
                            <div className="flex gap-1 mt-2">
                              {step.evidence.map((filename, eIdx) => (
                                <img
                                  key={eIdx}
                                  src={`${BACKEND_URL}/api/sprint-qa/evidence/${filename}`}
                                  alt={`Evidence ${eIdx + 1}`}
                                  className="w-10 h-10 object-cover rounded border cursor-pointer hover:ring-2 hover:ring-indigo-400"
                                  onClick={() => setEvidencePreview({ images: step.evidence, currentIndex: eIdx, testCaseId: currentExecution.test_case_id, stepNumber: step.step_number })}
                                />
                              ))}
                            </div>
                          )}
                          
                          {/* Action Buttons */}
                          <div className="flex items-center gap-2 mt-3">
                            <Button
                              size="sm"
                              variant={step.status === 'passed' ? 'default' : 'outline'}
                              className={`h-7 ${step.status === 'passed' ? 'bg-green-600 hover:bg-green-700' : 'hover:bg-green-50 hover:text-green-700 hover:border-green-300'}`}
                              onClick={() => updateStepResult(step.step_number, 'passed')}
                            >
                              <CheckCircle className="w-3.5 h-3.5 mr-1" />
                              Pass
                            </Button>
                            <Button
                              size="sm"
                              variant={step.status === 'failed' ? 'default' : 'outline'}
                              className={`h-7 ${step.status === 'failed' ? 'bg-red-600 hover:bg-red-700' : 'hover:bg-red-50 hover:text-red-700 hover:border-red-300'}`}
                              onClick={() => updateStepResult(step.step_number, 'failed')}
                            >
                              <XCircle className="w-3.5 h-3.5 mr-1" />
                              Fail
                            </Button>
                            <Button
                              size="sm"
                              variant={step.status === 'blocked' ? 'default' : 'outline'}
                              className={`h-7 ${step.status === 'blocked' ? 'bg-yellow-600 hover:bg-yellow-700' : 'hover:bg-yellow-50 hover:text-yellow-700 hover:border-yellow-300'}`}
                              onClick={() => updateStepResult(step.step_number, 'blocked')}
                            >
                              <AlertCircle className="w-3.5 h-3.5 mr-1" />
                              Block
                            </Button>
                            {/* Evidence Upload Button */}
                            <label className="cursor-pointer">
                              <input
                                type="file"
                                accept="image/jpeg,image/png,image/webp,image/gif"
                                className="hidden"
                                onChange={async (e) => {
                                  const file = e.target.files?.[0];
                                  if (file) {
                                    await uploadExecutionEvidence(currentExecution.id, step.step_number, file);
                                  }
                                  e.target.value = '';
                                }}
                              />
                              <div className="inline-flex items-center h-7 px-2 text-xs border rounded-md hover:bg-gray-100 transition-colors">
                                <ImagePlus className="w-3.5 h-3.5 mr-1 text-gray-500" />
                                Evidence
                              </div>
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Complete Execution Buttons */}
              <div className="pt-4 border-t space-y-3">
                <h3 className="text-sm font-semibold">Complete Execution</h3>
                <div className="flex items-center gap-2">
                  <Button
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    onClick={() => completeTestExecution('passed')}
                    disabled={executionLoading}
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Mark as Passed
                  </Button>
                  <Button
                    variant="destructive"
                    className="flex-1"
                    onClick={() => completeTestExecution('failed')}
                    disabled={executionLoading}
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Mark as Failed
                  </Button>
                </div>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => completeTestExecution('blocked')}
                  disabled={executionLoading}
                >
                  <AlertCircle className="w-4 h-4 mr-2" />
                  Mark as Blocked
                </Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Execution Details View Modal */}
      {viewingExecution && (
        <div 
          className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4"
          onClick={() => setViewingExecution(null)}
        >
          <div 
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <Eye className="w-5 h-5 text-indigo-600" />
                  Execution Details
                </h2>
                <p className="text-sm text-gray-500">
                  {viewingExecution.test_case_key} - {viewingExecution.test_case_title}
                </p>
              </div>
              <button
                className="p-2 hover:bg-gray-100 rounded-full"
                onClick={() => setViewingExecution(null)}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Execution Summary */}
            <div className="px-6 py-4 bg-gray-50 border-b">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {getStatusBadge(viewingExecution.overall_result || viewingExecution.status)}
                  <span className="text-sm text-gray-600">
                    Executed by: {viewingExecution.executed_by_name || 'Unknown'}
                  </span>
                </div>
                <span className="text-sm text-gray-500">
                  {formatDate(viewingExecution.completed_at || viewingExecution.created_at)}
                </span>
              </div>
            </div>
            
            {/* Step Results */}
            <div className="px-6 py-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                <ListOrdered className="w-4 h-4" />
                Step Results ({viewingExecution.step_results?.length || 0} steps)
              </h3>
              
              <div className="space-y-3">
                {viewingExecution.step_results?.map((step, idx) => (
                  <div 
                    key={idx} 
                    className={`p-4 rounded-lg border-2 ${
                      step.status === 'passed' ? 'bg-green-50 border-green-200' :
                      step.status === 'failed' ? 'bg-red-50 border-red-200' :
                      step.status === 'blocked' ? 'bg-yellow-50 border-yellow-200' :
                      'bg-gray-50 border-gray-200'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <span className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        step.status === 'passed' ? 'bg-green-200 text-green-800' :
                        step.status === 'failed' ? 'bg-red-200 text-red-800' :
                        step.status === 'blocked' ? 'bg-yellow-200 text-yellow-800' :
                        'bg-gray-200 text-gray-800'
                      }`}>
                        {step.status === 'passed' ? <CheckCircle className="w-4 h-4" /> :
                         step.status === 'failed' ? <XCircle className="w-4 h-4" /> :
                         step.status === 'blocked' ? <AlertCircle className="w-4 h-4" /> :
                         step.step_number}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-medium text-gray-500">Step {step.step_number}</span>
                          <Badge className={`text-xs ${
                            step.status === 'passed' ? 'bg-green-100 text-green-700' :
                            step.status === 'failed' ? 'bg-red-100 text-red-700' :
                            step.status === 'blocked' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {step.status?.replace('_', ' ').toUpperCase() || 'NOT EXECUTED'}
                          </Badge>
                        </div>
                        <p className="text-sm font-medium text-gray-900">{step.action}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          <span className="font-medium">Expected:</span> {step.expected_result}
                        </p>
                        {step.actual_result && (
                          <p className="text-xs text-gray-600 mt-1">
                            <span className="font-medium">Actual:</span> {step.actual_result}
                          </p>
                        )}
                        {step.notes && (
                          <p className="text-xs text-gray-500 mt-1 italic">Note: {step.notes}</p>
                        )}
                        
                        {/* Evidence Images */}
                        {step.evidence?.length > 0 && (
                          <div className="mt-3">
                            <p className="text-xs font-medium text-gray-500 mb-2">Evidence ({step.evidence.length})</p>
                            <div className="flex gap-2 flex-wrap">
                              {step.evidence.map((filename, eIdx) => (
                                <img
                                  key={eIdx}
                                  src={`${BACKEND_URL}/api/sprint-qa/evidence/${filename}`}
                                  alt={`Evidence ${eIdx + 1}`}
                                  className="w-16 h-16 object-cover rounded border cursor-pointer hover:ring-2 hover:ring-indigo-400 transition-all"
                                  onClick={() => setEvidencePreview({ 
                                    images: step.evidence, 
                                    currentIndex: eIdx, 
                                    testCaseId: viewingExecution.test_case_id, 
                                    stepNumber: step.step_number 
                                  })}
                                />
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Footer */}
            <div className="sticky bottom-0 bg-white border-t px-6 py-4">
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => setViewingExecution(null)}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Evidence Preview Modal */}
      {evidencePreview && (
        <div 
          className="fixed inset-0 bg-black/80 z-[110] flex items-center justify-center"
          onClick={() => setEvidencePreview(null)}
        >
          <div 
            className="relative max-w-4xl max-h-[90vh] p-4"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              className="absolute top-2 right-2 text-white bg-black/50 rounded-full w-8 h-8 flex items-center justify-center hover:bg-black/70 z-10"
              onClick={() => setEvidencePreview(null)}
            >
              <X className="w-5 h-5" />
            </button>
            
            {/* Navigation arrows */}
            {evidencePreview.images.length > 1 && (
              <>
                <button
                  className="absolute left-0 top-1/2 -translate-y-1/2 text-white bg-black/50 rounded-full w-10 h-10 flex items-center justify-center hover:bg-black/70"
                  onClick={() => setEvidencePreview({
                    ...evidencePreview,
                    currentIndex: (evidencePreview.currentIndex - 1 + evidencePreview.images.length) % evidencePreview.images.length
                  })}
                >
                  <ChevronUp className="w-6 h-6 rotate-[-90deg]" />
                </button>
                <button
                  className="absolute right-0 top-1/2 -translate-y-1/2 text-white bg-black/50 rounded-full w-10 h-10 flex items-center justify-center hover:bg-black/70"
                  onClick={() => setEvidencePreview({
                    ...evidencePreview,
                    currentIndex: (evidencePreview.currentIndex + 1) % evidencePreview.images.length
                  })}
                >
                  <ChevronUp className="w-6 h-6 rotate-90" />
                </button>
              </>
            )}
            
            {/* Image */}
            <img
              src={`${BACKEND_URL}/api/sprint-qa/evidence/${evidencePreview.images[evidencePreview.currentIndex]}`}
              alt={`Evidence ${evidencePreview.currentIndex + 1}`}
              className="max-w-full max-h-[80vh] object-contain rounded-lg"
            />
            
            {/* Image counter */}
            <div className="text-center text-white mt-2">
              {evidencePreview.currentIndex + 1} / {evidencePreview.images.length}
            </div>
            
            {/* Delete button */}
            <div className="text-center mt-2">
              <Button
                variant="destructive"
                size="sm"
                onClick={() => {
                  const filename = evidencePreview.images[evidencePreview.currentIndex];
                  deleteEvidence(evidencePreview.testCaseId, evidencePreview.stepNumber, filename);
                  if (evidencePreview.images.length === 1) {
                    setEvidencePreview(null);
                  } else {
                    setEvidencePreview({
                      ...evidencePreview,
                      images: evidencePreview.images.filter((_, i) => i !== evidencePreview.currentIndex),
                      currentIndex: Math.min(evidencePreview.currentIndex, evidencePreview.images.length - 2)
                    });
                  }
                }}
              >
                <Trash2 className="w-4 h-4 mr-1" /> Delete Evidence
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
  
  // Check for full-page views first - show if data is loaded
  if (fullPageTestCase) {
    return (
      <>
        {renderFullPageTestCaseView()}
        {renderSharedModals()}
      </>
    );
  }
  
  if (fullPageScenario) {
    return (
      <>
        {renderFullPageScenarioView()}
        {renderSharedModals()}
      </>
    );
  }
  
  return (
    <div className="h-[calc(100vh-180px)] flex flex-col bg-gray-50">
      {/* Project Header - URL-based navigation */}
      <div className="bg-white border-b px-4 py-3 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={navigateToProjects}
            className="hover:bg-gray-100"
            data-testid="back-to-projects"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Projects
          </Button>
          <div className="h-6 w-px bg-gray-200" />
          {currentProject && (
            <div className="flex items-center gap-3">
              <div 
                className="w-8 h-8 rounded-lg flex items-center justify-center text-lg"
                style={{ backgroundColor: currentProject.color ? `${currentProject.color}20` : '#e0e7ff' }}
              >
                {currentProject.icon || '📁'}
              </div>
              <div>
                <h1 className="font-semibold text-gray-900 flex items-center gap-2">
                  {currentProject.name}
                  {currentProject.is_legacy && (
                    <Badge variant="secondary" className="text-xs">Legacy</Badge>
                  )}
                </h1>
                <p className="text-xs text-gray-500">
                  {sprints.length} sprints • {requirements.length} requirements • Prefix: {qaSettings.requirement_prefix || 'REQ'}
                </p>
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => { fetchSprints(); fetchRequirements(); }}
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white border-b px-4 flex-shrink-0">
        <Tabs value={activeTab} onValueChange={(val) => { setActiveTab(val); clearRequirementSelection(); setSelectedRequirements([]); }}>
          <TabsList className="h-12 bg-transparent border-0">
            <TabsTrigger value="backlog" className="data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700 px-4">
              <List className="w-4 h-4 mr-2" />
              Backlog
            </TabsTrigger>
            <TabsTrigger value="board" className="data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700 px-4">
              <LayoutGrid className="w-4 h-4 mr-2" />
              Sprint Board
            </TabsTrigger>
            <TabsTrigger value="requirements" className="data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700 px-4">
              <FileText className="w-4 h-4 mr-2" />
              Requirements
              {requirements.length > 0 && <Badge variant="secondary" className="ml-2">{requirements.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="sprints" className="data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700 px-4">
              <Calendar className="w-4 h-4 mr-2" />
              Sprints
              <Badge variant="secondary" className="ml-2">{sprints.length}</Badge>
            </TabsTrigger>
            <TabsTrigger value="all-items" data-testid="all-items-tab" className="data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700 px-4">
              <Database className="w-4 h-4 mr-2" />
              All Items
            </TabsTrigger>
            <TabsTrigger value="settings" className="data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700 px-4">
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Loading State */}
      {projectLoading && (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600 mx-auto mb-2" />
            <p className="text-gray-500">Loading project...</p>
          </div>
        </div>
      )}

      {/* Content */}
      {!projectLoading && (
        <div className="flex-1 overflow-hidden min-h-0">
          {activeTab === 'backlog' && renderBacklogView()}
          {activeTab === 'board' && renderSprintBoard()}
          {activeTab === 'requirements' && (
            selectedRequirement ? renderRequirementDetail() : renderRequirementsList()
          )}
          {activeTab === 'sprints' && renderSprintsView()}
          {activeTab === 'all-items' && currentProject && (
            <AllItemsView
              projectId={currentProject.id}
              projectSlug={projectSlug}
              onNavigateToRequirement={navigateToRequirement}
              onNavigateToScenario={navigateToScenario}
              onNavigateToTestCase={navigateToTestCase}
            />
          )}
          {activeTab === 'settings' && renderSettingsView()}
        </div>
      )}

      {/* Create Requirement Modal - Enhanced with AI Generation */}
      <Dialog open={showCreateRequirementModal} onOpenChange={(open) => {
        setShowCreateRequirementModal(open);
        if (!open) resetAIGeneration();
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Create Requirement
            </DialogTitle>
          </DialogHeader>
          
          {/* Mode Tabs */}
          <div className="flex border-b mb-4">
            <button
              onClick={() => setAiGenerationMode('manual')}
              className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                aiGenerationMode === 'manual' 
                  ? 'border-indigo-600 text-indigo-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Edit className="w-4 h-4 inline mr-2" />
              Manual Entry
            </button>
            <button
              onClick={() => setAiGenerationMode('ai')}
              className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                aiGenerationMode === 'ai' 
                  ? 'border-indigo-600 text-indigo-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Sparkles className="w-4 h-4 inline mr-2" />
              AI Generate
            </button>
          </div>

          {/* Manual Entry Mode */}
          {aiGenerationMode === 'manual' && (
            <div className="space-y-4 overflow-y-auto flex-1 pr-2">
              <div>
                <label className="text-sm font-medium">Title *</label>
                <Input
                  value={requirementForm.title}
                  onChange={(e) => setRequirementForm({ ...requirementForm, title: e.target.value })}
                  placeholder="User login feature"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Type</label>
                  <Select value={requirementForm.type} onValueChange={(v) => setRequirementForm({ ...requirementForm, type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="epic">🎯 Epic</SelectItem>
                      <SelectItem value="story">📖 Story</SelectItem>
                      <SelectItem value="bug">🐛 Bug</SelectItem>
                      <SelectItem value="technical">⚙️ Technical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium">Priority</label>
                  <Select value={requirementForm.priority} onValueChange={(v) => setRequirementForm({ ...requirementForm, priority: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Sprint</label>
                <SearchableSelect
                  options={[
                    { value: 'none', label: 'No Sprint (Backlog)' },
                    ...sprints.map(s => ({
                      value: s.id,
                      label: `${s.key} ${s.name}`,
                      icon: <Calendar className="w-4 h-4 text-indigo-500" />
                    }))
                  ]}
                  value={requirementForm.sprint_id || selectedSprint?.id || 'none'}
                  onValueChange={(v) => setRequirementForm({ ...requirementForm, sprint_id: v === 'none' ? null : v })}
                  placeholder="Select sprint..."
                  searchPlaceholder="Search sprints..."
                  emptyText="No sprints found."
                />
              </div>
              <div>
                <label className="text-sm font-medium">Project</label>
                <SearchableSelect
                  options={[
                    { value: 'none', label: 'No Project' },
                    ...availableProjects.map(p => ({
                      value: p.id,
                      label: p.name,
                      icon: <span>{p.icon || '📁'}</span>
                    }))
                  ]}
                  value={requirementForm.project_id || (currentProject?.id !== 'unassigned' ? currentProject?.id : '') || 'none'}
                  onValueChange={(v) => setRequirementForm({ ...requirementForm, project_id: v === 'none' ? null : v })}
                  placeholder="Select project..."
                  searchPlaceholder="Search projects..."
                  emptyText="No projects found."
                />
              </div>
              <div>
                <label className="text-sm font-medium">Description</label>
                <RichTextEditor
                  content={requirementForm.description}
                  onChange={(html) => setRequirementForm({ ...requirementForm, description: html })}
                  placeholder="Add a detailed description with formatting..."
                  minHeight={120}
                  data-testid="create-requirement-description"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Business Rules</label>
                <Textarea
                  value={requirementForm.business_rules}
                  onChange={(e) => setRequirementForm({ ...requirementForm, business_rules: e.target.value })}
                  placeholder="What business rules must be followed?"
                  rows={2}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Edge Cases</label>
                <Textarea
                  value={requirementForm.edge_cases}
                  onChange={(e) => setRequirementForm({ ...requirementForm, edge_cases: e.target.value })}
                  placeholder="What edge cases should be considered?"
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Story Points</label>
                  <Input
                    type="number"
                    min="0"
                    value={requirementForm.story_points || ''}
                    onChange={(e) => setRequirementForm({ ...requirementForm, story_points: e.target.value ? parseInt(e.target.value) : null })}
                    placeholder="Points"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Reporter</label>
                  <SearchableSelect
                    options={[
                      { value: 'none', label: 'Not specified' },
                      ...teamMembers.map(m => ({
                        value: m.id,
                        label: `${m.name || m.email}${currentUser?.id === m.id ? ' (me)' : ''}`,
                        icon: <User className="w-4 h-4 text-gray-500" />
                      }))
                    ]}
                    value={requirementForm.reporter_id || 'none'}
                    onValueChange={(v) => setRequirementForm({ ...requirementForm, reporter_id: v === 'none' ? null : v })}
                    placeholder="Select reporter..."
                    searchPlaceholder="Search team members..."
                    emptyText="No team members found."
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Assignee</label>
                <SearchableSelect
                  options={[
                    { value: 'none', label: 'Unassigned' },
                    ...teamMembers.map(m => ({
                      value: m.id,
                      label: `${m.name || m.email}${currentUser?.id === m.id ? ' (me)' : ''}`,
                      icon: <User className="w-4 h-4 text-blue-500" />
                    }))
                  ]}
                  value={requirementForm.assignee_id || 'none'}
                  onValueChange={(v) => setRequirementForm({ ...requirementForm, assignee_id: v === 'none' ? null : v })}
                  placeholder="Select assignee..."
                  searchPlaceholder="Search team members..."
                  emptyText="No team members found."
                />
                {currentUser && requirementForm.assignee_id !== currentUser.id && (
                  <button 
                    type="button"
                    className="text-xs text-indigo-600 hover:text-indigo-800 mt-1"
                    onClick={() => setRequirementForm({ ...requirementForm, assignee_id: currentUser.id })}
                  >
                    Assign to me
                  </button>
                )}
              </div>
            </div>
          )}

          {/* AI Generation Mode */}
          {aiGenerationMode === 'ai' && (
            <div className="flex-1 overflow-y-auto">
              {!aiPreview ? (
                <div className="space-y-4">
                  {/* Project Selection */}
                  <div>
                    <label className="text-sm font-medium">Project *</label>
                    <SearchableSelect
                      options={[
                        ...availableProjects.map(p => ({
                          value: p.id,
                          label: p.name,
                          icon: <span>{p.icon || '📁'}</span>
                        }))
                      ]}
                      value={requirementForm.project_id || (currentProject?.id !== 'unassigned' ? currentProject?.id : '')}
                      onValueChange={(v) => setRequirementForm({ ...requirementForm, project_id: v })}
                      placeholder="Select project..."
                      searchPlaceholder="Search projects..."
                      emptyText="No projects found."
                    />
                  </div>

                  {/* AI Input Section */}
                  <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg p-4 border border-indigo-100">
                    <div className="flex items-center gap-2 mb-3">
                      <Bot className="w-5 h-5 text-indigo-600" />
                      <span className="font-medium text-indigo-900">AI Requirement Generator</span>
                    </div>
                    <p className="text-sm text-gray-600 mb-4">
                      Describe your requirement or upload a document. AI will generate the complete QA hierarchy including 
                      acceptance criteria, test scenarios, test cases, and test steps.
                    </p>

                    {/* Prompt Input */}
                    <div className="mb-4">
                      <label className="text-sm font-medium mb-2 block">Describe your requirement</label>
                      <Textarea
                        value={aiPrompt}
                        onChange={(e) => setAiPrompt(e.target.value)}
                        placeholder="Example: Create a user authentication system that allows users to login with email/password, supports password reset via email, implements rate limiting for failed login attempts, and maintains session management with JWT tokens..."
                        rows={5}
                        className="w-full"
                      />
                    </div>

                    {/* Or Divider */}
                    <div className="flex items-center gap-4 my-4">
                      <div className="flex-1 h-px bg-gray-300"></div>
                      <span className="text-sm text-gray-500">OR</span>
                      <div className="flex-1 h-px bg-gray-300"></div>
                    </div>

                    {/* File Upload */}
                    <div>
                      <label className="text-sm font-medium mb-2 block">Upload a document (PDF, TXT)</label>
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-indigo-400 transition-colors">
                        <input
                          type="file"
                          accept=".pdf,.txt,.doc,.docx"
                          onChange={(e) => setAiFile(e.target.files?.[0] || null)}
                          className="hidden"
                          id="ai-file-upload"
                        />
                        <label htmlFor="ai-file-upload" className="cursor-pointer">
                          <FileUp className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                          {aiFile ? (
                            <div>
                              <p className="text-sm font-medium text-indigo-600">{aiFile.name}</p>
                              <p className="text-xs text-gray-500">{(aiFile.size / 1024).toFixed(1)} KB</p>
                            </div>
                          ) : (
                            <div>
                              <p className="text-sm text-gray-600">Click to upload or drag and drop</p>
                              <p className="text-xs text-gray-400">PDF, TXT, DOC, DOCX (max 10MB)</p>
                            </div>
                          )}
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Generate Button */}
                  <Button 
                    onClick={generateAIPreview}
                    disabled={aiGenerating || (!aiPrompt && !aiFile)}
                    className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
                  >
                    {aiGenerating ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Generating with AI...
                      </>
                    ) : (
                      <>
                        <Wand2 className="w-4 h-4 mr-2" />
                        Generate with AI
                      </>
                    )}
                  </Button>
                </div>
              ) : (
                /* AI Preview Section */
                <div className="space-y-4">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="text-sm text-green-800">AI has generated content. Review below and click Save to create.</span>
                  </div>

                  {/* Requirement Preview */}
                  <div className="border rounded-lg p-4">
                    <h3 className="font-semibold text-lg flex items-center gap-2 mb-3">
                      <FileText className="w-5 h-5 text-indigo-600" />
                      Requirement
                    </h3>
                    <div className="space-y-2 text-sm">
                      <p><strong>Title:</strong> {aiPreview.requirement?.title}</p>
                      <p><strong>Type:</strong> <Badge variant="outline">{aiPreview.requirement?.type}</Badge></p>
                      <p><strong>Priority:</strong> <Badge variant="outline">{aiPreview.requirement?.priority}</Badge></p>
                      <p><strong>Description:</strong> {aiPreview.requirement?.description}</p>
                      {aiPreview.requirement?.business_rules && (
                        <p><strong>Business Rules:</strong> {aiPreview.requirement?.business_rules}</p>
                      )}
                      {aiPreview.requirement?.edge_cases && (
                        <p><strong>Edge Cases:</strong> {aiPreview.requirement?.edge_cases}</p>
                      )}
                    </div>
                  </div>

                  {/* Acceptance Criteria Preview */}
                  {aiPreview.acceptance_criteria?.length > 0 && (
                    <div className="border rounded-lg p-4">
                      <h3 className="font-semibold flex items-center gap-2 mb-3">
                        <CheckSquare className="w-5 h-5 text-green-600" />
                        Acceptance Criteria ({aiPreview.acceptance_criteria.length})
                      </h3>
                      <div className="space-y-3">
                        {aiPreview.acceptance_criteria.map((ac, idx) => (
                          <div key={idx} className="bg-gray-50 rounded p-3 text-sm">
                            <p className="font-medium mb-1">{ac.title}</p>
                            <p className="text-gray-600">
                              <span className="text-blue-600">Given</span> {ac.given}<br />
                              <span className="text-amber-600">When</span> {ac.when}<br />
                              <span className="text-green-600">Then</span> {ac.then}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Test Scenarios Preview */}
                  {aiPreview.test_scenarios?.length > 0 && (
                    <div className="border rounded-lg p-4">
                      <h3 className="font-semibold flex items-center gap-2 mb-3">
                        <Layers className="w-5 h-5 text-purple-600" />
                        Test Scenarios ({aiPreview.test_scenarios.length})
                      </h3>
                      <div className="space-y-4">
                        {aiPreview.test_scenarios.map((scenario, sIdx) => (
                          <div key={sIdx} className="bg-gray-50 rounded-lg p-3">
                            <p className="font-medium text-sm">{scenario.title}</p>
                            <p className="text-xs text-gray-500 mb-2">{scenario.description}</p>
                            
                            {/* Test Cases */}
                            {scenario.test_cases?.length > 0 && (
                              <div className="ml-4 mt-2 space-y-2">
                                <p className="text-xs font-medium text-gray-600">Test Cases ({scenario.test_cases.length}):</p>
                                {scenario.test_cases.map((tc, tcIdx) => (
                                  <div key={tcIdx} className="bg-white rounded p-2 border text-xs">
                                    <p className="font-medium">{tc.title}</p>
                                    <p className="text-gray-500">{tc.test_steps?.length || 0} test steps</p>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setAiPreview(null)} className="flex-1">
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Back to Edit
                    </Button>
                    <Button 
                      onClick={saveAIGeneratedContent} 
                      disabled={actionLoading}
                      className="flex-1 bg-green-600 hover:bg-green-700"
                    >
                      {actionLoading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Check className="w-4 h-4 mr-2" />
                      )}
                      Save All
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Footer for Manual Mode */}
          {aiGenerationMode === 'manual' && (
            <DialogFooter className="mt-4 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowCreateRequirementModal(false)}>Cancel</Button>
              <Button onClick={createRequirement} disabled={!requirementForm.title || actionLoading}>
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Sprint Modal */}
      <Dialog open={showCreateSprintModal} onOpenChange={setShowCreateSprintModal}>
        <DialogContent className="max-w-xl">
          <DialogHeader className="pb-4 border-b">
            <DialogTitle className="flex items-center gap-2 text-lg">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <Calendar className="w-5 h-5 text-indigo-600" />
              </div>
              Create Sprint
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Sprint Name *</label>
                <Input
                  data-testid="sprint-name-input"
                  value={sprintForm.name}
                  onChange={(e) => setSprintForm({ ...sprintForm, name: e.target.value })}
                  placeholder="Sprint 1 - Authentication"
                  className="text-base"
                />
              </div>
              
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Sprint Goal</label>
                <Textarea
                  data-testid="sprint-goal-input"
                  value={sprintForm.goal}
                  onChange={(e) => setSprintForm({ ...sprintForm, goal: e.target.value })}
                  placeholder="What should be accomplished in this sprint?"
                  rows={2}
                  className="resize-none"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Start Date <span className="text-red-500">*</span>
                </label>
                <Input 
                  data-testid="sprint-start-date"
                  type="date" 
                  value={sprintForm.start_date} 
                  onChange={(e) => setSprintForm({ ...sprintForm, start_date: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  End Date <span className="text-red-500">*</span>
                </label>
                <Input 
                  data-testid="sprint-end-date"
                  type="date" 
                  value={sprintForm.end_date} 
                  onChange={(e) => setSprintForm({ ...sprintForm, end_date: e.target.value })}
                  required
                />
              </div>
              
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Status</label>
                <Select value={sprintForm.status} onValueChange={(v) => setSprintForm({ ...sprintForm, status: v })}>
                  <SelectTrigger data-testid="sprint-status-select" className="w-full">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="planning">
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                        <span>Planning</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="active">
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-green-500"></span>
                        <span>Active</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="completed">
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-slate-500"></span>
                        <span>Completed</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Date validation message */}
            {sprintForm.start_date && sprintForm.end_date && new Date(sprintForm.end_date) < new Date(sprintForm.start_date) && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                <AlertCircle className="w-4 h-4" />
                End date must be after start date
              </div>
            )}
          </div>
          <DialogFooter className="pt-4 border-t">
            <Button variant="outline" onClick={() => setShowCreateSprintModal(false)}>Cancel</Button>
            <Button 
              data-testid="create-sprint-btn"
              onClick={createSprint} 
              disabled={
                !sprintForm.name || 
                !sprintForm.start_date || 
                !sprintForm.end_date || 
                (sprintForm.start_date && sprintForm.end_date && new Date(sprintForm.end_date) < new Date(sprintForm.start_date)) ||
                actionLoading
              }
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Create Sprint
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Sprint Modal */}
      <Dialog open={showEditSprintModal} onOpenChange={setShowEditSprintModal}>
        <DialogContent className="max-w-xl">
          <DialogHeader className="pb-4 border-b">
            <DialogTitle className="flex items-center gap-2 text-lg">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <Edit className="w-5 h-5 text-indigo-600" />
              </div>
              Edit Sprint
              {editingSprint?.key && (
                <Badge className="text-sm font-mono font-semibold bg-indigo-100 text-indigo-700 border-indigo-200">
                  {editingSprint.key}
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Sprint Name *</label>
                <Input
                  data-testid="edit-sprint-name-input"
                  value={sprintForm.name}
                  onChange={(e) => setSprintForm({ ...sprintForm, name: e.target.value })}
                  placeholder="Sprint 1 - Authentication"
                  className="text-base"
                />
              </div>
              
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Sprint Goal</label>
                <Textarea
                  data-testid="edit-sprint-goal-input"
                  value={sprintForm.goal}
                  onChange={(e) => setSprintForm({ ...sprintForm, goal: e.target.value })}
                  placeholder="What should be accomplished in this sprint?"
                  rows={2}
                  className="resize-none"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Start Date <span className="text-red-500">*</span>
                </label>
                <Input 
                  data-testid="edit-sprint-start-date"
                  type="date" 
                  value={sprintForm.start_date} 
                  onChange={(e) => setSprintForm({ ...sprintForm, start_date: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  End Date <span className="text-red-500">*</span>
                </label>
                <Input 
                  data-testid="edit-sprint-end-date"
                  type="date" 
                  value={sprintForm.end_date} 
                  onChange={(e) => setSprintForm({ ...sprintForm, end_date: e.target.value })}
                  required
                />
              </div>
              
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Status</label>
                <Select value={sprintForm.status} onValueChange={(v) => setSprintForm({ ...sprintForm, status: v })}>
                  <SelectTrigger data-testid="edit-sprint-status-select" className="w-full">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="planning">
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                        <span>Planning</span>
                        <span className="text-xs text-gray-400 ml-2">- Sprint is being planned</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="active">
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-green-500"></span>
                        <span>Active</span>
                        <span className="text-xs text-gray-400 ml-2">- Sprint is in progress</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="completed">
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-slate-500"></span>
                        <span>Completed</span>
                        <span className="text-xs text-gray-400 ml-2">- Sprint is finished</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Date validation message */}
            {sprintForm.start_date && sprintForm.end_date && new Date(sprintForm.end_date) < new Date(sprintForm.start_date) && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                <AlertCircle className="w-4 h-4" />
                End date must be after start date
              </div>
            )}
          </div>
          <DialogFooter className="pt-4 border-t">
            <div className="flex items-center justify-between w-full">
              <Button 
                variant="outline"
                data-testid="delete-sprint-modal-btn"
                onClick={() => editingSprint && deleteSprint(editingSprint.id)}
                className="text-red-500 hover:text-red-700 hover:bg-red-50 hover:border-red-300"
                disabled={actionLoading}
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Delete Sprint
              </Button>
              <div className="flex items-center gap-3">
                <Button variant="outline" onClick={() => { setShowEditSprintModal(false); setEditingSprint(null); }}>
                  Cancel
                </Button>
                <Button 
                  data-testid="update-sprint-btn"
                  onClick={updateSprint} 
                  disabled={
                    !sprintForm.name || 
                    !sprintForm.start_date || 
                    !sprintForm.end_date || 
                    (sprintForm.start_date && sprintForm.end_date && new Date(sprintForm.end_date) < new Date(sprintForm.start_date)) ||
                    actionLoading
                  }
                  className="bg-indigo-600 hover:bg-indigo-700"
                >
                  {actionLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Acceptance Criteria Modal */}
      <Dialog open={showAddACModal} onOpenChange={setShowAddACModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Acceptance Criteria</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Title *</label>
              <Input
                value={acForm.title}
                onChange={(e) => setAcForm({ ...acForm, title: e.target.value })}
                placeholder="User can log in with valid credentials"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-green-700">Given (Precondition)</label>
              <Textarea
                value={acForm.given}
                onChange={(e) => setAcForm({ ...acForm, given: e.target.value })}
                placeholder="User is on the login page"
                rows={2}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-blue-700">When (Action)</label>
              <Textarea
                value={acForm.when}
                onChange={(e) => setAcForm({ ...acForm, when: e.target.value })}
                placeholder="User enters valid email and password and clicks Login"
                rows={2}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-purple-700">Then (Expected Result)</label>
              <Textarea
                value={acForm.then}
                onChange={(e) => setAcForm({ ...acForm, then: e.target.value })}
                placeholder="User is redirected to the dashboard"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddACModal(false)}>Cancel</Button>
            <Button onClick={createAcceptanceCriteria} disabled={!acForm.title || actionLoading}>
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Test Scenario Modal */}
      <Dialog open={showAddScenarioModal} onOpenChange={setShowAddScenarioModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Test Scenario</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Title *</label>
              <Input
                value={scenarioForm.title}
                onChange={(e) => setScenarioForm({ ...scenarioForm, title: e.target.value })}
                placeholder="Login functionality tests"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Description</label>
              <RichTextEditor
                content={scenarioForm.description}
                onChange={(html) => setScenarioForm({ ...scenarioForm, description: html })}
                placeholder="Describe the test scenario scope and objectives..."
                minHeight={100}
                data-testid="create-scenario-description"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddScenarioModal(false)}>Cancel</Button>
            <Button onClick={createTestScenario} disabled={!scenarioForm.title || actionLoading}>
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Scenario'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Acceptance Criteria Modal */}
      <Dialog open={showEditACModal} onOpenChange={setShowEditACModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5" />
              Edit Acceptance Criteria
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Title *</label>
              <Input
                value={acForm.title}
                onChange={(e) => setAcForm({ ...acForm, title: e.target.value })}
                placeholder="User can log in with valid credentials"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-green-700">Given (Precondition)</label>
              <Textarea
                value={acForm.given}
                onChange={(e) => setAcForm({ ...acForm, given: e.target.value })}
                placeholder="User is on the login page"
                rows={2}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-blue-700">When (Action)</label>
              <Textarea
                value={acForm.when}
                onChange={(e) => setAcForm({ ...acForm, when: e.target.value })}
                placeholder="User enters valid email and password and clicks Login"
                rows={2}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-purple-700">Then (Expected Result)</label>
              <Textarea
                value={acForm.then}
                onChange={(e) => setAcForm({ ...acForm, then: e.target.value })}
                placeholder="User is redirected to the dashboard"
                rows={2}
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_verified"
                checked={acForm.is_verified || false}
                onChange={(e) => setAcForm({ ...acForm, is_verified: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300 text-indigo-600"
              />
              <label htmlFor="is_verified" className="text-sm font-medium text-gray-700">Mark as Verified</label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowEditACModal(false); setEditingAC(null); }}>Cancel</Button>
            <Button onClick={updateAcceptanceCriteria} disabled={!acForm.title || actionLoading} className="bg-indigo-600 hover:bg-indigo-700">
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Test Scenario Modal */}
      <Dialog open={showEditScenarioModal} onOpenChange={setShowEditScenarioModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5" />
              Edit Test Scenario
              {editingScenario?.key && (
                <Badge className="text-sm font-mono bg-amber-50 text-amber-700">{editingScenario.key}</Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Title *</label>
              <Input
                value={scenarioForm.title}
                onChange={(e) => setScenarioForm({ ...scenarioForm, title: e.target.value })}
                placeholder="Login functionality tests"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Description</label>
              <RichTextEditor
                content={scenarioForm.description}
                onChange={(html) => setScenarioForm({ ...scenarioForm, description: html })}
                placeholder="Describe the test scenario scope and objectives..."
                minHeight={100}
                data-testid="edit-scenario-description"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowEditScenarioModal(false); setEditingScenario(null); }}>Cancel</Button>
            <Button onClick={updateTestScenario} disabled={!scenarioForm.title || actionLoading} className="bg-indigo-600 hover:bg-indigo-700">
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Test Case Sheet (Side Panel) - Add/Edit/View */}
      <Sheet open={showTestCaseSheet} onOpenChange={setShowTestCaseSheet}>
        <SheetContent className="w-[600px] sm:max-w-[600px] overflow-y-auto">
          <SheetHeader className="pb-4 border-b">
            <SheetTitle className="flex items-center gap-2">
              {testCaseSheetMode === 'add' && (
                <>
                  <Plus className="w-5 h-5 text-indigo-600" />
                  Add Test Case
                </>
              )}
              {testCaseSheetMode === 'edit' && (
                <>
                  <Edit className="w-5 h-5 text-indigo-600" />
                  Edit Test Case
                  {editingTestCase?.key && (
                    <Badge className="text-sm font-mono bg-teal-50 text-teal-700">{editingTestCase.key}</Badge>
                  )}
                </>
              )}
              {testCaseSheetMode === 'view' && (
                <>
                  {selectedTestCase?.key && (
                    <Badge className="text-sm font-mono bg-teal-50 text-teal-700">{selectedTestCase.key}</Badge>
                  )}
                  {selectedTestCase?.title}
                </>
              )}
            </SheetTitle>
          </SheetHeader>

          {/* View Mode */}
          {testCaseSheetMode === 'view' && selectedTestCase && (
            <div className="space-y-6 py-6">
              {/* Actions */}
              <div className="flex items-center gap-2">
                <Button 
                  variant="default" 
                  size="sm" 
                  className="bg-indigo-600 hover:bg-indigo-700"
                  onClick={() => {
                    // Navigate to full-page test case view
                    const projectSlug = currentProject?.slug || 'default';
                    window.history.pushState({}, '', `/sprint-qa/project/${projectSlug}/test-case/${selectedTestCase.key}`);
                    setFullPageTestCase({ test_case: selectedTestCase, loading: false });
                    setShowTestCaseSheet(false);
                    fetchTestCaseByKey(selectedTestCase.key);
                  }}
                >
                  <ExternalLink className="w-4 h-4 mr-1" /> Open Full View
                </Button>
                <Button variant="outline" size="sm" className="text-red-500 hover:text-red-700" onClick={() => deleteTestCase(selectedTestCase.id)}>
                  <Trash2 className="w-4 h-4 mr-1" /> Delete
                </Button>
              </div>

              {/* Badges */}
              <div className="flex flex-wrap gap-2">
                <Badge className={testCaseStatusConfig[selectedTestCase.status]?.color}>
                  {testCaseStatusConfig[selectedTestCase.status]?.label}
                </Badge>
                <Badge className={priorityConfig[selectedTestCase.priority]?.color}>
                  {priorityConfig[selectedTestCase.priority]?.icon} {priorityConfig[selectedTestCase.priority]?.label}
                </Badge>
                <Badge variant="outline">
                  {selectedTestCase.test_type === 'automation' ? '🤖 Automation' : '👤 Manual'}
                </Badge>
              </div>

              {/* Preconditions */}
              {selectedTestCase.preconditions && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Preconditions</h4>
                  <p className="text-sm text-gray-600 bg-amber-50 p-3 rounded border border-amber-200">{selectedTestCase.preconditions}</p>
                </div>
              )}

              {/* Description */}
              {selectedTestCase.description && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Description</h4>
                  <div className="text-sm text-gray-600">
                    <RichTextViewer content={selectedTestCase.description} />
                  </div>
                </div>
              )}

              {/* Test Steps */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <ListOrdered className="w-4 h-4" /> Test Steps ({selectedTestCase.test_steps?.length || 0})
                </h4>
                {selectedTestCase.test_steps?.length > 0 ? (
                  <div className="space-y-3">
                    {selectedTestCase.test_steps.map((step, idx) => (
                      <div key={idx} className="p-3 bg-gray-50 rounded-lg border">
                        <div className="flex items-start gap-3">
                          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold flex items-center justify-center">
                            {step.step_number}
                          </span>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">{step.action}</p>
                            <p className="text-sm text-gray-500 mt-1">
                              <span className="font-medium text-green-700">Expected:</span> {step.expected_result}
                            </p>
                          </div>
                          <Badge variant="outline" className="text-xs">
                            {step.status === 'passed' ? '✅ Pass' : step.status === 'failed' ? '❌ Fail' : '⏳ Pending'}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 italic">No test steps defined</p>
                )}
              </div>

              {/* Expected Result */}
              {selectedTestCase.expected_result && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Overall Expected Result</h4>
                  <p className="text-sm text-gray-600 bg-green-50 p-3 rounded border border-green-200">
                    {selectedTestCase.expected_result}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Add/Edit Mode */}
          {(testCaseSheetMode === 'add' || testCaseSheetMode === 'edit') && (
            <div className="space-y-5 py-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Title *</label>
                <Input
                  value={testCaseForm.title}
                  onChange={(e) => setTestCaseForm({ ...testCaseForm, title: e.target.value })}
                  placeholder="Verify login with valid credentials"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Priority</label>
                  <Select value={testCaseForm.priority} onValueChange={(v) => setTestCaseForm({ ...testCaseForm, priority: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Test Type</label>
                  <Select value={testCaseForm.test_type || 'manual'} onValueChange={(v) => setTestCaseForm({ ...testCaseForm, test_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manual">👤 Manual</SelectItem>
                      <SelectItem value="automation">🤖 Automation</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Assignee</label>
                  <Select value={testCaseForm.assignee_id || 'none'} onValueChange={(v) => setTestCaseForm({ ...testCaseForm, assignee_id: v === 'none' ? null : v })}>
                    <SelectTrigger><SelectValue placeholder="Select assignee" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Unassigned</SelectItem>
                      {teamMembers.map(m => (
                        <SelectItem key={m.id} value={m.id}>
                          {m.name || m.email}
                          {currentUser?.id === m.id && ' (me)'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {currentUser && testCaseForm.assignee_id !== currentUser.id && (
                    <button 
                      type="button"
                      className="text-xs text-indigo-600 hover:text-indigo-800 mt-1"
                      onClick={() => setTestCaseForm({ ...testCaseForm, assignee_id: currentUser.id })}
                    >
                      Assign to me
                    </button>
                  )}
                </div>
              </div>

              {testCaseSheetMode === 'edit' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Status</label>
                  <Select value={testCaseForm.status || 'draft'} onValueChange={(v) => setTestCaseForm({ ...testCaseForm, status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="ready">Ready</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="passed">Passed</SelectItem>
                      <SelectItem value="failed">Failed</SelectItem>
                      <SelectItem value="blocked">Blocked</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Preconditions</label>
                <Textarea
                  value={testCaseForm.preconditions}
                  onChange={(e) => setTestCaseForm({ ...testCaseForm, preconditions: e.target.value })}
                  placeholder="What must be true before this test starts?"
                  rows={2}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Description</label>
                <RichTextEditor
                  content={testCaseForm.description}
                  onChange={(html) => setTestCaseForm({ ...testCaseForm, description: html })}
                  placeholder="Describe what this test case validates..."
                  minHeight={100}
                  data-testid="test-case-description"
                />
              </div>
              
              {/* Test Steps */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <ListOrdered className="w-4 h-4" /> Test Steps
                </label>
                <div className="space-y-2">
                  {testCaseForm.test_steps.map((step, idx) => (
                    <div key={idx} className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg border">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold flex items-center justify-center mt-0.5">
                        {step.step_number}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{step.action}</p>
                        <p className="text-xs text-gray-500 mt-0.5">Expected: {step.expected_result}</p>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => removeTestStep(idx)} className="h-7 w-7 p-0">
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))}
                  <div className="space-y-2 pt-2 border-t">
                    <Input
                      placeholder="Step action (e.g., Click Login button)"
                      value={newStep.action}
                      onChange={(e) => setNewStep({ ...newStep, action: e.target.value })}
                    />
                    <div className="flex gap-2">
                      <Input
                        placeholder="Expected result"
                        value={newStep.expected_result}
                        onChange={(e) => setNewStep({ ...newStep, expected_result: e.target.value })}
                        className="flex-1"
                      />
                      <Button variant="outline" onClick={addTestStep} disabled={!newStep.action}>
                        <Plus className="w-4 h-4 mr-1" /> Add Step
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Overall Expected Result</label>
                <Textarea
                  value={testCaseForm.expected_result}
                  onChange={(e) => setTestCaseForm({ ...testCaseForm, expected_result: e.target.value })}
                  placeholder="User is successfully logged in and redirected to dashboard"
                  rows={2}
                />
              </div>
            </div>
          )}

          {/* Footer for Add/Edit modes */}
          {(testCaseSheetMode === 'add' || testCaseSheetMode === 'edit') && (
            <SheetFooter className="pt-4 border-t">
              <div className="flex items-center justify-between w-full">
                {testCaseSheetMode === 'edit' && editingTestCase && (
                  <Button 
                    variant="outline" 
                    onClick={() => deleteTestCase(editingTestCase.id)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4 mr-1" /> Delete
                  </Button>
                )}
                {testCaseSheetMode === 'add' && <div />}
                <div className="flex items-center gap-2">
                  <Button variant="outline" onClick={() => setShowTestCaseSheet(false)}>Cancel</Button>
                  <Button 
                    onClick={testCaseSheetMode === 'edit' ? updateTestCase : createTestCase} 
                    disabled={!testCaseForm.title || actionLoading}
                    className="bg-indigo-600 hover:bg-indigo-700"
                  >
                    {actionLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : testCaseSheetMode === 'edit' ? (
                      <CheckCircle className="w-4 h-4 mr-2" />
                    ) : (
                      <Plus className="w-4 h-4 mr-2" />
                    )}
                    {testCaseSheetMode === 'edit' ? 'Save Changes' : 'Create Test Case'}
                  </Button>
                </div>
              </div>
            </SheetFooter>
          )}
        </SheetContent>
      </Sheet>

 

      {/* Edit Requirement Modal - Enhanced */}
      <Dialog open={showEditRequirementModal} onOpenChange={setShowEditRequirementModal}>
        <DialogContent className="max-w-4xl w-[95vw] max-h-[90vh]">
          <DialogHeader className="pb-4 border-b">
            <DialogTitle className="flex items-center gap-3 text-xl">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <Edit className="w-5 h-5 text-indigo-600" />
              </div>
              <span>Edit Requirement</span>
              {editingRequirement?.key && (
                <Badge className="text-sm font-mono font-semibold bg-indigo-100 text-indigo-700 border-indigo-200">
                  {editingRequirement.key}
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          
          <div className="overflow-y-auto max-h-[calc(90vh-180px)] py-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column - Core Details */}
              <div className="space-y-5">
                <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Basic Information
                  </h3>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Title *</label>
                    <Input
                      data-testid="edit-requirement-title"
                      value={requirementForm.title}
                      onChange={(e) => setRequirementForm({ ...requirementForm, title: e.target.value })}
                      placeholder="Enter requirement title"
                      className="text-base"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Description</label>
                    <RichTextEditor
                      content={requirementForm.description}
                      onChange={(html) => setRequirementForm({ ...requirementForm, description: html })}
                      placeholder="Provide a detailed description with formatting..."
                      minHeight={150}
                      data-testid="edit-requirement-description"
                    />
                  </div>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-2">
                    <Target className="w-4 h-4" />
                    Classification
                  </h3>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Type</label>
                      <Select value={requirementForm.type} onValueChange={(v) => setRequirementForm({ ...requirementForm, type: v })}>
                        <SelectTrigger data-testid="edit-requirement-type" className="bg-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="epic">
                            <div className="flex items-center gap-2">
                              <span className="text-lg">🎯</span>
                              <span>Epic</span>
                            </div>
                          </SelectItem>
                          <SelectItem value="story">
                            <div className="flex items-center gap-2">
                              <span className="text-lg">📖</span>
                              <span>Story</span>
                            </div>
                          </SelectItem>
                          <SelectItem value="bug">
                            <div className="flex items-center gap-2">
                              <span className="text-lg">🐛</span>
                              <span>Bug</span>
                            </div>
                          </SelectItem>
                          <SelectItem value="technical">
                            <div className="flex items-center gap-2">
                              <span className="text-lg">⚙️</span>
                              <span>Technical</span>
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Priority</label>
                      <Select value={requirementForm.priority} onValueChange={(v) => setRequirementForm({ ...requirementForm, priority: v })}>
                        <SelectTrigger data-testid="edit-requirement-priority" className="bg-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">
                            <div className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full bg-slate-400"></span>
                              <span>Low</span>
                            </div>
                          </SelectItem>
                          <SelectItem value="medium">
                            <div className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                              <span>Medium</span>
                            </div>
                          </SelectItem>
                          <SelectItem value="high">
                            <div className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                              <span>High</span>
                            </div>
                          </SelectItem>
                          <SelectItem value="critical">
                            <div className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full bg-red-500"></span>
                              <span>Critical</span>
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Right Column - Status & Details */}
              <div className="space-y-5">
                <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" />
                    Status & Assignment
                  </h3>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Status</label>
                      <Select value={requirementForm.status || 'draft'} onValueChange={(v) => setRequirementForm({ ...requirementForm, status: v })}>
                        <SelectTrigger data-testid="edit-requirement-status" className="bg-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="draft">
                            <Badge variant="secondary" className="bg-gray-100">Draft</Badge>
                          </SelectItem>
                          <SelectItem value="ready">
                            <Badge className="bg-blue-100 text-blue-700">Ready</Badge>
                          </SelectItem>
                          <SelectItem value="in_progress">
                            <Badge className="bg-amber-100 text-amber-700">In Progress</Badge>
                          </SelectItem>
                          <SelectItem value="done">
                            <Badge className="bg-green-100 text-green-700">Done</Badge>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Testing Status</label>
                      <Select value={requirementForm.testing_status || 'not_started'} onValueChange={(v) => setRequirementForm({ ...requirementForm, testing_status: v })}>
                        <SelectTrigger data-testid="edit-requirement-testing-status" className="bg-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="not_started">
                            <div className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full bg-slate-400"></span>
                              <span>Not Started</span>
                            </div>
                          </SelectItem>
                          <SelectItem value="to_test">
                            <div className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                              <span>To Test</span>
                            </div>
                          </SelectItem>
                          <SelectItem value="testing">
                            <div className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                              <span>Testing</span>
                            </div>
                          </SelectItem>
                          <SelectItem value="done">
                            <div className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full bg-green-500"></span>
                              <span>Done</span>
                            </div>
                          </SelectItem>
                          <SelectItem value="blocked">
                            <div className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full bg-red-500"></span>
                              <span>Blocked</span>
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      <Calendar className="w-4 h-4 inline mr-1" />
                      Sprint Assignment
                    </label>
                    <SearchableSelect
                      options={[
                        { value: 'none', label: 'No Sprint (Backlog)' },
                        ...sprints.map(s => ({
                          value: s.id,
                          label: `${s.key} ${s.name}`,
                          icon: <Calendar className="w-4 h-4 text-indigo-500" />
                        }))
                      ]}
                      value={requirementForm.sprint_id || 'none'}
                      onValueChange={(v) => setRequirementForm({ ...requirementForm, sprint_id: v === 'none' ? null : v })}
                      placeholder="Select sprint..."
                      searchPlaceholder="Search sprints..."
                      emptyText="No sprints found."
                      data-testid="edit-requirement-sprint"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      <FolderOpen className="w-4 h-4 inline mr-1" />
                      Project Assignment
                    </label>
                    <SearchableSelect
                      options={[
                        { value: 'none', label: 'No Project' },
                        ...availableProjects.map(p => ({
                          value: p.id,
                          label: p.name,
                          icon: <span>{p.icon || '📁'}</span>
                        }))
                      ]}
                      value={requirementForm.project_id || 'none'}
                      onValueChange={(v) => setRequirementForm({ ...requirementForm, project_id: v === 'none' ? null : v })}
                      placeholder="Select project..."
                      searchPlaceholder="Search projects..."
                      emptyText="No projects found."
                      data-testid="edit-requirement-project"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Story Points</label>
                      <Input
                        data-testid="edit-requirement-story-points"
                        type="number"
                        min="0"
                        value={requirementForm.story_points || ''}
                        onChange={(e) => setRequirementForm({ ...requirementForm, story_points: e.target.value ? parseInt(e.target.value) : null })}
                        placeholder="Points"
                        className="bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        <User className="w-4 h-4 inline mr-1" />
                        Reporter
                      </label>
                      <SearchableSelect
                        options={[
                          { value: 'none', label: 'Not specified' },
                          ...teamMembers.map(m => ({
                            value: m.id,
                            label: `${m.name || m.email}${currentUser?.id === m.id ? ' (me)' : ''}`,
                            icon: <User className="w-4 h-4 text-gray-500" />
                          }))
                        ]}
                        value={requirementForm.reporter_id || 'none'}
                        onValueChange={(v) => setRequirementForm({ ...requirementForm, reporter_id: v === 'none' ? null : v })}
                        placeholder="Select reporter..."
                        searchPlaceholder="Search team members..."
                        emptyText="No team members found."
                        data-testid="edit-requirement-reporter"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      <User className="w-4 h-4 inline mr-1" />
                      Assignee
                    </label>
                    <SearchableSelect
                      options={[
                        { value: 'none', label: 'Unassigned' },
                        ...teamMembers.map(m => ({
                          value: m.id,
                          label: `${m.name || m.email}${currentUser?.id === m.id ? ' (me)' : ''}`,
                          icon: <User className="w-4 h-4 text-blue-500" />
                        }))
                      ]}
                      value={requirementForm.assignee_id || 'none'}
                      onValueChange={(v) => setRequirementForm({ ...requirementForm, assignee_id: v === 'none' ? null : v })}
                      placeholder="Select assignee..."
                      searchPlaceholder="Search team members..."
                      emptyText="No team members found."
                      data-testid="edit-requirement-assignee"
                    />
                    {currentUser && requirementForm.assignee_id !== currentUser.id && (
                      <button 
                        type="button"
                        className="text-xs text-indigo-600 hover:text-indigo-800 mt-1"
                        onClick={() => setRequirementForm({ ...requirementForm, assignee_id: currentUser.id })}
                      >
                        Assign to me
                      </button>
                    )}
                  </div>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    Business Context
                  </h3>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Business Rules</label>
                    <Textarea
                      data-testid="edit-requirement-business-rules"
                      value={requirementForm.business_rules}
                      onChange={(e) => setRequirementForm({ ...requirementForm, business_rules: e.target.value })}
                      placeholder="Define the business rules that must be followed..."
                      rows={3}
                      className="resize-none"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Edge Cases</label>
                    <Textarea
                      data-testid="edit-requirement-edge-cases"
                      value={requirementForm.edge_cases}
                      onChange={(e) => setRequirementForm({ ...requirementForm, edge_cases: e.target.value })}
                      placeholder="Document edge cases and boundary conditions..."
                      rows={3}
                      className="resize-none"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter className="pt-4 border-t bg-gray-50 -mx-6 -mb-6 px-6 py-4 rounded-b-lg">
            <div className="flex items-center justify-between w-full">
              <p className="text-xs text-gray-500">
                {editingRequirement?.key && `Editing ${editingRequirement.key}`}
              </p>
              <div className="flex items-center gap-3">
                <Button variant="outline" onClick={() => { setShowEditRequirementModal(false); setEditingRequirement(null); }}>
                  Cancel
                </Button>
                <Button 
                  data-testid="save-edit-requirement-btn" 
                  onClick={updateRequirement} 
                  disabled={!requirementForm.title || actionLoading}
                  className="bg-indigo-600 hover:bg-indigo-700 min-w-[140px]"
                >
                  {actionLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign to Sprint Modal */}
      <Dialog open={showAssignSprintModal} onOpenChange={setShowAssignSprintModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Assign to Sprint
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {editingRequirement && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  {editingRequirement.key && (
                    <span className="text-sm font-mono font-semibold text-indigo-600">
                      {editingRequirement.key}
                    </span>
                  )}
                  <Badge className={requirementTypeConfig[editingRequirement.type]?.color + ' text-xs'}>
                    {requirementTypeConfig[editingRequirement.type]?.icon}
                  </Badge>
                </div>
                <p className="text-sm font-medium text-gray-900">{editingRequirement.title}</p>
                {editingRequirement.sprint_id && (
                  <p className="text-xs text-gray-500 mt-1">
                    Currently assigned to: {sprints.find(s => s.id === editingRequirement.sprint_id)?.name || 'Unknown Sprint'}
                  </p>
                )}
              </div>
            )}
            <div>
              <label className="text-sm font-medium">Select Sprint</label>
              <Select 
                data-testid="assign-sprint-select"
                value={editingRequirement?.sprint_id || 'none'} 
                onValueChange={(v) => setEditingRequirement({ ...editingRequirement, sprint_id: v === 'none' ? null : v })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select a sprint" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">
                    <span className="text-gray-500">No Sprint (Unassign)</span>
                  </SelectItem>
                  {sprints.map(s => (
                    <SelectItem key={s.id} value={s.id}>
                      <div className="flex items-center gap-2">
                        {s.key && <span className="font-mono text-xs text-indigo-600">{s.key}</span>}
                        <span>{s.name}</span>
                        <Badge className={`${sprintStatusConfig[s.status]?.color} text-xs ml-1`}>
                          {sprintStatusConfig[s.status]?.label}
                        </Badge>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {sprints.length === 0 && (
                <p className="text-xs text-amber-600 mt-2">
                  No sprints available. Create a sprint first from the Sprints tab.
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAssignSprintModal(false); setEditingRequirement(null); }}>Cancel</Button>
            <Button 
              data-testid="assign-sprint-btn"
              onClick={() => editingRequirement && assignRequirementToSprint(editingRequirement.id, editingRequirement.sprint_id)} 
              disabled={actionLoading}
            >
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Assign Sprint'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Column Modal */}
      <Dialog open={showColumnModal} onOpenChange={setShowColumnModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LayoutGrid className="w-5 h-5" />
              {editingColumn ? 'Edit Column' : 'Add Column'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Column Label *</label>
              <Input
                data-testid="column-label-input"
                value={columnForm.label}
                onChange={(e) => setColumnForm({ ...columnForm, label: e.target.value })}
                placeholder="e.g., In Review, QA Testing, UAT"
              />
              <p className="text-xs text-gray-500 mt-1">This is the name displayed on the board</p>
            </div>
            {!editingColumn && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Column ID (optional)</label>
                <Input
                  data-testid="column-id-input"
                  value={columnForm.id}
                  onChange={(e) => setColumnForm({ ...columnForm, id: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                  placeholder="Auto-generated from label"
                  className="font-mono"
                />
                <p className="text-xs text-gray-500 mt-1">Used internally. Leave blank to auto-generate.</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowColumnModal(false); setEditingColumn(null); }}>
              Cancel
            </Button>
            <Button 
              data-testid="save-column-btn"
              onClick={saveColumn} 
              disabled={!columnForm.label.trim()}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {editingColumn ? 'Save Changes' : 'Add Column'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Azure DevOps Import Dialog */}
      <AzureDevOpsImport
        open={showAzureImport}
        onClose={() => setShowAzureImport(false)}
        existingProjects={availableProjects}
        onImportComplete={(targetProjectId) => {
          setShowAzureImport(false);
          // Refresh requirements if imported to current project
          if (currentProject && targetProjectId === currentProject.id) {
            fetchRequirements();
          }
          // Refresh available projects list
          fetchAvailableProjects();
        }}
      />
    </div>
  );
}
